import { Component, OnInit, ViewChild, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
// Custom interface for slider options since we're using custom HTML range inputs
interface SliderOptions {
  floor: number;
  ceil: number;
  step: number;
  showTicks: boolean;
  getSelectionBarColor: (min: number, max: number) => string;
  getPointerColor: (value: number) => string;
  getTickColor: (value: number) => string;
}
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { ModuleConfigDialogComponent } from "../components/module-config-dialog/module-config-dialog.component";
import { CreateGroupDialogComponent } from "../site-setting/create-group-dialog/create-group-dialog.component";
import { environment } from "src/environments/environment";
import { DomSanitizer } from "@angular/platform-browser";
import { ChangeDetectorRef } from "@angular/core";
import { MessageService } from "primeng/api";
import { DeleteConfirmationDialogComponent } from "../site-setting/delete-confirmation-dialog/delete-confirmation-dialog.component";
import { CategorySuccessDialogComponent } from "../site-setting/category-success-dialog/category-success-dialog.component";
import { SiteSavingDialogComponent } from "../site-setting/site-saving-dialog/site-saving-dialog.component";
import { NotificationsService } from "../notifications/notifications.service";
import { Router, ActivatedRoute } from "@angular/router";

interface OpexParameter {
  id: number;
  label: string;
  key: string;
  checked: boolean;
}

interface DeleteConfirmationData {
  title: string;
  message: string;
  itemName: string;
  itemType: string;
}

@Component({
    selector: "app-site-setting",
    templateUrl: "./site-setting.component.html",
    styleUrls: ["./site-setting.component.css"],
    providers: [MessageService],
    standalone: false
})
export class SiteSettingComponent implements OnInit, OnDestroy {
  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  thirdFormGroup: FormGroup;
  fourthFormGroup: FormGroup;
  fifthFormGroup: FormGroup;

  isLinear = false;
  visible: boolean = false;
  public showPopover = false;
  @ViewChild("stepper") stepper!: MatStepper;

  home = { icon: "pi pi-home", routerLink: "/" };

  breadcrumbSteps = [
    { label: "Basic Details" },
    { label: "Edit & Configure Module" },
    { label: "Site Score" },
    { label: "Open Opex" },
    { label: "AI Topic Settings" },
  ];

  // Property to trigger local topic manager save
  shouldSaveTopics: boolean = false;

  items = [];

  modules = [
    { name: "Module 1" },
    { name: "Module 2" },
    { name: "Module 3" },
    { name: "Module 4" },
    // ...more modules
  ];

  // World currencies list
  currencies = [
    { code: 'USD', name: 'United States Dollar', symbol: '$', country: 'United States' },
    { code: 'EUR', name: 'Euro', symbol: '€', country: 'European Union' },
    { code: 'GBP', name: 'British Pound Sterling', symbol: '£', country: 'United Kingdom' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', country: 'Japan' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', country: 'China' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', country: 'India' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', country: 'Australia' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', country: 'Canada' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', country: 'Switzerland' },
    { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', country: 'Hong Kong' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', country: 'Singapore' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', country: 'Sweden' },
    { code: 'KRW', name: 'South Korean Won', symbol: '₩', country: 'South Korea' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', country: 'Norway' },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', country: 'New Zealand' },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$', country: 'Mexico' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', country: 'Brazil' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', country: 'South Africa' },
    { code: 'RUB', name: 'Russian Ruble', symbol: '₽', country: 'Russia' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺', country: 'Turkey' },
    { code: 'AED', name: 'United Arab Emirates Dirham', symbol: 'د.إ', country: 'United Arab Emirates' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', country: 'Saudi Arabia' },
    { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', country: 'Poland' },
    { code: 'THB', name: 'Thai Baht', symbol: '฿', country: 'Thailand' },
    { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', country: 'Indonesia' },
    { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', country: 'Malaysia' },
    { code: 'PHP', name: 'Philippine Peso', symbol: '₱', country: 'Philippines' },
    { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', country: 'Vietnam' },
    { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', country: 'Czech Republic' },
    { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪', country: 'Israel' },
    { code: 'CLP', name: 'Chilean Peso', symbol: '$', country: 'Chile' },
    { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', country: 'Pakistan' },
    { code: 'EGP', name: 'Egyptian Pound', symbol: '£', country: 'Egypt' },
    { code: 'COP', name: 'Colombian Peso', symbol: '$', country: 'Colombia' },
    { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', country: 'Nigeria' },
    { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', country: 'Bangladesh' },
    { code: 'ARS', name: 'Argentine Peso', symbol: '$', country: 'Argentina' },
    { code: 'DKK', name: 'Danish Krone', symbol: 'kr', country: 'Denmark' },
    { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', country: 'Hungary' },
    { code: 'RON', name: 'Romanian Leu', symbol: 'lei', country: 'Romania' },
    { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', country: 'Ukraine' },
    { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', country: 'Peru' },
    { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', country: 'Kuwait' },
    { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼', country: 'Qatar' },
    { code: 'OMR', name: 'Omani Rial', symbol: '﷼', country: 'Oman' },
    { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', country: 'Bahrain' },
    { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', country: 'Jordan' },
    { code: 'LKR', name: 'Sri Lankan Rupee', symbol: '₨', country: 'Sri Lanka' },
    { code: 'NPR', name: 'Nepalese Rupee', symbol: '₨', country: 'Nepal' },
    { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸', country: 'Kazakhstan' },
    { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', country: 'Morocco' },
    { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', country: 'Kenya' },
    { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', country: 'Ghana' },
    { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', country: 'Tanzania' },
    { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', country: 'Uganda' },
    { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', country: 'Ethiopia' },
    { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA', country: 'West Africa' },
    { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA', country: 'Central Africa' },
    { code: 'TWD', name: 'New Taiwan Dollar', symbol: 'NT$', country: 'Taiwan' },
    { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', country: 'Bulgaria' },
    { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn', country: 'Croatia' },
    { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr', country: 'Iceland' },
    { code: 'RSD', name: 'Serbian Dinar', symbol: 'дин.', country: 'Serbia' },
    { code: 'GEL', name: 'Georgian Lari', symbol: '₾', country: 'Georgia' },
    { code: 'AMD', name: 'Armenian Dram', symbol: '֏', country: 'Armenia' },
    { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼', country: 'Azerbaijan' },
    { code: 'BYN', name: 'Belarusian Ruble', symbol: 'Br', country: 'Belarus' },
    { code: 'MDL', name: 'Moldovan Leu', symbol: 'L', country: 'Moldova' },
    { code: 'MKD', name: 'Macedonian Denar', symbol: 'ден', country: 'North Macedonia' },
    { code: 'ALL', name: 'Albanian Lek', symbol: 'L', country: 'Albania' },
    { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark', symbol: 'KM', country: 'Bosnia and Herzegovina' },
    { code: 'DZD', name: 'Algerian Dinar', symbol: 'د.ج', country: 'Algeria' },
    { code: 'TND', name: 'Tunisian Dinar', symbol: 'د.ت', country: 'Tunisia' },
    { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل', country: 'Lebanon' },
    { code: 'IQD', name: 'Iraqi Dinar', symbol: 'ع.د', country: 'Iraq' },
    { code: 'IRR', name: 'Iranian Rial', symbol: '﷼', country: 'Iran' },
    { code: 'AFN', name: 'Afghan Afghani', symbol: '؋', country: 'Afghanistan' },
    { code: 'MMK', name: 'Myanmar Kyat', symbol: 'K', country: 'Myanmar' },
    { code: 'KHR', name: 'Cambodian Riel', symbol: '៛', country: 'Cambodia' },
    { code: 'LAK', name: 'Lao Kip', symbol: '₭', country: 'Laos' },
    { code: 'MNT', name: 'Mongolian Tugrik', symbol: '₮', country: 'Mongolia' },
    { code: 'UZS', name: 'Uzbekistani Som', symbol: 'лв', country: 'Uzbekistan' },
    { code: 'TJS', name: 'Tajikistani Somoni', symbol: 'SM', country: 'Tajikistan' },
    { code: 'TMT', name: 'Turkmenistani Manat', symbol: 'T', country: 'Turkmenistan' },
    { code: 'KGS', name: 'Kyrgyzstani Som', symbol: 'лв', country: 'Kyrgyzstan' },
    { code: 'FJD', name: 'Fijian Dollar', symbol: 'FJ$', country: 'Fiji' },
    { code: 'PGK', name: 'Papua New Guinean Kina', symbol: 'K', country: 'Papua New Guinea' },
    { code: 'WST', name: 'Samoan Tala', symbol: 'WS$', country: 'Samoa' },
    { code: 'TOP', name: 'Tongan Paʻanga', symbol: 'T$', country: 'Tonga' },
    { code: 'VUV', name: 'Vanuatu Vatu', symbol: 'VT', country: 'Vanuatu' },
    { code: 'SBD', name: 'Solomon Islands Dollar', symbol: 'SI$', country: 'Solomon Islands' },
    { code: 'CRC', name: 'Costa Rican Colón', symbol: '₡', country: 'Costa Rica' },
    { code: 'PAB', name: 'Panamanian Balboa', symbol: 'B/.', country: 'Panama' },
    { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q', country: 'Guatemala' },
    { code: 'HNL', name: 'Honduran Lempira', symbol: 'L', country: 'Honduras' },
    { code: 'NIO', name: 'Nicaraguan Córdoba', symbol: 'C$', country: 'Nicaragua' },
    { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$', country: 'Dominican Republic' },
    { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$', country: 'Jamaica' },
    { code: 'TTD', name: 'Trinidad and Tobago Dollar', symbol: 'TT$', country: 'Trinidad and Tobago' },
    { code: 'BBD', name: 'Barbadian Dollar', symbol: 'Bds$', country: 'Barbados' },
    { code: 'BSD', name: 'Bahamian Dollar', symbol: 'B$', country: 'Bahamas' },
    { code: 'BZD', name: 'Belize Dollar', symbol: 'BZ$', country: 'Belize' },
    { code: 'GYD', name: 'Guyanese Dollar', symbol: 'G$', country: 'Guyana' },
    { code: 'SRD', name: 'Surinamese Dollar', symbol: '$', country: 'Suriname' },
    { code: 'HTG', name: 'Haitian Gourde', symbol: 'G', country: 'Haiti' },
    { code: 'CUP', name: 'Cuban Peso', symbol: '₱', country: 'Cuba' },
    { code: 'BOB', name: 'Bolivian Boliviano', symbol: 'Bs.', country: 'Bolivia' },
    { code: 'PYG', name: 'Paraguayan Guarani', symbol: '₲', country: 'Paraguay' },
    { code: 'UYU', name: 'Uruguayan Peso', symbol: '$U', country: 'Uruguay' },
    { code: 'VES', name: 'Venezuelan Bolívar', symbol: 'Bs.S', country: 'Venezuela' },
    { code: 'AWG', name: 'Aruban Florin', symbol: 'ƒ', country: 'Aruba' },
    { code: 'ANG', name: 'Netherlands Antillean Guilder', symbol: 'ƒ', country: 'Curaçao' },
    { code: 'XCD', name: 'East Caribbean Dollar', symbol: '$', country: 'Eastern Caribbean' },
    { code: 'BMD', name: 'Bermudian Dollar', symbol: '$', country: 'Bermuda' },
    { code: 'KYD', name: 'Cayman Islands Dollar', symbol: '$', country: 'Cayman Islands' },
    { code: 'MUR', name: 'Mauritian Rupee', symbol: '₨', country: 'Mauritius' },
    { code: 'SCR', name: 'Seychellois Rupee', symbol: '₨', country: 'Seychelles' },
    { code: 'MVR', name: 'Maldivian Rufiyaa', symbol: 'Rf', country: 'Maldives' },
    { code: 'BND', name: 'Brunei Dollar', symbol: 'B$', country: 'Brunei' },
    { code: 'BTN', name: 'Bhutanese Ngultrum', symbol: 'Nu.', country: 'Bhutan' },
    { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', country: 'Zambia' },
    { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK', country: 'Malawi' },
    { code: 'ZWL', name: 'Zimbabwean Dollar', symbol: 'Z$', country: 'Zimbabwe' },
    { code: 'BWP', name: 'Botswana Pula', symbol: 'P', country: 'Botswana' },
    { code: 'NAD', name: 'Namibian Dollar', symbol: 'N$', country: 'Namibia' },
    { code: 'SZL', name: 'Swazi Lilangeni', symbol: 'E', country: 'Eswatini' },
    { code: 'LSL', name: 'Lesotho Loti', symbol: 'M', country: 'Lesotho' },
    { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT', country: 'Mozambique' },
    { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz', country: 'Angola' },
    { code: 'CDF', name: 'Congolese Franc', symbol: 'FC', country: 'Democratic Republic of the Congo' },
    { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw', country: 'Rwanda' },
    { code: 'BIF', name: 'Burundian Franc', symbol: 'FBu', country: 'Burundi' },
    { code: 'DJF', name: 'Djiboutian Franc', symbol: 'Fdj', country: 'Djibouti' },
    { code: 'ERN', name: 'Eritrean Nakfa', symbol: 'Nfk', country: 'Eritrea' },
    { code: 'SOS', name: 'Somali Shilling', symbol: 'S', country: 'Somalia' },
    { code: 'SDG', name: 'Sudanese Pound', symbol: 'ج.س.', country: 'Sudan' },
    { code: 'SSP', name: 'South Sudanese Pound', symbol: '£', country: 'South Sudan' },
    { code: 'LYD', name: 'Libyan Dinar', symbol: 'ل.د', country: 'Libya' },
    { code: 'MRU', name: 'Mauritanian Ouguiya', symbol: 'UM', country: 'Mauritania' },
    { code: 'GMD', name: 'Gambian Dalasi', symbol: 'D', country: 'Gambia' },
    { code: 'GNF', name: 'Guinean Franc', symbol: 'FG', country: 'Guinea' },
    { code: 'SLL', name: 'Sierra Leonean Leone', symbol: 'Le', country: 'Sierra Leone' },
    { code: 'LRD', name: 'Liberian Dollar', symbol: 'L$', country: 'Liberia' },
    { code: 'CVE', name: 'Cape Verdean Escudo', symbol: '$', country: 'Cape Verde' },
    { code: 'STN', name: 'São Tomé and Príncipe Dobra', symbol: 'Db', country: 'São Tomé and Príncipe' },
    { code: 'SYP', name: 'Syrian Pound', symbol: '£S', country: 'Syria' },
    { code: 'YER', name: 'Yemeni Rial', symbol: '﷼', country: 'Yemen' },
    { code: 'MOP', name: 'Macanese Pataca', symbol: 'MOP$', country: 'Macau' },
    { code: 'KPW', name: 'North Korean Won', symbol: '₩', country: 'North Korea' }
  ];

  availableModules: any[] = [];

  // For 11 steps (0-10), left at 1, right at 9
  moduleRanges = this.modules.map(() => [1, 9]);
  sliderMin = 0;
  sliderMax = 10;

  public fabPosition: { top: number; left: number } | null = null; // Initial position
  public isDraggingFab = false;
  private dragOffset = { x: 0, y: 0 };
  private dragMoved = false;
  private dragThreshold = 5;
  private dragStartX = 0;
  private dragStartY = 0;
  public popoverPosition: "above" | "below" = "above";

  public sliderOptions: SliderOptions = {
    floor: 0,
    ceil: 10,
    step: 1,
    showTicks: false,
    getSelectionBarColor: (min: number, max: number): string => {
      // Yellow between handles
      return "#fbc02d";
    },
    getPointerColor: (value: number): string => {
      // Handles color (optional)
      return "#fff";
    },
    getTickColor: (value: number): string => {
      return "#bdbdbd";
    },
  };

  opexParameters: OpexParameter[] = [];

  // Add new properties for configuration loading
  isViewMode: boolean = false;
  hasSavedConfiguration: boolean = false;
  isLoadingConfiguration: boolean = false; // Add this new property
  savedConfiguration: any = null; // Store the loaded configuration

  opexData = {
    electricity: [
      {
        parameters: "Electricity",
        details: "Main supply",
        units: "kWh",
        type: "Utility",
        code: "ELEC001",
        uploadType: "Manual",
        formula: "A*B",
        priceType: "Fixed",
        price: "0.12",
      },
    ],
    water: [
      {
        parameters: "Water",
        details: "Municipal",
        units: "m³",
        type: "Utility",
        code: "WATR001",
        uploadType: "Auto",
        formula: "C*D",
        priceType: "Variable",
        price: "0.05",
      },
    ],
    gas: [
      {
        parameters: "Gas",
        details: "Natural Gas",
        units: "m³",
        type: "Utility",
        code: "GAS001",
        uploadType: "Manual",
        formula: "E*F",
        priceType: "Fixed",
        price: "0.09",
      },
    ],
    // Add more as needed
  };

  moduleScoreRanges: any[] = [];

  gridModules: {
    name: string;
    module_code?: string;
    module_alias?: string;
    module_id?: any;
  }[][] = [];

  isFirstStepCompleted = false;
  isFirstStepError = false;
  isSecondStepCompleted = false;
  isThirdStepCompleted = false;
  isFourthStepCompleted = false;
  isOpexStepCompleted = false;
  isSecondStepError = false;
  isThirdStepError = false;
  isFourthStepError = false;
  isOpexStepError = false;
  isAiTopicStepCompleted = false;
  isAiTopicStepError = false;

  selectedOpexIndex: number | null = null;

  createdGroups: any[] = [];

  // Track modules with validation errors (modules without parameters)
  modulesWithValidationErrors: Set<string> = new Set(); // Stores keys like "row_col"

  genericLibraryData: any;
  private genericLibraryOpexWaitPromise: Promise<void> | null = null;
  rawConfigurationData: any; // Store the raw configuration data from API

  // Add these properties
  showModuleSearch = false;
  moduleSearchText = "";

  // Example structure: { [moduleKey: string]: ModuleConfig }
  moduleConfigs: { [key: string]: any } = {};

  editingFormulaRow: any = null;

  // Formula editor state
  formulaEditorVisible = false;
  formulaEditorText = "";
  formulaEditorRow: any = null;

  editingOpexRow: any = null;
  originalOpexRowData: any = null; // Store original row data for cancel functionality

  youtubeHelpVisible = false;
  youtubeEmbedUrl: any = "";

  // Description modal properties
  isDescriptionModalOpen = false;
  descriptionModalText = "";

  // Video IDs for each step
  youtubeStepVideoIds = [
    "RuHW6qUZYG8", // Step 1: Basic Details
    "B-eFVqnWa7o", // Step 2: Edit & Configure Module
    "B-eFVqnWa7o", // Step 3: Group Modules
    "PtpftSYJMjs", // Step 4: Site Score
    "MTlQvyNQ3PM", // Step 5: Open Opex
    "MTlQvyNQ3PM", // Step 6: AI Topic Settings (using same video for now)
  ];

  siteNameFromSession: string = "";
  // Authoritative site being edited. The "Configure Site" action routes to
  // /site/editsite/:siteid, so the route param identifies the row that was
  // clicked. Previously the component always read sessionStorage['sites-info'][0]
  // which, for a system admin, is the first entry of their allsites (site 1) —
  // making every row open site 1. Resolved once in ngOnInit.
  activeSiteId: string | number | null = null;
  showGroupModulesStep = false;
  isLoadingFormData = false;
  isFormModified = false;
  lastSavedTimestamp: Date | null = null;

  // Add a 'data' property to hold group info or other dynamic data
  data: any = {};

  // Add this property for dynamic module ranges
  public dynamicModuleRanges: {
    code: string;
    name: string;
    range: [number, number];
  }[] = [];

  userRole: string = "";
  isCategoryMode: boolean = false;
  isNew: boolean = false;
  categoryOptions: any[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private http: HttpClient,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private notificationsService: NotificationsService
  ) {
    this.firstFormGroup = this._formBuilder.group({
      siteNameCtrl: ["", Validators.required],
      siteAddressCtrl: ["", [Validators.required]],
      contactNumberCtrl: ["", [Validators.required, Validators.pattern(/^\+?[0-9]{1,15}$/), Validators.maxLength(16)]],
      siteDescriptionCtrl: ["", Validators.required],
      plantViewCtrl: ["", Validators.required],
      globalDataCleansingCtrl: ["", Validators.required],
      currencyCtrl: ["", Validators.required],
      assistantIdCtrl: [""], // Initially no Validators.required
      apiKeyCtrl: [""], // Initially no Validators.required
      alertTypeCtrl: [""], // Initially no Validators.required
      latitudeCtrl: [""], // Initially no Validators.required
      longitudeCtrl: [""], // Initially no Validators.required
      categoryCtrl: [""], // Initially no Validators.required
    });
    this.secondFormGroup = this._formBuilder.group({
      channels: [null, [Validators.required, Validators.min(1)]],
      positions: [null, [Validators.required, Validators.min(1)]],
    });
    this.thirdFormGroup = this._formBuilder.group({
      thirdCtrl: ["", Validators.required],
    });
    this.fourthFormGroup = this._formBuilder.group({
      fourthCtrl: [""],
    });
    this.fifthFormGroup = this._formBuilder.group({
      aiTopicCtrl: [""],
    });
  }

  /**
   * Start polling backend for save status using a best-effort token (jobId or siteId).
   * This method is tolerant to different backend response shapes and will stop
   * when a terminal state is observed or when max attempts are reached.
   */
  private startSaveStatusPolling(
    jobId: any,
    siteId?: any,
    statusEndpoint?: string
  ) {
    // Clear any previous polling
    this.stopSaveStatusPolling();

    this.saveStatusPollingAttempts = 0;

    // Build status URL. If backend returned a statusEndpoint, use it (absolute or relative).
    let statusUrl = `${environment.nodeUrl1}siteSaveStatus`;
    if (statusEndpoint && typeof statusEndpoint === "string") {
      if (statusEndpoint.startsWith("http")) {
        statusUrl = statusEndpoint;
      } else if (statusEndpoint.startsWith("/")) {
        statusUrl = environment.nodeUrl1.replace(/\/$/, "") + statusEndpoint;
      } else {
        statusUrl = environment.nodeUrl1 + statusEndpoint;
      }
    }

    // If backend returns a status path under /api/moduleReports, rewrite it
    // to the siteSetting jobStatus path (preserve host and job id).
    try {
      if (
        typeof statusUrl === "string" &&
        statusUrl.indexOf("/api/moduleReports") !== -1
      ) {
        statusUrl = statusUrl.replace("/api/moduleReports", "/siteSetting");
      }
    } catch (e) {
      // noop — if replacement fails, continue with original statusUrl
    }

    const headers: any = { "Content-Type": "application/json" };
    const sessionToken = sessionStorage.getItem("auth_token");
    if (sessionToken) headers["Authorization"] = `Bearer ${sessionToken}`;

    // Try to read current user id from localStorage so we can include it in poll requests
    let storedUserId: any = null;
    try {
      const userStr = sessionStorage.getItem("user-info");
      if (userStr) {
        const parsedUser = JSON.parse(userStr);
        storedUserId = parsedUser?.id || parsedUser?.user_id || null;
      }
    } catch (e) {
      // ignore parsing errors
    }

    const poll = () => {
      this.saveStatusPollingAttempts++;

      if (this.saveStatusPollingAttempts > this.saveStatusMaxAttempts) {

        // Update step 8 as failed (timeout) in the save notification
        if (this.currentSaveNotificationId) {
          this.notificationsService.updateNotificationStepStatus(
            this.currentSaveNotificationId, 7, 'failed'
          );
        }

        this.notificationsService.setIsSiteSettingSaving(false);
        this.stopSaveStatusPolling();
        if (this.savingDialogRef) {
          try {
            this.savingDialogRef.close();
          } catch (e) {}
          this.savingDialogRef = null;
        }
        
        // Update the existing notification instead of creating a new one
        if (this.currentSaveNotificationId) {
          this.notificationsService.updateNotificationById(
            this.currentSaveNotificationId,
            {
              title: "Site Configuration Save Timeout",
              message: "Save operation timed out. Please check later.",
              shortMessage: "Save operation timed out",
              severity: "warning",
              isProcessing: false,
            }
          );
        }
        return;
      }

      const payload: any = { purpose: "saveStatus" };
      // Attach jobId if provided
      if (jobId) {
        payload.jobId = jobId;
      }
      // Ensure site_id is the actual site identifier when available (do NOT send jobId as site_id)
      if (siteId) {
        payload.site_id = siteId;
      }
      // Include user_id (if available) so backend can authenticate/authorize status requests
      if (storedUserId) {
        payload.user_id = storedUserId;
      }

      this.http.post(statusUrl, payload, { headers }).subscribe(
        (statusRes: any) => {
          // Normalize status shapes. Some backends wrap info under `jobStatus`.
          const jobObj = statusRes?.jobStatus ?? statusRes ?? {};

          // Extract raw state and progress from available shapes
          let rawState: any =
            jobObj?.status ??
            statusRes?.status ??
            statusRes?.state ??
            (statusRes?.completed ? "completed" : undefined);
          const progressRaw =
            jobObj?.progress ??
            statusRes?.progress ??
            statusRes?.percentage ??
            statusRes?.percent;

          // Normalize numeric status codes and strings
          let state: string | undefined;
          if (typeof rawState === "number") {
            state = rawState === 1 ? "completed" : String(rawState);
          } else if (typeof rawState === "string") {
            state = rawState.toLowerCase();
          } else {
            state = undefined;
          }

          // Normalize progress to a number when possible
          let numericProgress: number | null = null;
          if (typeof progressRaw === "number") numericProgress = progressRaw;
          else if (
            typeof progressRaw === "string" &&
            progressRaw.trim() !== "" &&
            !isNaN(Number(progressRaw))
          )
            numericProgress = Number(progressRaw);

          // Terminal completed states (accept various spellings, case-insensitive)
          const statusText =
            (jobObj?.status ?? statusRes?.status ?? state ?? "") + "";
          const lowerStatusText = String(statusText).toLowerCase();
          const isCompleted =
            (lowerStatusText &&
              (lowerStatusText.indexOf("complete") !== -1 ||
                lowerStatusText.indexOf("done") !== -1 ||
                lowerStatusText === "success" ||
                lowerStatusText === "succeeded")) ||
            statusRes?.success === true ||
            statusRes?.done === true ||
            (numericProgress !== null && numericProgress >= 100);

          // Running/in-progress detection: only when progress < 100 or explicit running state
          const isRunning =
            (state &&
              (state.indexOf("in_progress") !== -1 ||
                state.indexOf("running") !== -1 ||
                state.indexOf("progress") !== -1)) ||
            (numericProgress !== null &&
              numericProgress > 0 &&
              numericProgress < 100);

          // If in progress, update progress on the existing notification (don't create new one)
          if (isRunning) {
            const msg =
              numericProgress !== null
                ? `Saving in progress (${numericProgress}%)`
                : "Saving in progress...";
            
            // Update the existing notification's message instead of creating a new one
            if (this.currentSaveNotificationId) {
              this.notificationsService.updateNotificationById(
                this.currentSaveNotificationId,
                {
                  message: msg,
                  shortMessage: msg,
                  progress: numericProgress || undefined,
                }
              );
            }
            return; // continue polling
          }

          if (isCompleted) {

            // Update step 8 as completed in the save notification
            if (this.currentSaveNotificationId) {
              this.notificationsService.updateNotificationStepStatus(
                this.currentSaveNotificationId, 7, 'completed', 100
              );
            }

            this.notificationsService.setIsSiteSettingSaving(false);
            this.stopSaveStatusPolling();
            if (this.savingDialogRef) {
              try {
                this.savingDialogRef.close();
              } catch (e) {}
              this.savingDialogRef = null;
            }
            const resultMsg =
              jobObj?.message ||
              jobObj?.result?.msg ||
              statusRes?.message ||
              "Site settings saved successfully.";
            
            // Update the existing notification instead of creating a new one
            if (this.currentSaveNotificationId) {
              this.notificationsService.updateNotificationById(
                this.currentSaveNotificationId,
                {
                  title: "Site Configuration Saved",
                  message: resultMsg,
                  shortMessage: resultMsg,
                  severity: "success",
                  isProcessing: false,
                }
              );
            }
            return;
          }

          // Failure detection
          if (
            state === "failed" ||
            statusRes?.success === false ||
            statusRes?.error ||
            lowerStatusText.indexOf("fail") !== -1
          ) {

            // Update step 8 as failed in the save notification
            if (this.currentSaveNotificationId) {
              this.notificationsService.updateNotificationStepStatus(
                this.currentSaveNotificationId, 7, 'failed'
              );
            }

            this.notificationsService.setIsSiteSettingSaving(false);
            this.stopSaveStatusPolling();
            if (this.savingDialogRef) {
              try {
                this.savingDialogRef.close();
              } catch (e) {}
              this.savingDialogRef = null;
            }
            
            // Update the existing notification instead of creating a new one
            const errorMsg = jobObj?.message || statusRes?.message || "Save failed on server.";
            if (this.currentSaveNotificationId) {
              this.notificationsService.updateNotificationById(
                this.currentSaveNotificationId,
                {
                  title: "Site Configuration Save Failed",
                  message: errorMsg,
                  shortMessage: errorMsg,
                  severity: "error",
                  isProcessing: false,
                }
              );
            }
            return;
          }

          // otherwise continue polling
        },
        (err) => {
        }
      );
    };

    // Call immediately and then repeat every 30 seconds
    poll();
    this.saveStatusPollingId = setInterval(poll, 30000);
  }

  private stopSaveStatusPolling() {
    if (this.saveStatusPollingId) {
      clearInterval(this.saveStatusPollingId);
      this.saveStatusPollingId = null;
    }
    this.saveStatusPollingAttempts = 0;
  }

  // Dialog reference for the saving modal so we can close it from callbacks
  private savingDialogRef: any = null;

  // Save notification ID for step progress updates
  private currentSaveNotificationId: string | null = null;

  // Polling control for backend save status
  private saveStatusPollingId: any = null;
  private saveStatusPollingAttempts = 0;
  private saveStatusMaxAttempts = 60; // max attempts; total timeout = maxAttempts * interval (interval currently 60s)

  /**
   * Helper function to determine plant view value from backend data
   * Backend stores it as isgroup (1 = groupsAndSubgroups, 0 = default)
   * but may also have plant_view or plantView fields
   */
  private getPlantViewValue(data: any): string {
    // First check if isgroup is present (the actual saved field)
    if (data.isgroup !== undefined && data.isgroup !== null) {
      return data.isgroup === 1 || data.isgroup === "1" || data.isgroup === true
        ? "groupsAndSubgroups"
        : "default";
    }
    // Fallback to plant_view or plantView fields if present
    if (
      data.plant_view !== undefined &&
      data.plant_view !== null &&
      data.plant_view !== ""
    ) {
      return data.plant_view;
    }
    if (
      data.plantView !== undefined &&
      data.plantView !== null &&
      data.plantView !== ""
    ) {
      return data.plantView;
    }
    // Default to "default" if nothing found
    return "default";
  }

  /**
   * Helper function to get the display name for a module (alias if available, otherwise module name)
   */
  getModuleDisplayName(module: any): string {
    if (!module) return "";
    // Check for alias in various possible field names
    const alias = module.aliasName || module.module_alias || module.alias;
    if (alias && alias.trim() !== "") {
      return alias;
    }
    // Fallback to module name
    return module.name || module.module_name || "";
  }

  /**
   * Helper function to determine global data cleansing value from backend data
   * Backend stores it as iszero (1 = dataWithNoDate, 2 = dataWithNoValue, 0 = Factory/Default)
   * but may also have global_data_cleansing or globalDataCleansing fields
   */
  private getGlobalDataCleansingValue(data: any): string {
    // First check if iszero is present (the actual saved field)
    if (data.iszero !== undefined && data.iszero !== null) {
      if (data.iszero === 1 || data.iszero === "1" || data.iszero === true) {
        return "dataWithNoDate";
      }
      if (data.iszero === 2 || data.iszero === "2") {
        return "dataWithNoValue";
      }
      // iszero === 0 or any other value means "Factory" (Default)
      return "Factory";
    }
    // Fallback to global_data_cleansing or globalDataCleansing fields if present
    if (
      data.global_data_cleansing !== undefined &&
      data.global_data_cleansing !== null &&
      data.global_data_cleansing !== ""
    ) {
      return data.global_data_cleansing;
    }
    if (
      data.globalDataCleansing !== undefined &&
      data.globalDataCleansing !== null &&
      data.globalDataCleansing !== ""
    ) {
      return data.globalDataCleansing;
    }
    // Default to "Factory" if nothing found (matches the default option in the dropdown)
    return "Factory";
  }

  /**
   * The sub-category record being edited. Despite the name, the `category_id` query
   * param identifies the sub-category itself — its parent is `category_master_id`,
   * held by the "Select Category" dropdown. Null while creating a new sub-category,
   * which is why the config tabs stay hidden until it has been saved once.
   */
  private resolveSubCategoryId(): string | number {
    if (!this.isCategoryMode || this.isNew) {
      return null;
    }

    const queryParams = this.activatedRoute.snapshot.queryParams;
    if (queryParams["category_id"]) {
      return queryParams["category_id"];
    }

    try {
      const stored = sessionStorage.getItem("category-info");
      if (stored) {
        const info = JSON.parse(stored);
        const record = Array.isArray(info) ? info[0] : info;
        return record?.category_id ?? record?.id ?? null;
      }
    } catch {
      // Malformed session data is not fatal - the tabs simply stay hidden.
    }
    return null;
  }

  /**
   * The grid code of one module card, i.e. the specific unit on the plant: channel plus
   * position ("1A"), or "00" / "0LAB" / "0CP" for the special modules.
   *
   * `module_id` is the library's module *type*: a site can run the same type many times
   * (8 "Aerobic Bio" units, 5 "Clarifier" units) and every one of those cards carries the
   * same `module_id`, so it cannot address a single unit. The grid code can, and it is
   * what keeps a Target/Indexes/Behaviour save on the unit the user clicked.
   *
   * Deliberately NOT read from `module.module_code`: for a module freshly dropped on the
   * grid that field holds the *library type's* code (see onGridDrop), which is identical
   * across all its units — using it would reintroduce the very bug this fixes. Only the
   * grid position identifies a unit, so it comes from the clicked cell, falling back to
   * the channel/position pair on a saved row.
   *
   * Null in sub-category mode, whose endpoints do not take it.
   */
  /**
   * A sub-category being created has no id yet, so the module config dialog cannot save
   * its Target / Indexes / Behaviour tabs against one. It buffers them instead and hands
   * them back on close; they travel in the create payload under each module's entry.
   *
   * Site mode is deliberately excluded: site config is keyed per grid unit, and those
   * units do not exist until the site is created.
   */
  private get isConfigDraft(): boolean {
    // `isNew` and not merely "no id resolved": an existing sub-category whose id fails
    // to resolve must keep today's behaviour and hide the tabs, not quietly switch to
    // buffering edits that the live save endpoints should have taken.
    return this.isCategoryMode && this.isNew && !this.resolveSubCategoryId();
  }

  private resolveModuleCode(module: any, gridCode?: string): string {
    if (this.isCategoryMode) {
      return null;
    }

    const code =
      gridCode ||
      (module?.channel != null && module?.position != null
        ? `${module.channel}${module.position}`
        : null);

    if (!code) {
      // Loud on purpose. Without it the six config endpoints fall back to module-wide
      // behaviour, which is silently WRONG for a module the site runs more than once -
      // the bug this field exists to fix.
      console.warn(
        "[site config] No grid code for module " +
          `"${module?.module_name || module?.name}" - Target/Indexes/Behaviour will be ` +
          "saved module-wide, which is wrong if this site runs the module more than once."
      );
    }

    return code;
  }

  /**
   * The unit code for a `moduleConfigs` entry, for formula qualification.
   *
   * Deliberately does NOT use `resolveModuleCode`: that one returns null in category
   * mode because the sub-category endpoints do not key on the unit, but a formula still
   * has to say which unit an operand came from. Also avoids `config.module_code`, which
   * holds the library module's code for some entries and the grid code for others.
   * Channel + position, else the grid code the `moduleConfigs` key ends with.
   */
  private unitCodeForConfig(key: string, config: any): string {
    if (config?.channel != null && config?.position != null) {
      return `${config.channel}${config.position}`;
    }
    return key.substring(key.lastIndexOf("_") + 1).trim();
  }

  /**
   * Every configured module on this plant except the one being opened, in the shape the
   * config dialog's formula resolver wants. This screen is the only place that holds
   * them all at once, so a formula can only reach across units if we hand them over.
   *
   * Parameters are always present in memory (`allparameters` is populated for saved and
   * draft entries alike). Index names are not - only a draft carries them, so the tabs
   * component fetches the rest on demand.
   */
  private buildSiblingModules(excludeKey: string): any[] {
    return Object.keys(this.moduleConfigs)
      .filter((key) => key !== excludeKey)
      .map((key) => {
        const config = this.moduleConfigs[key];
        const moduleCode = this.unitCodeForConfig(key, config);
        const parameters = (config?.allparameters ?? config?.parameters ?? [])
          .map((param: any) => ({
            name: String(param?.parameter_name ?? param?.parameters ?? "").trim(),
            code: String(param?.code ?? param?.parameter_code ?? "").trim(),
          }))
          .filter((param: any) => param.name && param.code);

        return {
          moduleCode,
          moduleId: config?.module_id ?? null,
          label:
            config?.aliasName ||
            config?.module_alias ||
            config?.module_name ||
            moduleCode,
          parameters,
          indexes: (config?.configDraft?.moduleIndexes?.indexes ?? [])
            .map((record: any) => ({
              name: String(record?.index_name ?? "").trim(),
              code: String(record?.index_code ?? "").trim(),
            }))
            .filter((index: any) => index.name),
        };
      })
      // A unit with no code cannot be qualified, and one with nothing to offer would
      // only cost a request later.
      .filter((sibling) => sibling.moduleCode && (sibling.parameters.length || sibling.moduleId));
  }

  private loadCategoryOptions(): void {
    this.http.post(`${environment.nodeUrl1}loadCategory`, {}).subscribe({
      next: (response: any) => {
        if (response?.status === 1) {
          this.categoryOptions = response?.data || [];
        } else {
          this.categoryOptions = response?.data || [];
        }
      },
      error: () => {
        this.categoryOptions = [];
      }
    });
  }

  ngOnInit(): void {

    // Resolve which site is being edited from the route (/site/editsite/:siteid).
    this.activeSiteId = this.resolveActiveSiteId();

    // Check if we're in category mode via query param
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.isCategoryMode = queryParams["mode"] === "category";
    this.isNew = queryParams["isNew"] === "true";
    if (this.isCategoryMode) {
      this.loadCategoryOptions();
    }

    // Update breadcrumb steps label based on isNew
    if (this.isNew && this.isCategoryMode) {
      this.breadcrumbSteps[0].label = "New Category Details";
      this.breadcrumbSteps[1].label = "Add & Configure Module";
    } else if (this.isCategoryMode) {
      this.breadcrumbSteps[0].label = "Category Details";
      this.breadcrumbSteps[1].label = "Edit & Configure Module";
    } else {
      this.breadcrumbSteps[0].label = "Basic Details";
      this.breadcrumbSteps[1].label = "Edit & Configure Module";
    }

    this.loadGenericLibrary();
    this.updateBreadcrumb(0);

    // Get user role from session storage
    this.getUserRole();

    // Update form validators based on user role and category mode
    this.updateFormValidators();

    // Always attempt to send request to backend when page opens
    this.initializeFormData();

    // Ensure special modules maintain correct properties after initialization
    this.ensureSpecialModuleProperties();

    // Start auto-save timer (save every 2 minutes)
    this.startAutoSave();

    this.firstFormGroup.get("plantViewCtrl")?.valueChanges.subscribe((val) => {
      this.showGroupModulesStep = val === "groupsAndSubgroups";

      if (!this.showGroupModulesStep) {
        // Mark thirdFormGroup as valid so stepper can pass through
        this.thirdFormGroup.get("thirdCtrl")?.setValue("skip");
        this.thirdFormGroup.get("thirdCtrl")?.updateValueAndValidity();
      } else {
        // Reset if needed
        this.thirdFormGroup.get("thirdCtrl")?.setValue("");
        this.thirdFormGroup.get("thirdCtrl")?.updateValueAndValidity();
      }
      setTimeout(() => this.cdr.detectChanges());
    });

    // Update validators when modules are configured
    this.updateChannelPositionValidators();

    this.secondFormGroup.get("channels")?.valueChanges.subscribe((newValue) => {
      if (newValue === null || newValue === undefined) {
        return;
      }
      // Validate before resizing
      const { maxChannel } = this.getMaxChannelAndPosition();
      if (maxChannel > 0 && newValue < maxChannel) {
        this.messageService.add({
          severity: "error",
          summary: "Invalid Channel Value",
          detail: `Channel cannot be smaller than ${maxChannel} (modules are configured up to channel ${maxChannel})`,
        });
        // Revert to minimum valid value
        this.secondFormGroup
          .get("channels")
          ?.setValue(maxChannel, { emitEvent: false });
        return;
      }
      this.initGridModules();
    });

    this.secondFormGroup
      .get("positions")
      ?.valueChanges.subscribe((newValue) => {
        if (newValue === null || newValue === undefined) {
          return;
        }
        // Validate before resizing
        const { maxPosition } = this.getMaxChannelAndPosition();
        if (maxPosition > 0 && newValue < maxPosition) {
          this.messageService.add({
            severity: "error",
            summary: "Invalid Position Value",
            detail: `Position cannot be smaller than ${maxPosition} (modules are configured up to position ${this.getColumnLabel(
              maxPosition - 1
            )})`,
          });
          // Revert to minimum valid value
          this.secondFormGroup
            .get("positions")
            ?.setValue(maxPosition, { emitEvent: false });
          return;
        }
        this.initGridModules();
      });

    // Track form modifications
    this.firstFormGroup.valueChanges.subscribe(() => {
      this.isFormModified = true;
      // Clear first step error state when form becomes valid
      if (this.firstFormGroup.valid) {
        this.isFirstStepError = false;
      }
      // Save form data to session storage as backup
      // But only if we don't have fresh backend data
      if (!this.hasSavedConfiguration) {
        this.saveFormDataToSession();
      }
    });

    // Initialize row.selected for all opexData rows
    Object.values(this.opexData).forEach((arr: any[]) => {
      arr.forEach((row) => {
        row.selected = row.selected ?? false;
      });
    });
  }

  /**
   * Test the API endpoint to see what response structure we get
   */
  testApiEndpoint() {
    const siteInfoStr = sessionStorage.getItem("sites-info");
    if (siteInfoStr) {
      try {
        const siteInfoArr = JSON.parse(siteInfoStr);
        if (
          Array.isArray(siteInfoArr) &&
          siteInfoArr.length > 0 &&
          siteInfoArr[0].site_id
        ) {
          const siteId = siteInfoArr[0].site_id;

          const payload = { site_id: siteId, purpose: "prefilldata" };
          const apiUrl = `${environment.nodeUrl1}getSitePrefillData`;


          // Test without headers first
          this.http.post(apiUrl, payload).subscribe(
            (response: any) => {
            },
            (error) => {
            }
          );
        }
      } catch (e) {
      }
    }
  }

  /**
   * Resolve the site that should be edited. The route param (:siteid) is
   * authoritative because the "Configure Site" action navigates to
   * /site/editsite/:siteid. Only when no route param is present do we fall back
   * to the first site in session storage (legacy entry points / direct opens).
   */
  private resolveActiveSiteId(): string | number | null {
    const routeSiteId = this.activatedRoute.snapshot.paramMap.get("siteid");
    if (routeSiteId != null && routeSiteId !== "") {
      const numericSiteId = Number(routeSiteId);
      return Number.isNaN(numericSiteId) ? routeSiteId : numericSiteId;
    }

    const siteInfoStr = sessionStorage.getItem("sites-info");
    if (siteInfoStr) {
      try {
        const siteInfoArr = JSON.parse(siteInfoStr);
        if (
          Array.isArray(siteInfoArr) &&
          siteInfoArr.length > 0 &&
          siteInfoArr[0].site_id
        ) {
          return siteInfoArr[0].site_id;
        }
      } catch (e) {
      }
    }
    return null;
  }

  /**
   * Initialize form data when the page opens
   * This method will always attempt to send a request to the backend
   * UNLESS it's a new category creation (isNew=true in query params)
   */
  initializeFormData() {
    // Check if this is a new category creation
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const isNewCategory =
      queryParams["isNew"] === "true" && this.isCategoryMode;

    if (isNewCategory) {
      // Clear any existing form data from session storage
      this.clearFormDataFromSession();
      // Don't call prefill API for new categories - only allGenericLibrary is needed
      return;
    }

    // Check if we're editing a category (category_id in query params)
    if (this.isCategoryMode && queryParams["category_id"]) {
      const categoryId = queryParams["category_id"];
      this.prefillFormWithCategoryData(categoryId);
      return;
    }

    // Load the site identified by the route (activeSiteId). Session storage is
    // only consulted for a display name while the authoritative prefill API
    // response is in flight.
    const siteId = this.activeSiteId;
    if (siteId) {
      const siteInfoStr = sessionStorage.getItem("sites-info");
      if (siteInfoStr) {
        try {
          const siteInfoArr = JSON.parse(siteInfoStr);
          const match = Array.isArray(siteInfoArr)
            ? siteInfoArr.find((s) => s && s.site_id == siteId)
            : null;
          this.siteNameFromSession = match?.site_name || "";
        } catch (e) {
          this.siteNameFromSession = "";
        }
      }

      this.prefillFormWithSiteData(siteId);
      // Note: fetchCompleteSiteConfiguration will be called from prefillFormWithSiteData
    } else {
      // If no site_id, try to restore from session storage
      this.restoreFormDataFromSession();
    }
  }

  prefillFormWithSiteData(siteId: number | string) {
    this.isLoadingFormData = true;

    // First, get basic site details
    const basicPayload = {
      site_id: siteId,
      purpose: "prefilldata",
    };


    // Using the nodeUrl1 endpoint as per the environment configuration
    const basicApiUrl = `${environment.nodeUrl1}getSitePrefillData`;

    // Add authentication headers if token exists
    const sessionToken = sessionStorage.getItem("auth_token");
    const headers: any = { "Content-Type": "application/json" };
    if (sessionToken) {
      headers["Authorization"] = `Bearer ${sessionToken}`;
    }

    this.http
      .post(basicApiUrl, basicPayload, { headers })
      .subscribe(
        (response: any) => {

          // Handle different response structures
          let siteData = null;

          if (response && response.success && response.data) {
            // Standard structure: { success: true, data: {...} }
            siteData = response.data;
          } else if (response && response.data) {
            // Structure: { data: {...} }
            siteData = response.data;
          } else if (response && response.success && response.result) {
            // Structure: { success: true, result: {...} }
            siteData = response.result;
          } else if (response && response.result) {
            // Structure: { result: {...} }
            siteData = response.result;
          } else if (
            response &&
            typeof response === "object" &&
            Object.keys(response).length > 0
          ) {
            // Direct data structure: { site_name: "...", site_address: "..." }
            siteData = response;
          }

          if (siteData) {

            // Prefill the firstFormGroup with the received data
            const plantViewValue = this.getPlantViewValue(siteData);
            const globalDataCleansingValue =
              this.getGlobalDataCleansingValue(siteData);

            this.firstFormGroup.patchValue({
              siteNameCtrl: siteData.site_name || siteData.siteName || "",
              siteAddressCtrl:
                siteData.site_address || siteData.siteAddress || "",
              contactNumberCtrl:
                siteData.site_phone || siteData.contactNumber || "",
              siteDescriptionCtrl:
                siteData.site_description || siteData.siteDescription || "",
              plantViewCtrl: plantViewValue,
              globalDataCleansingCtrl: globalDataCleansingValue,
              currencyCtrl: siteData.currency || siteData.site_currency || "",
              assistantIdCtrl:
                siteData.assistantid || siteData.assistantId || "",
              apiKeyCtrl: siteData.apikey || siteData.apiKey || "",
              alertTypeCtrl:
                siteData.alert_type || siteData.alertType || "default",
              latitudeCtrl: siteData.site_latitude || "",
              longitudeCtrl: siteData.site_longitude || "",
              categoryCtrl: siteData.category || "",
            });


            // Show success message
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: "Site data loaded successfully!",
            });

            // Reset form modification flag since we just loaded fresh data
            this.isFormModified = false;

            // Trigger change detection to update the UI
            this.cdr.detectChanges();

            // Now fetch complete configuration (modules, parameters, groups, etc.)
            this.fetchCompleteSiteConfiguration(siteId);
          } else {

            // Show warning message
            this.messageService.add({
              severity: "warn",
              summary: "Warning",
              detail:
                "No site data found. Please check your site configuration. Check console for details.",
            });
          }
        },
        (error) => {
          // Show user-friendly error message
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "Failed to load site data. Please try again or contact support if the issue persists.",
          });
        }
      )
      .add(() => {
        // Always set loading to false when request completes (success or error)
        this.isLoadingFormData = false;
      });
  }

  prefillFormWithCategoryData(categoryId: number | string) {
    this.isLoadingFormData = true;

    // Get category details using category API
    const categoryPayload = {
      purpose: "categorydetails",
      category_id: categoryId,
    };


    const categoryApiUrl = `${environment.nodeUrl1}category/categorydetails`;

    // Add authentication headers if token exists
    const sessionToken = sessionStorage.getItem("auth_token");
    const headers: any = { "Content-Type": "application/json" };
    if (sessionToken) {
      headers["Authorization"] = `Bearer ${sessionToken}`;
    }

    this.http
      .post(categoryApiUrl, categoryPayload, { headers })
      .subscribe(
        (response: any) => {

          // Handle different response structures
          let categoryInfo = null;

          if (response && response.status === 1 && response.categoryinfo) {
            categoryInfo = Array.isArray(response.categoryinfo)
              ? response.categoryinfo[0]
              : response.categoryinfo;


            if (categoryInfo) {
              // Map category data to form structure
              this.siteNameFromSession = categoryInfo.category_name || "";


              this.firstFormGroup.patchValue({
                siteNameCtrl: categoryInfo.category_name || "",
                siteDescriptionCtrl: categoryInfo.category_description || "",
                categoryCtrl: categoryInfo.category_master_id || "",
                // Don't set other fields as they're hidden in category mode
              });

              // Set channels and positions if available
              const channelCount = parseInt(categoryInfo.channel_count) || 0;
              const positionCount = parseInt(categoryInfo.position_count) || 0;


              if (channelCount > 0) {
                this.secondFormGroup.patchValue({
                  channels: channelCount,
                });
              }
              if (positionCount > 0) {
                this.secondFormGroup.patchValue({
                  positions: positionCount,
                });
              }

              // Initialize grid if channels and positions are set
              // Must be done before loading modules
              if (channelCount > 0 && positionCount > 0) {
                this.initGridModules();

                // Load selected modules (second step data) if available
                // Use setTimeout to ensure grid is fully initialized and form is updated
                setTimeout(() => {
                  if (
                    response.selectedmodules &&
                    Array.isArray(response.selectedmodules)
                  ) {

                    this.loadCategoryModulesToGrid(response.selectedmodules);

                    // Update dynamic module ranges after loading modules
                    this.updateDynamicModuleRanges();

                    // Load site score ranges from allscores
                    this.loadCategoryScoreRanges(response.selectedmodules);

                    // Trigger change detection
                    this.cdr.detectChanges();
                  }

                  // Load opex modules if available
                  if (
                    response.selectedopexmodules &&
                    Array.isArray(response.selectedopexmodules)
                  ) {
                    this.loadCategoryOpexConfiguration(
                      response.selectedopexmodules
                    );
                  }
                }, 200);
              }


              // Show success message
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Category data loaded successfully!",
              });

              // Reset form modification flag
              this.isFormModified = false;

              // Trigger change detection
              this.cdr.detectChanges();

              // Store category info in session storage for later use
              sessionStorage.setItem(
                "category-info",
                JSON.stringify([categoryInfo])
              );

              // Set configuration flags
              this.hasSavedConfiguration = true;
              this.isViewMode = true;
            }
          } else {
            this.messageService.add({
              severity: "warn",
              summary: "Warning",
              detail:
                "No category data found. Please check your category configuration.",
            });
          }
        },
        (error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "Failed to load category data. Please try again or contact support if the issue persists.",
          });
        }
      )
      .add(() => {
        // Always set loading to false when request completes
        this.isLoadingFormData = false;
      });
  }

  /**
   * Load category modules into grid and module configurations
   * This method handles the selectedmodules array from category API response
   */
  loadCategoryModulesToGrid(selectedModules: any[]) {
    if (!selectedModules || !Array.isArray(selectedModules)) {
      return;
    }


    selectedModules.forEach((categoryModule: any, index: number) => {
      const moduleName = categoryModule.module_name;
      const moduleCode = categoryModule.module_code;
      const channel = categoryModule.channel;
      const position = categoryModule.position;


      // Check if it's a special module (channel "0" and position "0")
      const isSpecialModule = channel === "0" && position === "0";

      if (isSpecialModule) {
        // Handle special modules (Complete System, Lab Module, Corporate Module)
        const key = `${moduleName}_${moduleCode}`;

        // Transform selectedparameters to parameters format
        const transformedParameters = (
          categoryModule.selectedparameters || []
        ).map((param: any) => {
          // Transform allranges structure from category API
          const transformedRanges = (param.allranges || []).map(
            (range: any) => ({
              score:
                typeof range.score === "string"
                  ? parseFloat(range.score)
                  : range.score || 0,
              min:
                typeof range.min === "string"
                  ? parseFloat(range.min)
                  : range.min || 0,
              max:
                typeof range.max === "string"
                  ? parseFloat(range.max)
                  : range.max || 0,
              selected: true,
              // Read the edited query from the same fields the site paths use;
              // keep `range_name` only as a last-resort fallback for legacy data.
              query:
                range.query || range.parameter_range_name || range.range_name || "",
              range_id: range.range_id,
              loadedFromSavedConfig: true,
              originalSelection: true,
              min_value:
                typeof range.min === "string"
                  ? parseFloat(range.min)
                  : range.min || 0,
              max_value:
                typeof range.max === "string"
                  ? parseFloat(range.max)
                  : range.max || 0,
            })
          );

          return {
            parameter_id: param.parameter_id,
            parameter_name: param.parameter_name,
            code: param.parameter_code || param.code,
            parameter_code: param.parameter_code || param.code,
            parameter_unit: param.parameter_unit || "",
            parameter_type: param.parameter_type || 0,
            parameter_formula: param.parameter_formula || "",
            upload_source: param.upload_source || 0,
            parameter_weight: param.parameter_weight || 0,
            allranges: transformedRanges,
            unit: param.parameter_unit || "",
            type: param.parameter_type || 0,
            uploadType: param.upload_source === 2 ? "Online" : "Manual",
            weight:
              typeof param.parameter_weight === "string"
                ? parseFloat(param.parameter_weight)
                : param.parameter_weight || 0,
            formula: param.parameter_formula || "",
            calculation: param.calculation || param.parameter_calculation || "",
            parameter_calculation:
              param.parameter_calculation || param.calculation || "",
            query: param.parameter_query || "",
            criticalParameter:
              param.parameter_critical === 1 ||
              param.parameter_critical === true,
            selected: true,
            checked: true,
            isFromSavedConfig: true,
            ranges: transformedRanges,
          };
        });

        // Score ranges (allscores) are not read here - loadCategoryScoreRanges
        // processes them when the site score step loads.

        // Handle displayOnDashboard - check both camelCase and snake_case, prioritize camelCase
        const displayOnDashboardValue =
          categoryModule.displayOnDashboard !== undefined
            ? categoryModule.displayOnDashboard
            : categoryModule.display_on_dashboard !== undefined
            ? !!categoryModule.display_on_dashboard
            : this.moduleConfigs[key]?.displayOnDashboard !== undefined
            ? this.moduleConfigs[key].displayOnDashboard
            : false;

        this.moduleConfigs[key] = {
          module_id: categoryModule.module_id,
          module_name: moduleName,
          module_code: moduleCode,
          module_alias: categoryModule.module_alias || moduleName,
          aliasName: categoryModule.module_alias || moduleName,
          module_type: categoryModule.module_type || "",
          moduleType: categoryModule.module_type || "",
          displayOnDashboard: displayOnDashboardValue,
          display_on_dashboard: displayOnDashboardValue, // Also preserve snake_case for consistency
          parameters: transformedParameters,
          allparameters: transformedParameters,
          selectedProductCodes: transformedParameters
            .map((p: any) => p.code || p.parameter_code || "")
            .filter(Boolean),
          isFromSavedConfiguration: true,
        };

      } else {
        // Handle grid modules
        const channelNum = parseInt(channel);
        const row = channelNum - 1; // Convert channel to 0-based index (channel "2" -> row 1)
        const col = this.getColumnIndex(position); // Convert position to 0-based index (position "C" -> col 2)


        if (
          row >= 0 &&
          row < this.gridModules.length &&
          col >= 0 &&
          col < (this.gridModules[0]?.length || 0)
        ) {
          // Place module in grid
          this.gridModules[row][col] = {
            name: moduleName,
            module_id: categoryModule.module_id,
            module_code: moduleCode,
            module_alias: categoryModule.module_alias || moduleName,
          } as any;

          // Transform selectedparameters to parameters format
          const transformedParameters = (
            categoryModule.selectedparameters || []
          ).map((param: any) => {
            // Transform allranges structure from category API
            const transformedRanges = (param.allranges || []).map(
              (range: any) => ({
                score:
                  typeof range.score === "string"
                    ? parseFloat(range.score)
                    : range.score || 0,
                min:
                  typeof range.min === "string"
                    ? parseFloat(range.min)
                    : range.min || 0,
                max:
                  typeof range.max === "string"
                    ? parseFloat(range.max)
                    : range.max || 0,
                selected: true,
                // Read the edited query from the same fields the site paths use;
                // keep `range_name` only as a last-resort fallback for legacy data.
                query:
                  range.query || range.parameter_range_name || range.range_name || "",
                range_id: range.range_id,
                loadedFromSavedConfig: true,
                originalSelection: true,
                min_value:
                  typeof range.min === "string"
                    ? parseFloat(range.min)
                    : range.min || 0,
                max_value:
                  typeof range.max === "string"
                    ? parseFloat(range.max)
                    : range.max || 0,
              })
            );

            return {
              parameter_id: param.parameter_id,
              parameter_name: param.parameter_name,
              code: param.parameter_code || param.code,
              parameter_code: param.parameter_code || param.code,
              parameter_unit: param.parameter_unit || "",
              parameter_type: param.parameter_type || 0,
              parameter_formula: param.parameter_formula || "",
              upload_source: param.upload_source || 0,
              parameter_weight: param.parameter_weight || 0,
              allranges: transformedRanges,
              unit: param.parameter_unit || "",
              type: param.parameter_type || 0,
              uploadType: param.upload_source === 2 ? "Online" : "Manual",
              weight:
                typeof param.parameter_weight === "string"
                  ? parseFloat(param.parameter_weight)
                  : param.parameter_weight || 0,
              formula: param.parameter_formula || "",
              calculation: param.calculation || param.parameter_calculation || "",
              parameter_calculation:
                param.parameter_calculation || param.calculation || "",
              query: param.parameter_query || "",
              criticalParameter:
                param.parameter_critical === 1 ||
                param.parameter_critical === true,
              selected: true,
              checked: true,
              isFromSavedConfig: true,
              ranges: transformedRanges,
            };
          });

          // Create module configuration key
          const key = `${moduleName}_${channel}${position}`;

          // Handle displayOnDashboard - check both camelCase and snake_case, prioritize camelCase
          const displayOnDashboardValue =
            categoryModule.displayOnDashboard !== undefined
              ? categoryModule.displayOnDashboard
              : categoryModule.display_on_dashboard !== undefined
              ? !!categoryModule.display_on_dashboard
              : this.moduleConfigs[key]?.displayOnDashboard !== undefined
              ? this.moduleConfigs[key].displayOnDashboard
              : false;

          this.moduleConfigs[key] = {
            module_id: categoryModule.module_id,
            module_name: moduleName,
            module_code: moduleCode,
            module_alias: categoryModule.module_alias || moduleName,
            aliasName: categoryModule.module_alias || moduleName,
            module_type: categoryModule.module_type || "",
            moduleType: categoryModule.module_type || "",
            displayOnDashboard: displayOnDashboardValue,
            display_on_dashboard: displayOnDashboardValue, // Also preserve snake_case for consistency
            parameters: transformedParameters,
            allparameters: transformedParameters,
            selectedProductCodes: transformedParameters
              .map((p: any) => p.code || p.parameter_code || "")
              .filter(Boolean),
            isFromSavedConfiguration: true,
          };

          // Handle score ranges (allscores) will be processed in loadCategoryScoreRanges

        }
      }
    });


    // Update validators after loading modules
    this.updateChannelPositionValidators();
  }

  /**
   * Load category opex configuration
   */
  loadCategoryOpexConfiguration(selectedOpexModules: any[]) {
    if (!selectedOpexModules || !Array.isArray(selectedOpexModules)) {
      return;
    }


    // Ensure opexParameters is populated from generic library if empty
    if (!this.opexParameters || this.opexParameters.length === 0) {
      if (
        this.genericLibraryData &&
        Array.isArray(this.genericLibraryData.allopexmodules)
      ) {
        this.opexParameters = this.genericLibraryData.allopexmodules.map(
          (mod: any) => ({
            id: mod.module_id,
            label: mod.module_name || mod.opex_module_name || "",
            key: mod.module_code || mod.opex_module_code || "",
            checked: false,
          })
        );
      } else {
        // Fallback: Create opexParameters from the selectedOpexModules API response
        // This ensures we can still check and display modules even if generic library isn't loaded yet
        // Use a Map to prevent duplicates (in case same module appears multiple times in API)
        const uniqueModulesMap = new Map();
        selectedOpexModules.forEach((om: any) => {
          const moduleKey = om.opex_module_code || "";
          if (moduleKey && !uniqueModulesMap.has(moduleKey)) {
            uniqueModulesMap.set(moduleKey, {
              id: om.category_opex_module_id || null,
              label: om.opex_module_name || "",
              key: moduleKey,
              checked: true, // Mark as checked since they're in the selected modules from API
            });
          }
        });
        this.opexParameters = Array.from(uniqueModulesMap.values());
        // Since we already marked them as checked above, skip the matching logic below
        // Continue to populate opexData below
      }
    }

    // Update opex parameters with saved selections (only if generic library data was used)
    // If we created opexParameters from API response, they're already checked, so skip this
    if (
      this.genericLibraryData &&
      Array.isArray(this.genericLibraryData.allopexmodules) &&
      this.opexParameters.length > 0
    ) {

      let checkedCount = 0;
      this.opexParameters = this.opexParameters.map((param) => {
        const savedOpexModule = selectedOpexModules.find((om: any) => {
          const moduleCode = om.opex_module_code || "";
          const paramKey = param.key || "";
          // Try exact match first
          if (moduleCode === paramKey) {
            return true;
          }
          // Try case-insensitive match
          if (moduleCode.toLowerCase() === paramKey.toLowerCase()) {
            return true;
          }
          // Try matching by module_id if available
          if (
            om.category_opex_module_id &&
            param.id &&
            om.category_opex_module_id.toString() === param.id.toString()
          ) {
            return true;
          }
          return false;
        });
        if (savedOpexModule) {
          checkedCount++;
          return {
            ...param,
            checked: true,
          };
        }
        return param;
      });
    }

    // Populate opexData with ALL parameters from generic library, but mark API parameters as selected
    selectedOpexModules.forEach((opexModule: any, index: number) => {
      const opexKey = opexModule.opex_module_code;

      // First, find the module in generic library to get ALL parameters
      let allParameters: any[] = [];
      if (
        this.genericLibraryData &&
        Array.isArray(this.genericLibraryData.allopexmodules)
      ) {
        const genericModule = this.genericLibraryData.allopexmodules.find(
          (mod: any) => (mod.module_code || mod.opex_module_code) === opexKey
        );

        if (genericModule && genericModule.allopexparameters) {
          allParameters = genericModule.allopexparameters.map(
            (paramData: any) => ({
              parameter_id: paramData.parameter_id,
              parameter_name: paramData.parameter_name,
              details: paramData.details || "",
              parameter_unit: paramData.parameter_unit || "",
              parameter_type:
                this.getParameterTypeString(paramData.parameter_type) || "Raw",
              parameter_code: paramData.parameter_code || "",
              uploadType: paramData.upload_source === 2 ? "Online" : "Manual",
              formula: paramData.parameter_formula || "",
              price_type:
                this.getPriceTypeString(paramData.price_type) || "Fixed",
              price: paramData.price || "",
              parameter_banchmark: paramData.parameter_banchmark || null,
              selected: false, // Default to unchecked
            })
          );
        }
      } else {
        // If generic library isn't available, we'll populate with API parameters first
        // and then merge with generic library when it loads (handled in loadGenericLibrary)
        if (
          opexModule.selectedparameters &&
          Array.isArray(opexModule.selectedparameters)
        ) {
          allParameters = opexModule.selectedparameters.map((param: any) => ({
            parameter_id:
              param.category_opex_module_parameter_id || param.parameter_id,
            parameter_name: param.opex_parameter_name || param.parameter_name,
            details: param.details || "",
            parameter_unit: param.parameter_unit || "",
            parameter_type:
              this.getParameterTypeString(
                param.opex_parameter_type || param.parameter_type
              ) || "Raw",
            parameter_code: param.parameter_code || "",
            uploadType: param.upload_source === 2 ? "Online" : "Manual",
            formula: param.parameter_formula || "",
            price_type: this.getPriceTypeString(param.price_type) || "Fixed",
            price: param.price || "",
            parameter_banchmark: param.parameter_banchmark || null,
            selected: true,
          }));
        }
      }

      // If we have selected parameters from API, mark them as selected and update their values
      if (
        opexModule.selectedparameters &&
        Array.isArray(opexModule.selectedparameters)
      ) {

        // Create a map of selected parameters by parameter_code for quick lookup
        const selectedParamsMap = new Map();
        opexModule.selectedparameters.forEach((param: any) => {
          const paramCode = param.parameter_code || "";
          selectedParamsMap.set(paramCode, param);
        });

        // Update allParameters: mark selected ones and update their values from API
        allParameters = allParameters.map((param: any) => {
          const paramCode = param.parameter_code || "";
          const selectedParam = selectedParamsMap.get(paramCode);

          if (selectedParam) {
            // This parameter is in the API response - use API values and mark as selected
            return {
              ...param,
              parameter_id:
                selectedParam.category_opex_module_parameter_id ||
                selectedParam.parameter_id ||
                param.parameter_id,
              parameter_name:
                selectedParam.opex_parameter_name ||
                selectedParam.parameter_name ||
                param.parameter_name,
              details:
                selectedParam.details !== undefined
                  ? selectedParam.details
                  : param.details,
              parameter_unit:
                selectedParam.parameter_unit || param.parameter_unit,
              parameter_type:
                this.getParameterTypeString(
                  selectedParam.opex_parameter_type ||
                    selectedParam.parameter_type
                ) || param.parameter_type,
              parameter_code:
                selectedParam.parameter_code || param.parameter_code,
              uploadType:
                selectedParam.upload_source === 2
                  ? "Online"
                  : selectedParam.upload_source === 1
                  ? "Manual"
                  : param.uploadType,
              formula: selectedParam.parameter_formula || param.formula,
              price_type:
                this.getPriceTypeString(selectedParam.price_type) ||
                param.price_type,
              price:
                selectedParam.price !== undefined
                  ? selectedParam.price
                  : param.price,
              parameter_banchmark:
                selectedParam.parameter_banchmark !== undefined
                  ? selectedParam.parameter_banchmark
                  : param.parameter_banchmark,
              selected: true,
            };
          } else {
            // This parameter is not in API response - keep it but leave it unchecked
            return param;
          }
        });

        // Add any API parameters that don't exist in generic library
        opexModule.selectedparameters.forEach((selectedParam: any) => {
          const paramCode = selectedParam.parameter_code || "";
          const existsInAll = allParameters.some(
            (p) => (p.parameter_code || "") === paramCode
          );

          if (!existsInAll) {
            allParameters.push({
              parameter_id:
                selectedParam.category_opex_module_parameter_id ||
                selectedParam.parameter_id,
              parameter_name:
                selectedParam.opex_parameter_name ||
                selectedParam.parameter_name,
              details: selectedParam.details || "",
              parameter_unit: selectedParam.parameter_unit || "",
              parameter_type:
                this.getParameterTypeString(
                  selectedParam.opex_parameter_type ||
                    selectedParam.parameter_type
                ) || "Raw",
              parameter_code: paramCode,
              uploadType:
                selectedParam.upload_source === 2 ? "Online" : "Manual",
              formula: selectedParam.parameter_formula || "",
              price_type:
                this.getPriceTypeString(selectedParam.price_type) || "Fixed",
              price: selectedParam.price || "",
              parameter_banchmark: selectedParam.parameter_banchmark || null,
              selected: true,
            });
          }
        });
      }

      // Set the final opexData for this module
      if (allParameters.length > 0) {
        this.opexData[opexKey] = allParameters;
      }
    });

    // Auto-select the first checked opex module to show its data
    const firstCheckedIndex = this.opexParameters.findIndex((p) => p.checked);
    if (firstCheckedIndex !== -1) {
      this.selectedOpexIndex = firstCheckedIndex;
    }

    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }

  /**
   * Load category site score ranges from allscores array in selectedmodules
   */
  loadCategoryScoreRanges(selectedModules: any[]) {
    if (!selectedModules || !Array.isArray(selectedModules)) {
      return;
    }


    // Array to store module ranges for dynamicModuleRanges
    const moduleRanges: any[] = [];
    // Array to store module score ranges
    const moduleScoreRangesArray: any[] = [];

    selectedModules.forEach((categoryModule: any, index: number) => {
      const moduleName = categoryModule.module_name;
      const moduleCode = categoryModule.module_code;
      const channel = categoryModule.channel;
      const position = categoryModule.position;

      // Check if module has score ranges
      if (
        categoryModule.allscores &&
        Array.isArray(categoryModule.allscores) &&
        categoryModule.allscores.length > 0
      ) {

        // Get the first score data (usually one per module)
        const scoreData = categoryModule.allscores[0];

        // Parse score ranges (convert strings to numbers)
        const lowMin =
          typeof scoreData.low_range_min === "string"
            ? parseFloat(scoreData.low_range_min)
            : scoreData.low_range_min || 0;
        const lowMax =
          typeof scoreData.low_range_max === "string"
            ? parseFloat(scoreData.low_range_max)
            : scoreData.low_range_max || 0;
        const midMin =
          typeof scoreData.mid_range_min === "string"
            ? parseFloat(scoreData.mid_range_min)
            : scoreData.mid_range_min || 0;
        const midMax =
          typeof scoreData.mid_range_max === "string"
            ? parseFloat(scoreData.mid_range_max)
            : scoreData.mid_range_max || 0;
        const highMin =
          typeof scoreData.high_range_min === "string"
            ? parseFloat(scoreData.high_range_min)
            : scoreData.high_range_min || 0;
        const highMax =
          typeof scoreData.high_range_max === "string"
            ? parseFloat(scoreData.high_range_max)
            : scoreData.high_range_max || 0;


        // Determine the module code for the range
        // For grid modules, use channel+position (e.g., "2C")
        // For special modules, use module_code (e.g., "00", "0LAB", "0CP")
        let rangeCode = moduleCode;
        if (channel !== "0" && position !== "0") {
          rangeCode = `${channel}${position}`;
        }

        // Create module range entry (mid range min/max becomes the slider range)
        const moduleRange = {
          code: rangeCode,
          name: moduleName,
          range: [midMin, midMax] as [number, number],
        };
        moduleRanges.push(moduleRange);

        // Create module score range entry with all three ranges
        const moduleScoreRange = {
          code: rangeCode,
          module_code: rangeCode,
          module_name: moduleName,
          name: moduleName,
          module: moduleName,
          low: [lowMin, lowMax] as [number, number],
          medium: [midMin, midMax] as [number, number],
          high: [highMin, highMax] as [number, number],
          low_range_color: scoreData.low_range_color || "#FF0000",
          mid_range_color: scoreData.mid_range_color || "#FFFF00",
          high_range_color: scoreData.high_range_color || "#00FF00",
        };
        moduleScoreRangesArray.push(moduleScoreRange);
      } else {
        // Module doesn't have score ranges, use default or find from existing ranges
        let rangeCode = moduleCode;
        if (channel !== "0" && position !== "0") {
          rangeCode = `${channel}${position}`;
        }

        // Check if we already have a range for this module in dynamicModuleRanges
        const existingRange = this.dynamicModuleRanges.find(
          (r) => r.code === rangeCode
        );
        if (existingRange) {
          moduleRanges.push(existingRange);
        } else {
          // Use default range
          const defaultRange = {
            code: rangeCode,
            name: moduleName,
            range: [1, 9] as [number, number],
          };
          moduleRanges.push(defaultRange);
        }
      }
    });

    // Update dynamicModuleRanges with loaded ranges
    if (moduleRanges.length > 0) {
      this.dynamicModuleRanges = moduleRanges;
    }

    // Update moduleScoreRanges with loaded score ranges
    if (moduleScoreRangesArray.length > 0) {
      this.moduleScoreRanges = moduleScoreRangesArray;
    } else {
      // If no score ranges found, calculate from dynamicModuleRanges
      this.moduleScoreRanges = this.dynamicModuleRanges.map((mod) => {
        const left = mod.range[0];
        const right = mod.range[1];
        return {
          code: mod.code,
          module_code: mod.code,
          module_name: mod.name,
          name: mod.name,
          module: mod.name,
          low: [this.sliderMin, left],
          medium: [left, right],
          high: [right, this.sliderMax],
        };
      });
    }

  }

  refreshFormData() {
    // Check if form has been modified
    if (this.isFormModified) {
      const confirmRefresh = confirm(
        "You have unsaved changes. Refreshing will lose all your changes. Are you sure you want to continue?"
      );
      if (!confirmRefresh) {
        return;
      }
    }

    // Refresh the site identified by the route (activeSiteId), not whatever
    // happens to be first in session storage.
    const siteId = this.activeSiteId;
    if (siteId) {
      this.prefillFormWithSiteData(siteId);

      // Reset form modification flag
      this.isFormModified = false;

      // Show success message
      this.messageService.add({
        severity: "info",
        summary: "Refreshing",
        detail: "Refreshing form data from server...",
      });
    } else {
      this.messageService.add({
        severity: "warn",
        summary: "Warning",
        detail: "No site information found in session storage.",
      });
    }
  }

  loadGenericLibrary() {
    const apiUrl = `${environment.nodeUrl1}allGenericLibrary`;
    this.http.post(apiUrl, { purpose: "getAll" }).subscribe(
      (res: any) => {
        this.genericLibraryData = res;
        // Set availableModules to the allmodules array from the response
        if (res && Array.isArray(res.allmodules)) {
          this.availableModules = res.allmodules.map((mod: any) => ({
            name: mod.module_name,
            id: mod.module_id,
          }));
        } else {
          this.availableModules = [];
        }
        // Set opexParameters from allopexmodules if present
        // BUT: If we're in category edit mode and already have opexParameters populated, merge/update instead of replacing
        if (res && Array.isArray(res.allopexmodules)) {
          if (
            this.isCategoryMode &&
            this.opexParameters &&
            this.opexParameters.length > 0
          ) {
            // In category edit mode: merge generic library data with existing category data
            // Preserve checked state from category API response
            const existingCheckedKeys = new Set(
              this.opexParameters.filter((p) => p.checked).map((p) => p.key)
            );

            // Create a map of existing parameters by key to avoid duplicates
            const existingParamsMap = new Map();
            this.opexParameters.forEach((param, index) => {
              existingParamsMap.set(param.key, { param, index });
            });

            // Update existing parameters or add new ones from generic library
            res.allopexmodules.forEach((mod: any) => {
              const modKey = mod.module_code || mod.opex_module_code || "";

              if (!modKey) {
                return;
              }

              const existingEntry = existingParamsMap.get(modKey);

              if (existingEntry) {
                // Update existing - preserve checked state and other properties
                this.opexParameters[existingEntry.index] = {
                  ...this.opexParameters[existingEntry.index],
                  id: mod.module_id,
                  label:
                    mod.module_name ||
                    mod.opex_module_name ||
                    this.opexParameters[existingEntry.index].label,
                  // Keep existing checked state
                };
              } else {
                // Add new module from generic library (unchecked) - only if not already exists
                this.opexParameters.push({
                  id: mod.module_id,
                  label: mod.module_name || mod.opex_module_name || "",
                  key: modKey,
                  checked: false,
                });
                // Update the map to prevent duplicates
                existingParamsMap.set(modKey, {
                  param: this.opexParameters[this.opexParameters.length - 1],
                  index: this.opexParameters.length - 1,
                });
              }
            });

            // Remove any duplicates that might have been created (check by key)
            const seenKeys = new Set();
            this.opexParameters = this.opexParameters.filter((param) => {
              if (seenKeys.has(param.key)) {
                return false;
              }
              seenKeys.add(param.key);
              return true;
            });

            // Restore checked state for modules that were checked before
            this.opexParameters.forEach((param) => {
              if (existingCheckedKeys.has(param.key)) {
                param.checked = true;
              }
            });

            // Restore selectedOpexIndex if it was set
            if (
              this.selectedOpexIndex === null ||
              this.selectedOpexIndex >= this.opexParameters.length
            ) {
              const firstChecked = this.opexParameters.findIndex(
                (p) => p.checked
              );
              if (firstChecked >= 0) {
                this.selectedOpexIndex = firstChecked;
              }
            }



            // Also merge opexData for checked modules - add all parameters from generic library
            this.opexParameters
              .filter((p) => p.checked)
              .forEach((param) => {
                const genericModule = res.allopexmodules.find(
                  (mod: any) =>
                    (mod.module_code || mod.opex_module_code) === param.key
                );

                if (
                  genericModule &&
                  genericModule.allopexparameters &&
                  this.opexData[param.key]
                ) {
                  const existingSelectedCodes = new Set(
                    this.opexData[param.key]
                      .filter((p: any) => p.selected)
                      .map((p: any) => p.parameter_code)
                  );

                  // Start with all parameters from generic library
                  const allGenericParams = genericModule.allopexparameters.map(
                    (paramData: any) => ({
                      parameter_id: paramData.parameter_id,
                      parameter_name: paramData.parameter_name,
                      details: paramData.details || "",
                      parameter_unit: paramData.parameter_unit || "",
                      parameter_type:
                        this.getParameterTypeString(paramData.parameter_type) ||
                        "Raw",
                      parameter_code: paramData.parameter_code || "",
                      uploadType:
                        paramData.upload_source === 2 ? "Online" : "Manual",
                      formula: paramData.parameter_formula || "",
                      price_type:
                        this.getPriceTypeString(paramData.price_type) ||
                        "Fixed",
                      price: paramData.price || "",
                      parameter_banchmark:
                        paramData.parameter_banchmark || null,
                      selected: existingSelectedCodes.has(
                        paramData.parameter_code
                      ), // Mark as selected if it was in API
                    })
                  );

                  // Merge with existing API data (preserve API values for selected parameters)
                  const existingDataMap = new Map();
                  this.opexData[param.key].forEach((p: any) => {
                    existingDataMap.set(p.parameter_code, p);
                  });

                  // Update with existing values where available
                  const mergedParams = allGenericParams.map((genParam: any) => {
                    const existing = existingDataMap.get(
                      genParam.parameter_code
                    );
                    if (existing) {
                      // Use existing values but ensure selected status is preserved
                      return {
                        ...existing,
                        selected: existing.selected || genParam.selected,
                      };
                    }
                    return genParam;
                  });

                  // Add any parameters from API that aren't in generic library
                  this.opexData[param.key].forEach((existingParam: any) => {
                    if (
                      !mergedParams.some(
                        (p: any) =>
                          p.parameter_code === existingParam.parameter_code
                      )
                    ) {
                      mergedParams.push(existingParam);
                    }
                  });

                  this.opexData[param.key] = mergedParams;
                }
              });
          } else {
            // Normal mode: replace with generic library data
            this.opexParameters = res.allopexmodules.map((mod: any) => ({
              id: mod.module_id,
              label: mod.module_name,
              key: mod.module_code,
              checked: false,
            }));
          }
        }
      },
      (err) => {
        this.availableModules = [];
      }
    );
  }

  ngAfterViewInit() {
    // Listen to stepper changes
    this.stepper.selectionChange.subscribe((event) => {
      this.updateBreadcrumb(event.selectedIndex);
    });
  }

  updateBreadcrumb(stepIndex: number) {
    // Always show 'Home' and 'Plant Customization' as static
    // The rest are dynamic, starting from index 1 in breadcrumbSteps
    const staticSteps = this.breadcrumbSteps.slice(0, 0);
    const dynamicSteps = this.breadcrumbSteps.slice(0, stepIndex + 1);
    this.items = [...staticSteps, ...dynamicSteps];
  }

  get progressValue(): number {
    if (this.stepper && this.stepper._steps && this.stepper._steps.length) {
      return (
        ((this.stepper.selectedIndex + 1) / this.stepper._steps.length) * 100
      );
    }
    return 0;
  }

  showDialog() {
    this.visible = true;
  }
  onFloatingButtonClick() {
    // Your logic here (e.g., open a dialog, add a module, etc.)
  }

  getPercent(val: number): number {
    return ((val - this.sliderMin) / (this.sliderMax - this.sliderMin)) * 100;
  }

  getSliderGradient(left: number, right: number): string {
    const min = this.sliderMin;
    const max = this.sliderMax;
    const leftPercent = ((left - min) / (max - min)) * 100;
    const rightPercent = ((right - min) / (max - min)) * 100;
    return `linear-gradient(
    to right,
    #e53935 0%,
    #e53935 ${leftPercent}%,
    #fbc02d ${leftPercent}%,
    #fbc02d ${rightPercent}%,
    green ${rightPercent}%,
    green 100%
  )`;
  }

  onLeftChange(idx: number) {
    const mod = this.dynamicModuleRanges[idx];
    if (mod.range[0] > mod.range[1]) {
      mod.range[0] = mod.range[1];
    }
  }

  onRightChange(idx: number) {
    const mod = this.dynamicModuleRanges[idx];
    if (mod.range[1] < mod.range[0]) {
      mod.range[1] = mod.range[0];
    }
  }

  get fabStyle() {
    if (!this.fabPosition) {
      // Default: bottom right
      return { right: "32px", bottom: "32px", left: "auto", top: "auto" };
    }
    // After drag: use absolute position
    return {
      top: this.fabPosition.top + "px",
      left: this.fabPosition.left + "px",
      right: "auto",
      bottom: "auto",
    };
  }

  public startDrag(event: MouseEvent | TouchEvent) {
    if (this.showPopover) {
      // Do not allow FAB drag when popover is open
      return;
    }
    event.preventDefault();
    this.dragMoved = false;
    const fab = (event.target as HTMLElement).closest(
      ".fab-draggable"
    ) as HTMLElement;
    if (fab && !this.fabPosition) {
      const rect = fab.getBoundingClientRect();
      this.fabPosition = { top: rect.top, left: rect.left };
    }
    this.isDraggingFab = true;
    const clientX =
      event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY =
      event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    this.dragStartX = clientX;
    this.dragStartY = clientY;
    this.dragOffset = {
      x: clientX - (this.fabPosition ? this.fabPosition.left : 0),
      y: clientY - (this.fabPosition ? this.fabPosition.top : 0),
    };
    window.addEventListener("mousemove", this.onDragMove);
    window.addEventListener("mouseup", this.stopDrag);
    window.addEventListener("touchmove", this.onDragMove, { passive: false });
    window.addEventListener("touchend", this.stopDrag);
  }

  public onDragMove = (event: MouseEvent | TouchEvent) => {
    if (!this.isDraggingFab || !this.fabPosition) return;
    const clientX =
      event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY =
      event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

    // Clamp to window
    const fab = document.querySelector(".fab-draggable") as HTMLElement;
    const fabWidth = fab ? fab.offsetWidth : 80;
    const fabHeight = fab ? fab.offsetHeight : 48;
    const minLeft = 0;
    const minTop = 0;
    const maxLeft = window.innerWidth - fabWidth;
    const maxTop = window.innerHeight - fabHeight;

    let newLeft = clientX - this.dragOffset.x;
    let newTop = clientY - this.dragOffset.y;
    newLeft = Math.max(minLeft, Math.min(newLeft, maxLeft));
    newTop = Math.max(minTop, Math.min(newTop, maxTop));

    // If moved more than threshold, consider as drag
    if (
      Math.abs(clientX - this.dragStartX) > this.dragThreshold ||
      Math.abs(clientY - this.dragStartY) > this.dragThreshold
    ) {
      this.dragMoved = true;
    }

    this.fabPosition.left = newLeft;
    this.fabPosition.top = newTop;
  };

  public stopDrag = () => {
    this.isDraggingFab = false;
    window.removeEventListener("mousemove", this.onDragMove);
    window.removeEventListener("mouseup", this.stopDrag);
    window.removeEventListener("touchmove", this.onDragMove);
    window.removeEventListener("touchend", this.stopDrag);
  };

  public onFabMouseUp(event: MouseEvent | TouchEvent) {
    if (!this.dragMoved) {
      this.setPopoverPosition();
      this.showPopover = !this.showPopover;
    }
  }

  private setPopoverPosition() {
    // Find the button's position
    const fab = document.querySelector(".fab-draggable") as HTMLElement;
    if (!fab) {
      this.popoverPosition = "above";
      return;
    }
    const rect = fab.getBoundingClientRect();
    const popoverHeight = 350; // match your min-height/max-height in px
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    // If not enough space above, show below; otherwise, show above
    if (spaceAbove < popoverHeight + 20 && spaceBelow > spaceAbove) {
      this.popoverPosition = "below";
    } else {
      this.popoverPosition = "above";
    }
  }

  onSliderChange(idx: number) {
    const min = this.sliderOptions.floor ?? 0;
    const max = this.sliderOptions.ceil ?? 10;
    const left = ((this.moduleRanges[idx][0] - min) / (max - min)) * 100;
    const right = ((this.moduleRanges[idx][1] - min) / (max - min)) * 100;
    const slider = document.querySelectorAll(".ngx-slider")[idx] as HTMLElement;
    if (slider) {
      slider.style.setProperty("--left", `${left}%`);
      slider.style.setProperty("--right", `${right}%`);
    }
  }

  get selectedOpexData() {
    // Flatten all checked parameter data into a single array
    return this.opexParameters
      .filter((p) => p.checked)
      .map((p) => this.opexData[p.key])
      .filter(Boolean)
      .flat();
  }

  logFirstStep() {
    if (this.firstFormGroup.valid) {
      this.isFirstStepCompleted = true;
      this.isFirstStepError = false;
      // ...any other logic
    } else {
      this.isFirstStepCompleted = false;
      this.isFirstStepError = true;
      // Optionally, mark all controls as touched to show validation errors
      this.firstFormGroup.markAllAsTouched();
    }
  }
  logSecondStep() {
    if (this.secondFormGroup.valid) {
      this.isSecondStepCompleted = true;
      this.isSecondStepError = false;
    } else {
      this.isSecondStepCompleted = false;
      this.isSecondStepError = true;
      this.secondFormGroup.markAllAsTouched();
    }
  }
  logThirdStep() {
    // Instead of only checking the form, check if there is at least one group
    if (this.createdGroups && this.createdGroups.length > 0) {
      this.isThirdStepCompleted = true;
      this.isThirdStepError = false;
    } else {
      this.isThirdStepCompleted = false;
      this.isThirdStepError = true;
      // Optionally, set an error on the form control to show validation error in UI
      this.thirdFormGroup.get("thirdCtrl")?.setErrors({ required: true });
    }
    // Log the actual group data
  }
  logFourthStep() {
    this.updateDynamicModuleRanges();

    // Calculate ranges for each configured module (using dynamicModuleRanges instead of hardcoded modules)
    this.moduleScoreRanges = this.dynamicModuleRanges.map((mod) => {
      const left = mod.range[0];
      const right = mod.range[1];
      return {
        code: mod.code,
        module_code: mod.code,
        module_name: mod.name,
        name: mod.name,
        module: mod.name,
        low: [this.sliderMin, left],
        medium: [left, right],
        high: [right, this.sliderMax],
      };
    });
    if (this.fourthFormGroup.valid) {
      this.isFourthStepCompleted = true;
      this.isFourthStepError = false;
    } else {
      this.isFourthStepCompleted = false;
      this.isFourthStepError = true;
      this.fourthFormGroup.markAllAsTouched();
    }
  }

  // ... existing code ...

  updateExistingGridModulesWithIds() {

    for (let row = 0; row < this.gridModules.length; row++) {
      for (let col = 0; col < this.gridModules[row].length; col++) {
        const gridModule = this.gridModules[row][col];
        if (gridModule && gridModule.name && !gridModule.module_id) {
          // Find the module from generic library to get its module_id
          const genericModule = this.genericLibraryData?.allmodules?.find(
            (mod: any) => mod.module_name === gridModule.name
          );

          if (genericModule) {
            // Update the existing grid module with module_id
            gridModule.module_id = genericModule.module_id;
            gridModule.module_code = genericModule.module_code;
            gridModule.module_alias = genericModule.module_alias;


            // Find and update the corresponding moduleConfigs entry
            // Try different key patterns to find the right entry
            const possibleKeys = [
              `${gridModule.name}_${gridModule.module_code || ""}`,
              `${gridModule.name}_${this.getRowLabel(row)}${this.getColumnLabel(
                col
              )}`,
              gridModule.name,
            ];

            let foundKey = null;
            for (const key of possibleKeys) {
              if (this.moduleConfigs[key]) {
                foundKey = key;
                break;
              }
            }

            if (foundKey) {
              this.moduleConfigs[foundKey].module_id = genericModule.module_id;
            }
          }
        }
      }
    }

    // Also update special modules that might be missing module_id
    this.updateSpecialModulesWithIds();

  }

  // Add this new method to handle special modules
  updateSpecialModulesWithIds() {

    const specialModules = [
      { name: "Complete System", code: "00" },
      { name: "Lab Module", code: "0LAB" },
      { name: "Corporate Module", code: "0CP" },
    ];

    specialModules.forEach((specialModule) => {
      const key = `${specialModule.name}_${specialModule.code}`;
      if (this.moduleConfigs[key] && !this.moduleConfigs[key].module_id) {
        // Find the module from generic library
        const genericModule = this.genericLibraryData?.allmodules?.find(
          (mod: any) => mod.module_name === specialModule.name
        );

        if (genericModule) {
          this.moduleConfigs[key].module_id = genericModule.module_id;
        }
      }
    });

  }

  /**
   * Resolve the site_id for the outbound save payload.
   * Site mode returns the raw id. Sub-category (category) mode namespaces the id
   * with a "101" prefix to match the convention used by the report sub-category
   * flow (getPayloadSiteId in dashboard-report) — e.g. site 1 -> 1011.
   */
  private getPayloadSiteId(
    siteId: string | number | null
  ): string | number | null {
    if (!this.isCategoryMode) {
      return siteId;
    }
    const rawSiteId = String(siteId ?? "").trim();
    if (!rawSiteId) {
      return siteId;
    }
    const prefixedSiteId = `101${rawSiteId}`;
    const numericSiteId = Number(prefixedSiteId);
    return Number.isNaN(numericSiteId) ? prefixedSiteId : numericSiteId;
  }

  onSave() {
    // API call to update site edit status - called first when save button is clicked.
    // Save to the site identified by the route (activeSiteId) so we never write
    // back to the wrong site (previously this read sessionStorage['sites-info'][0]).
    let siteId = this.activeSiteId;
    if (!siteId) {
      const siteInfoStr = sessionStorage.getItem("sites-info");
      if (siteInfoStr) {
        try {
          const siteInfoArr = JSON.parse(siteInfoStr);
          if (
            Array.isArray(siteInfoArr) &&
            siteInfoArr.length > 0 &&
            siteInfoArr[0].site_id
          ) {
            siteId = siteInfoArr[0].site_id;
          }
        } catch (e) {
        }
      }
    }

    // Get user_id from authenticated session
    let userId = null;
    const userInfo = sessionStorage.getItem("user-info");
    if (userInfo) {
      try {
        const userData = JSON.parse(userInfo);
        userId = userData.id || userData.user_id || null;
      } catch (e) {
      }
    }

    // Make API call to update site edit status
    if (siteId && userId) {
      const payload = {
        site_id: siteId,
        user_id: userId,
        site_edit_status: true,
      };

      this.http
        .post(`${environment.nodeUrl1}site/updateEditStatus`, payload)
        .toPromise()
        .then((response: any) => {
        })
        .catch((error) => {
        });
    }

    // Open saving dialog
    const savingDialogRef = this.dialog.open(SiteSavingDialogComponent, {
      width: "500px",
      panelClass: "custom-dialog-radius",
      data: {
        message: "Site configuration is saving. Please wait...",
      },
      disableClose: true, // Prevent closing by clicking outside
    });

    // Keep dialog reference on the component for use in callbacks
    this.savingDialogRef = savingDialogRef;

    // Set isSiteSettingSaving flag to true
    this.notificationsService.setIsSiteSettingSaving(true);

    // Define save steps for progress tracking
    const saveSteps = [
      { name: 'Validating Configuration', status: 'pending' as const, progress: 0 },
      { name: 'Preparing Basic Details', status: 'pending' as const, progress: 0 },
      { name: 'Preparing Module Configuration', status: 'pending' as const, progress: 0 },
      { name: 'Preparing Groups', status: 'pending' as const, progress: 0 },
      { name: 'Preparing Site Score', status: 'pending' as const, progress: 0 },
      { name: 'Preparing Opex Settings', status: 'pending' as const, progress: 0 },
      { name: 'Sending to Backend', status: 'pending' as const, progress: 0 },
      { name: 'Backend Processing', status: 'pending' as const, progress: 0 },
    ];

    // Generate unique notification ID for tracking
    const saveNotificationId = `site-save-${Date.now()}`;
    this.currentSaveNotificationId = saveNotificationId;

    // Add notification to notification window with steps
    this.notificationsService.addNotification({
      id: saveNotificationId,
      title: "Site Configuration Saving",
      message: "Preparing site configuration...",
      type: "system",
      severity: "site setting",
      timestamp: new Date(),
      read: false,
      isProcessing: true,
      progress: 0,
      metadata: {
        isSiteSettingSaving: true,
        steps: saveSteps,
        overallProgress: 0,
      },
    } as any);

    // Helper function to update step status
    const updateStepStatus = (stepIndex: number, status: 'pending' | 'processing' | 'completed' | 'failed', progress?: number) => {
      this.notificationsService.updateNotificationStepStatus(saveNotificationId, stepIndex, status, progress);
    };

    // Step 1: Validating Configuration
    updateStepStatus(0, 'processing');

    // Polling will be started after we receive a jobId from the siteCreate response

    // Trigger local topic manager save
    this.shouldSaveTopics = true;

    // Reset the trigger after a short delay
    setTimeout(() => {
      this.shouldSaveTopics = false;
    }, 100);

    // Validate all steps before saving
    this.logFirstStep();
    this.logSecondStep();
    this.logThirdStep();
    this.logFourthStep();

    // Step 1 Complete: Validation done
    updateStepStatus(0, 'completed', 100);

    // Step 2: Preparing Basic Details
    updateStepStatus(1, 'processing');

    // Get category_id from session storage or query params (for category mode)
    let categoryId = null;
    if (this.isCategoryMode) {
      // Check if isNew - if new, category_id should be null
      if (!this.isNew) {
        // For editing existing category, try to get category_id from session storage
        const categoryInfoStr = sessionStorage.getItem("category-info");
        if (categoryInfoStr) {
          try {
            const categoryInfo = JSON.parse(categoryInfoStr);
            // Handle both array and object formats
            if (Array.isArray(categoryInfo) && categoryInfo.length > 0) {
              categoryId = categoryInfo[0].category_id || categoryInfo[0].id;
            } else if (categoryInfo.category_id || categoryInfo.id) {
              categoryId = categoryInfo.category_id || categoryInfo.id;
            }
          } catch (e) {
          }
        }

        // Also check query params for category_id
        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (!categoryId && queryParams["category_id"]) {
          categoryId = queryParams["category_id"];
        }
      }
      // If isNew is true, categoryId remains null
    }

    // Transform plantViewCtrl to isgroup
    const basicDetails = { ...this.firstFormGroup.value };

    // Transform plantViewCtrl to isgroup
    if (basicDetails.plantViewCtrl === "groupsAndSubgroups") {
      basicDetails.isgroup = 1;
    } else {
      basicDetails.isgroup = 0; // Default or any other value
    }
    // Remove the original plantViewCtrl field
    delete basicDetails.plantViewCtrl;

    // Transform globalDataCleansingCtrl to iszero
    if (basicDetails.globalDataCleansingCtrl === "dataWithNoValue") {
      basicDetails.iszero = 2;
    } else if (basicDetails.globalDataCleansingCtrl === "dataWithNoDate") {
      basicDetails.iszero = 1;
    } else {
      basicDetails.iszero = 0; // Other values or not selected
    }
    // Remove the original globalDataCleansingCtrl field
    delete basicDetails.globalDataCleansingCtrl;

    // Add goption field (same logic as edit-site component)
    // If iszero == 0, then goption = 0, otherwise goption = iszero
    basicDetails.goption = basicDetails.iszero === 0 ? 0 : basicDetails.iszero;

    // Transform currencyCtrl to currency
    basicDetails.currency = basicDetails.currencyCtrl || "";
    delete basicDetails.currencyCtrl;

    // For category mode, map categoryCtrl to category_master_id
    if (this.isCategoryMode) {
      basicDetails.category_master_id = basicDetails.categoryCtrl || "";
      delete basicDetails.categoryCtrl;
    }

    // Step 2 Complete: Basic Details prepared
    updateStepStatus(1, 'completed', 100);

    // Step 3: Preparing Module Configuration
    updateStepStatus(2, 'processing');

    this.updateExistingGridModulesWithIds();

    const modulesWithoutId = Object.keys(this.moduleConfigs).filter((key) => {
      const config = this.moduleConfigs[key];
      return !config.module_id && config.module_id !== 0; // Allow 0 as valid ID
    });

    if (modulesWithoutId.length > 0) {

      // Mark step 3 as failed
      updateStepStatus(2, 'failed');

      // Close saving dialog and stop polling
      try {
        savingDialogRef.close();
      } catch (e) {}
      try {
        if (this.savingDialogRef) {
          this.savingDialogRef.close();
        }
      } catch (e) {}
      this.stopSaveStatusPolling();
      this.savingDialogRef = null;

      // Set isSiteSettingSaving flag to false
      this.notificationsService.setIsSiteSettingSaving(false);

      this.messageService.add({
        severity: "error",
        summary: "Validation Error",
        detail: `The following modules are missing module_id: ${modulesWithoutId.join(
          ", "
        )}. Please refresh the page and try again.`,
      });
      return; // Don't proceed with save
    }

    // First, collect ALL modules from the grid (both configured and unconfigured)
    const allGridModules: any[] = [];
    for (let row = 0; row < this.gridModules.length; row++) {
      for (let col = 0; col < this.gridModules[row].length; col++) {
        const gridMod = this.gridModules[row][col];
        if (gridMod) {
          allGridModules.push({
            ...gridMod,
            channel: this.getRowLabel(row),
            position: this.getColumnLabel(col),
            row,
            col,
          });
        }
      }
    }

    // Also include special modules that might not be in the grid
    const specialModules = [
      { name: "Complete System", code: "00", channel: "0", position: "0" },
      { name: "Lab Module", code: "0LAB", channel: "0", position: "LAB" },
      { name: "Corporate Module", code: "0CP", channel: "0", position: "CP" },
    ];

    // Always add special modules (whether configured or not)
    specialModules.forEach((specialMod) => {
      const key = `${specialMod.name}_${specialMod.code}`;

      if (this.moduleConfigs[key]) {
        // Module has been configured, use the configured data
        allGridModules.push({
          ...this.moduleConfigs[key],
          channel: specialMod.channel,
          position: specialMod.position,
        });
      } else {
        // Module hasn't been configured yet, but still include it
        // Find the module from generic library to get basic module data
        const genericModule = this.genericLibraryData?.allmodules?.find(
          (mod: any) => mod.module_name === specialMod.name
        );

        if (genericModule) {
          allGridModules.push({
            module_name: specialMod.name,
            module_id: genericModule.module_id,
            module_code: specialMod.code,
            module_alias: specialMod.name,
            module_type: genericModule.module_type,
            channel: specialMod.channel,
            position: specialMod.position,
            allparameters: [], // No parameters configured yet
            selectedProductCodes: [], // No parameters selected yet
            isSpecialModule: true, // Flag to identify special modules
          });

        }
      }
    });

    // Step 3 Complete: Module Configuration prepared
    updateStepStatus(2, 'completed', 100);

    // Step 4: Preparing Groups
    updateStepStatus(3, 'processing');

    // Initialize the filtered module configs object
    const filteredModuleConfigs: any = {};

    // Add grid modules (both configured and unconfigured)
    allGridModules.forEach((gridMod, idx) => {
      const moduleName = gridMod.name || gridMod.module_name;

      // For special modules, use their specific key format
      let key;
      if (gridMod.isSpecialModule) {
        key = `${moduleName}_${gridMod.module_code}`;
      } else {
        key = `${moduleName}_${gridMod.channel}${gridMod.position}`;
      }

      // Check if this module has been configured (has parameters)
      const configuredModule = this.moduleConfigs[key];

      if (configuredModule) {
        // Module has been configured, use the configured data with grid position
        const selectedCodes = Array.isArray(
          configuredModule.selectedProductCodes
        )
          ? configuredModule.selectedProductCodes
          : [];

        filteredModuleConfigs[key] = {
          ...configuredModule,
          channel: gridMod.channel,
          position: gridMod.position,
          allparameters: Array.isArray(configuredModule.allparameters)
            ? configuredModule.allparameters.filter((param: any) =>
                selectedCodes.includes(param.code)
              )
            : [],
          row: gridMod.row,
          col: gridMod.col,
          isSpecialModule: gridMod.isSpecialModule || false,
        };

        // Remove the 'parameters' property if it exists
        if ("parameters" in filteredModuleConfigs[key]) {
          delete filteredModuleConfigs[key].parameters;
        }

      } else {
        // Module hasn't been configured yet, add basic module data
        const genericModule = this.genericLibraryData?.allmodules?.find(
          (mod: any) => mod.module_name === moduleName
        );

        if (genericModule) {
          filteredModuleConfigs[key] = {
            module_name: moduleName,
            module_id: gridMod.module_id || genericModule.module_id,
            module_code: gridMod.module_code || genericModule.module_code,
            module_alias: gridMod.module_alias || genericModule.module_alias,
            module_type: gridMod.module_type || genericModule.module_type,
            channel: gridMod.channel,
            position: gridMod.position,
            allparameters: [], // No parameters configured yet
            selectedProductCodes: [], // No parameters selected yet
            row: gridMod.row,
            col: gridMod.col,
            isSpecialModule: gridMod.isSpecialModule || false,
          };

        }
      }
    });
    // Ensure special modules with configured parameters are always included
    // This ensures special modules that have been configured are always in the payload
    const configuredSpecialModules = [
      { name: "Complete System", code: "00", channel: "0", position: "0" },
      { name: "Lab Module", code: "0LAB", channel: "0", position: "LAB" },
      { name: "Corporate Module", code: "0CP", channel: "0", position: "CP" },
    ];

    configuredSpecialModules.forEach((specialMod) => {
      const key = `${specialMod.name}_${specialMod.code}`;
      const configuredModule = this.moduleConfigs[key];

      // Only add if it has configured parameters (allparameters exists and has items)
      if (
        configuredModule &&
        configuredModule.allparameters &&
        Array.isArray(configuredModule.allparameters) &&
        configuredModule.allparameters.length > 0
      ) {
        // Check if it's already in filteredModuleConfigs
        if (!filteredModuleConfigs[key]) {
          // Add it to filteredModuleConfigs if it has configured parameters
          const selectedCodes = Array.isArray(
            configuredModule.selectedProductCodes
          )
            ? configuredModule.selectedProductCodes
            : [];

          filteredModuleConfigs[key] = {
            ...configuredModule,
            channel: specialMod.channel,
            position: specialMod.position,
            allparameters: configuredModule.allparameters.filter(
              (param: any) =>
                selectedCodes.includes(param.code) ||
                param.selected ||
                param.checked
            ),
            isSpecialModule: true,
          };

          // Remove the 'parameters' property if it exists
          if ("parameters" in filteredModuleConfigs[key]) {
            delete filteredModuleConfigs[key].parameters;
          }

        } else {
          // Ensure it has all the necessary properties
          filteredModuleConfigs[key].isSpecialModule = true;
          filteredModuleConfigs[key].channel = specialMod.channel;
          filteredModuleConfigs[key].position = specialMod.position;
        }
      }
    });

    // Lift each module's buffered Target / Indexes / Behaviour config to the top of its
    // entry. Done in one pass over the finished object rather than in each branch that
    // builds one, so a module cannot be missed depending on how it got there.
    //
    // Only sub-category creation produces these: the tabs save themselves directly once
    // the record has an id. The backend writes them into subCategory_moduleTarget,
    // subCategory_moduleIndexes and subCategory_behaviourFinetuning in the same
    // transaction that creates the sub-category, so the record and its config never
    // exist without each other.
    Object.keys(filteredModuleConfigs).forEach((moduleKey) => {
      const entry = filteredModuleConfigs[moduleKey];
      const draft = entry?.configDraft;
      delete entry.configDraft;

      if (!draft) {
        return;
      }
      entry.moduleTarget = draft.moduleTarget ?? null;
      entry.moduleIndexes = draft.moduleIndexes ?? null;
      entry.behaviourFinetuning = draft.behaviourFinetuning ?? null;
    });

    // Build the new opex.selectedParameters structure
    const selectedParameters = this.opexParameters
      .filter((param) => param.checked)
      .map((param) => {
        // First check if we have user-configured data in opexData
        if (this.opexData[param.key] && this.opexData[param.key].length > 0) {
          // Use user-configured data from opexData
          const userConfiguredRows = this.opexData[param.key].filter(
            (row: any) => row.selected
          );

          return {
            key: param.key,
            label: param.label,
            module_code: param.key,
            selected: true,
            parameters: userConfiguredRows,
          };
        } else {
          // Fallback to generic library data if no user configuration
          const opexModule = this.genericLibraryData.allopexmodules.find(
            (mod: any) => mod.module_id === param.id
          );
          const selectedRows = (opexModule?.allopexparameters || []).filter(
            (row: any) => row.selected
          );

          return {
            key: param.key,
            label: param.label,
            module_code: param.key,
            selected: true,
            parameters: selectedRows,
          };
        }
      });

    // Step 4 Complete: Groups prepared
    updateStepStatus(3, 'completed', 100);

    // Step 5: Preparing Site Score
    updateStepStatus(4, 'processing');

    // Prepare payload
    const payload: any = {
      // Site mode: send the raw site_id. Sub-category (category) mode: namespace
      // the site_id with the "101" prefix, mirroring the report sub-category
      // convention (getPayloadSiteId in dashboard-report) so the backend receives
      // e.g. 1011 instead of the stale raw 1.
      site_id: this.getPayloadSiteId(siteId),

      // Include category_id for category mode (null for new categories, ID for editing)
      ...(this.isCategoryMode && { category_id: categoryId }),

      // Step 1: Basic Details (with transformed dropdown values)
      basicDetails: basicDetails,

      // Step 2: Module configuration
      moduleConfig: {
        channels: this.secondFormGroup.get("channels")?.value,
        positions: this.secondFormGroup.get("positions")?.value,
        gridModules: this.gridModules, // 2D array of modules placed in grid
        moduleConfigs: filteredModuleConfigs, // All module configurations (grid + special)
      },

      // Step 3: Groups (if used) - Enhanced to include subgroup details and second step modules
      groups: this.createdGroups.map((group) => {

        const mappedGroup: any = {
          groupName: group.groupName,
          groupPosition: group.groupPosition,
          createSubGroups: group.createSubGroups,
          numberOfSubGroups: group.numberOfSubGroups,
        };

        if (
          group.createSubGroups &&
          group.subGroups &&
          Array.isArray(group.subGroups)
        ) {
          // Include subgroup details with name, position, weight, and modules
          mappedGroup.subGroups = group.subGroups.map((subGroup) => {

            // Enrich modules from subgroup with full configuration from filteredModuleConfigs
            const enrichedModules = (subGroup.modules || []).map(
              (groupModule: any) => {
                // Try to find the full module configuration by module_id, module_code, or code
                const moduleId = groupModule.module_id;
                const moduleCode = groupModule.module_code || groupModule.code;
                const moduleName = groupModule.name || groupModule.module_name;

                // Find the full module config by matching module_id (most reliable), then module_code/code, then name
                const fullConfig = Object.values(filteredModuleConfigs).find(
                  (module: any) => {
                    // Priority 1: Match by module_id (most reliable)
                    if (
                      moduleId &&
                      module.module_id &&
                      module.module_id === moduleId
                    ) {
                      return true;
                    }
                    // Priority 2: Match by module_code or code
                    if (
                      moduleCode &&
                      (module.module_code === moduleCode ||
                        module.code === moduleCode)
                    ) {
                      return true;
                    }
                    // Priority 3: Match by name and code (fallback)
                    if (
                      moduleName &&
                      module.module_name === moduleName &&
                      moduleCode &&
                      (module.module_code === moduleCode ||
                        module.code === moduleCode)
                    ) {
                      return true;
                    }
                    return false;
                  }
                );

                if (fullConfig) {
                  // Merge group module with full configuration
                  const fullConfigTyped = fullConfig as any;
                  const groupModuleTyped = groupModule as any;
                  return {
                    ...fullConfigTyped,
                    // Preserve group-specific properties if they exist
                    weight:
                      groupModuleTyped.weight !== undefined
                        ? groupModuleTyped.weight
                        : fullConfigTyped.weight,
                    selected:
                      groupModuleTyped.selected !== undefined
                        ? groupModuleTyped.selected
                        : true,
                    // Preserve module_id if it exists in groupModule
                    module_id:
                      groupModuleTyped.module_id || fullConfigTyped.module_id,
                    // Preserve position from group module
                    position:
                      groupModuleTyped.position !== undefined && groupModuleTyped.position !== ""
                        ? groupModuleTyped.position
                        : fullConfigTyped.position,
                    // Set porder from group module's position (convert to number)
                    porder:
                      groupModuleTyped.position !== undefined && groupModuleTyped.position !== ""
                        ? Number(groupModuleTyped.position) || 0
                        : fullConfigTyped.porder || 0,
                  };
                }

                // If no full config found, return the group module with porder set from position
                const groupModuleTyped = groupModule as any;
                return {
                  ...groupModule,
                  porder:
                    groupModuleTyped.position !== undefined && groupModuleTyped.position !== ""
                      ? Number(groupModuleTyped.position) || 0
                      : groupModuleTyped.porder || 0,
                };
              }
            );


            return {
              name: subGroup.name,
              position: subGroup.position,
              weight: subGroup.weight,
              modules: enrichedModules,
            };
          });
        } else if (!group.createSubGroups && group.modules) {
          // Enrich modules directly if no subgroups (for backward compatibility)
          const enrichedModules = (group.modules || []).map(
            (groupModule: any) => {
              // Try to find the full module configuration by module_id, module_code, or code
              const moduleId = groupModule.module_id;
              const moduleCode = groupModule.module_code || groupModule.code;
              const moduleName = groupModule.name || groupModule.module_name;

              // Find the full module config by matching module_id (most reliable), then module_code/code, then name
              const fullConfig = Object.values(filteredModuleConfigs).find(
                (module: any) => {
                  // Priority 1: Match by module_id (most reliable)
                  if (
                    moduleId &&
                    module.module_id &&
                    module.module_id === moduleId
                  ) {
                    return true;
                  }
                  // Priority 2: Match by module_code or code
                  if (
                    moduleCode &&
                    (module.module_code === moduleCode ||
                      module.code === moduleCode)
                  ) {
                    return true;
                  }
                  // Priority 3: Match by name and code (fallback)
                  if (
                    moduleName &&
                    module.module_name === moduleName &&
                    moduleCode &&
                    (module.module_code === moduleCode ||
                      module.code === moduleCode)
                  ) {
                    return true;
                  }
                  return false;
                }
              );

              if (fullConfig) {
                // Merge group module with full configuration
                const fullConfigTyped = fullConfig as any;
                const groupModuleTyped = groupModule as any;
                return {
                  ...fullConfigTyped,
                  // Preserve group-specific properties if they exist
                  weight:
                    groupModuleTyped.weight !== undefined
                      ? groupModuleTyped.weight
                      : fullConfigTyped.weight,
                  selected:
                    groupModuleTyped.selected !== undefined
                      ? groupModuleTyped.selected
                      : true,
                  // Preserve module_id if it exists in groupModule
                  module_id:
                    groupModuleTyped.module_id || fullConfigTyped.module_id,
                  // Preserve position from group module
                  position:
                    groupModuleTyped.position !== undefined && groupModuleTyped.position !== ""
                      ? groupModuleTyped.position
                      : fullConfigTyped.position,
                  // Set porder from group module's position (convert to number)
                  porder:
                    groupModuleTyped.position !== undefined && groupModuleTyped.position !== ""
                      ? Number(groupModuleTyped.position) || 0
                      : fullConfigTyped.porder || 0,
                };
              }

              // If no full config found, return the group module with porder set from position
              const groupModuleTyped = groupModule as any;
              return {
                ...groupModule,
                porder:
                  groupModuleTyped.position !== undefined && groupModuleTyped.position !== ""
                    ? Number(groupModuleTyped.position) || 0
                    : groupModuleTyped.porder || 0,
              };
            }
          );


          mappedGroup.modules = enrichedModules;
        } else {
          // Fallback: include all modules from second step
          const allSecondStepModules = Object.values(filteredModuleConfigs);

          mappedGroup.modules = allSecondStepModules;
        }

        return mappedGroup;
      }),

      // Step 4: Site Score
      // Use dynamicModuleRanges which includes all configured modules (grid + special)
      // Transform to include module information (code, name) for backend
      siteScore: {
        moduleRanges: this.dynamicModuleRanges.map((mod) => ({
          code: mod.code,
          module_code: mod.code,
          module_name: mod.name,
          name: mod.name,
          range: mod.range,
        })),
        moduleScoreRanges: this.moduleScoreRanges, // calculated ranges with module info
      },

      // Opex
      opex: {
        selectedParameters,
      },
    };

    // Step 5 Complete: Site Score prepared
    updateStepStatus(4, 'completed', 100);

    // Step 6 Complete: Opex Settings prepared
    updateStepStatus(5, 'completed', 100);

    // Optionally, validate payload here

    // Log the payload before sending

    // Step 7: Sending to Backend
    updateStepStatus(6, 'processing');

    // Send to backend
    const apiUrl = `${environment.nodeUrl1}siteCreate`;

    this.http.post(apiUrl, payload).subscribe(
      (res: any) => {

        // Step 7 Complete: Sent to Backend
        updateStepStatus(6, 'completed', 100);

        // If backend started an async job, use returned jobId/statusUrl to poll for completion
        const jobId = res?.jobId || res?.job_id || res?.taskId || res?.task_id;
        const statusEndpoint =
          res?.statusUrl ||
          res?.statusURL ||
          res?.status_url ||
          res?.statusPath;
        if (jobId) {

          // Step 8: Backend Processing started
          updateStepStatus(7, 'processing');

          // Keep the saving dialog open and the isSiteSettingSaving flag true (already set)
          // Note: We no longer add a separate "Site Save Started" notification here
          // since the main notification with steps already tracks the progress

          // Start polling using the jobId and optional statusEndpoint
          try {
            this.startSaveStatusPolling(jobId, siteId, statusEndpoint);
          } catch (e) {
            // Fall back to immediate success handling below
          }

          // Do not close dialog here; polling will close it on completion
          return;
        }

        // No jobId returned -> treat as immediate success
        // Step 8 Complete: Backend Processing done (immediate success)
        updateStepStatus(7, 'completed', 100);

        // Close saving dialog and stop polling
        try {
          savingDialogRef.close();
        } catch (e) {}
        try {
          if (this.savingDialogRef) {
            this.savingDialogRef.close();
          }
        } catch (e) {}
        this.stopSaveStatusPolling();
        this.savingDialogRef = null;

        // Set isSiteSettingSaving flag to false
        this.notificationsService.setIsSiteSettingSaving(false);

        // handle success (show toast, redirect, etc.)

        // Check if this is a new category creation - show special dialog
        if (
          this.isCategoryMode &&
          this.isNew &&
          res &&
          res.status === 1 &&
          res.data &&
          res.data.message
        ) {

          // Open success dialog for new category
          this.openCategorySuccessDialog(
            res.data.message,
            res.data.category_id
          );
        } else {
          // Normal success handling for site mode or category edit mode
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Site has been successfully created",
          });
        }

        // Extract site_id from response and create table (for site mode)
        if (!this.isCategoryMode && res && res.site_id) {
          this.createSiteTable(res.site_id);
        }

        // Set configuration flags to indicate successful save
        this.hasSavedConfiguration = true;
        this.isViewMode = true;

        this.clearFormDataFromSession(); // Clear session storage on successful save
      },
      (err) => {
        // Mark step 7 as failed (sending to backend failed)
        updateStepStatus(6, 'failed');

        // Close saving dialog and stop polling
        try {
          savingDialogRef.close();
        } catch (e) {}
        try {
          if (this.savingDialogRef) {
            this.savingDialogRef.close();
          }
        } catch (e) {}
        this.stopSaveStatusPolling();
        this.savingDialogRef = null;

        // Set isSiteSettingSaving flag to false
        this.notificationsService.setIsSiteSettingSaving(false);

        // handle error (show error message)
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to create site. Please try again.",
        });
      }
    );
  }

  /**
   * Open success dialog after creating a new category
   */
  openCategorySuccessDialog(message: string, categoryId: number | string) {
    const dialogRef = this.dialog.open(CategorySuccessDialogComponent, {
      width: "500px",
      panelClass: "custom-dialog-radius",
      data: {
        message: message,
        categoryId: categoryId,
      },
      disableClose: true, // Prevent closing by clicking outside
    });

    dialogRef.afterClosed().subscribe((result) => {

      if (result === "finish") {
        // Handle "Finish Setup" button click
        // Navigate to category list or home page
        this.router.navigate(["/category"]);
      } else if (result === "reports") {
        // Handle "Setup Reports" button click
        // Navigate to add-report component with category information
        this.router.navigate(["/reportlibrary/addreport"], {
          queryParams: {
            fromCategory: "true",
            categoryId: categoryId,
          },
        });
      }
    });
  }

  getModuleScoreRanges() {
    // Returns an array of objects: { moduleName, low: [start, end], medium: [start, end], high: [start, end] }
    return this.modules.map((module, idx) => {
      const left = this.moduleRanges[idx][0];
      const right = this.moduleRanges[idx][1];
      return {
        module: module.name,
        low: [this.sliderMin, left],
        medium: [left, right],
        high: [right, this.sliderMax],
      };
    });
  }

  onModuleDragStart(event: DragEvent, moduleName: string) {
    event.dataTransfer?.setData("text/plain", moduleName);
  }

  onGridDragOver(event: DragEvent, row?: number, col?: number) {
    event.preventDefault(); // Required for drop event to fire
    
    // Check if cell already has a module configured - prevent drag over visual effect only
    if (row !== undefined && col !== undefined && this.gridModules[row]?.[col]) {
      (event.currentTarget as HTMLElement).classList.remove("drag-over");
      return; // Don't show drag-over visual on occupied cells
    }
    (event.currentTarget as HTMLElement).classList.add("drag-over");
  }

  onGridDragLeave(event: DragEvent) {
    (event.currentTarget as HTMLElement).classList.remove("drag-over");
  }

  onGridDrop(event: DragEvent, row: number, col: number) {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drag-over");
    
    // Check if cell already has a module configured
    if (this.gridModules[row]?.[col]) {
      this.messageService.add({
        severity: "warn",
        summary: "Cell Already Occupied",
        detail: `This grid cell already has a module configured. Please delete the existing module "${this.gridModules[row][col].name}" before adding a new one.`,
        life: 5000
      });
      return;
    }

    const moduleName = event.dataTransfer?.getData("text/plain");
    if (moduleName) {
      // Find the module from generic library to get its module_id
      const genericModule = this.genericLibraryData?.allmodules?.find(
        (mod: any) => mod.module_name === moduleName
      );

      if (genericModule) {
        // Create module object with module_id from generic library
        this.gridModules[row][col] = {
          name: moduleName,
          module_id: genericModule.module_id, // Add module_id here
          module_code: genericModule.module_code,
          module_alias: genericModule.module_alias,
        };

      } else {
        // Fallback if module not found in generic library
        this.gridModules[row][col] = { name: moduleName };
      }

      this.updateDynamicModuleRanges();

      // Update validators after adding module
      this.updateChannelPositionValidators();
    }
  }

  deleteModuleFromGrid(row: number, col: number, event: Event) {
    event.stopPropagation(); // Prevent event bubbling

    // Get the module name before deletion for logging
    const moduleName = this.gridModules[row][col]?.name;

    // Remove the module from the grid
    this.gridModules[row][col] = null;

    // Clear validation error for this module position
    const errorKey = `${row}_${col}`;
    this.modulesWithValidationErrors.delete(errorKey);

    // Grid code of the cell being deleted, e.g. "1E" - the same code that
    // openModuleConfigDialog(), validateModulesHaveParameters() and onSave() build.
    const gridCode = `${this.getRowLabel(row)}${this.getColumnLabel(col)}`;

    // Clean up the moduleConfigs entry for THIS cell only.
    //
    // moduleConfigs is keyed `${module_name}_${channel}${position}` - the grid code is
    // what makes the key unique, because a site runs the same module type in many cells
    // (NEPL has several activated-sludge units, all sharing one module_name and telling
    // themselves apart only by their alias). A prefix match on `module_name + "_"` was
    // therefore wiping the configuration of every other unit of the same type, which
    // then failed validateModulesHaveParameters() and blocked Next with
    // "All modules must have at least one parameter configured".
    if (moduleName) {
      delete this.moduleConfigs[`${moduleName}_${gridCode}`];
    }

    // Update dynamic module ranges after deletion
    this.updateDynamicModuleRanges();

    // Update validators after deleting module
    this.updateChannelPositionValidators();

    // Log the deletion for debugging

    // Optional: Show a confirmation message
    // You can implement a toast notification here if needed
  }

  /**
   * Creates a table for the newly created site
   * @param siteId The ID of the newly created site
   */
  createSiteTable(siteId: string | number) {
    const createTablePayload = {
      site_id: siteId,
      purpose: "create a table with siteId",
    };

    // You can adjust the API endpoint as needed
    const createTableUrl = `${environment.nodeUrl1}createSiteTable`;

    this.http.post(createTableUrl, createTablePayload).subscribe(
      (tableRes) => {
        // Table creation runs silently without user notification
      },
      (tableError) => {
        // Table creation errors are logged but not shown to user
      }
    );
  }

  fetchCompleteSiteConfiguration(siteId: number | string) {

    // Set loading state
    this.isLoadingConfiguration = true;

    // Get complete site configuration including modules, parameters, groups, etc.
    const configPayload = {
      site_id: siteId,
      purpose: "getCompleteConfiguration",
    };

    const configApiUrl = `${environment.nodeUrl1}getSiteCompleteConfiguration`;

    // Add authentication headers if token exists
    const sessionToken = sessionStorage.getItem("auth_token");
    const headers: any = { "Content-Type": "application/json" };
    if (sessionToken) {
      headers["Authorization"] = `Bearer ${sessionToken}`;
    }

    this.http
      .post(configApiUrl, configPayload, { headers })
      .subscribe(
        (response: any) => {

          if (response && response.status === 1 && response.data) {
            const configData = response.data;

            // Load all configuration data
            this.loadCompleteConfiguration(configData);

            // Set configuration flags
            this.hasSavedConfiguration = true;
            this.isViewMode = true;


            // Show success message
            this.messageService.add({
              severity: "success",
              summary: "Configuration Loaded",
              detail: "Complete site configuration loaded successfully!",
            });

          } else if (response && response.success && response.data) {
            // Alternative response structure
            const configData = response.data;

            this.loadCompleteConfiguration(configData);
            this.hasSavedConfiguration = true;
            this.isViewMode = true;


            this.messageService.add({
              severity: "success",
              summary: "Configuration Loaded",
              detail: "Complete site configuration loaded successfully!",
            });

          } else {
            this.hasSavedConfiguration = false;
            this.isViewMode = false;


            // Show info message that no saved configuration exists
            this.messageService.add({
              severity: "info",
              summary: "No Saved Configuration",
              detail:
                "No previous configuration found. You can start fresh or create a new configuration.",
            });
          }
        },
        (error) => {
          this.hasSavedConfiguration = false;
          this.isViewMode = false;


          // Show error message
          this.messageService.add({
            severity: "error",
            summary: "Configuration Error",
            detail:
              "Failed to load saved configuration. You can continue with a fresh form.",
          });
        }
      )
      .add(() => {
        // Always clear loading state when request completes
        this.isLoadingConfiguration = false;
      });
  }

  loadCompleteConfiguration(configData: any) {

    // Store the raw configuration data for later use
    this.rawConfigurationData = configData;

    // Load first step: Basic Details
    if (configData.basicDetails) {
      const plantViewValue = this.getPlantViewValue(configData.basicDetails);
      const globalDataCleansingValue = this.getGlobalDataCleansingValue(
        configData.basicDetails
      );

      this.firstFormGroup.patchValue({
        siteNameCtrl:
          configData.basicDetails.site_name ||
          configData.basicDetails.siteName ||
          "",
        siteAddressCtrl:
          configData.basicDetails.site_address ||
          configData.basicDetails.siteAddress ||
          "",
        contactNumberCtrl:
          configData.basicDetails.site_phone ||
          configData.basicDetails.contactNumber ||
          "",
        siteDescriptionCtrl:
          configData.basicDetails.site_description ||
          configData.basicDetails.siteDescription ||
          "",
        plantViewCtrl: plantViewValue,
        globalDataCleansingCtrl: globalDataCleansingValue,
        assistantIdCtrl:
          configData.basicDetails.assistantid ||
          configData.basicDetails.assistantId ||
          "",
        apiKeyCtrl:
          configData.basicDetails.apikey ||
          configData.basicDetails.apiKey ||
          "",
        alertTypeCtrl:
          configData.basicDetails.alert_type ||
          configData.basicDetails.alertType ||
          "default",
        latitudeCtrl:
          configData.basicDetails.site_latitude ||
          configData.basicDetails.latitude ||
          "",
        longitudeCtrl:
          configData.basicDetails.site_longitude ||
          configData.basicDetails.longitude ||
          "",
        categoryCtrl: configData.basicDetails.category || "",
        currencyCtrl:
          configData.basicDetails.currency ||
          configData.basicDetails.site_currency ||
          "",
      });

      // Update plant view step visibility based on the determined value
      if (plantViewValue === "groupsAndSubgroups") {
        this.showGroupModulesStep = true;
        // Mark thirdFormGroup as valid so stepper can pass through
        this.thirdFormGroup.get("thirdCtrl")?.setValue("");
        this.thirdFormGroup.get("thirdCtrl")?.updateValueAndValidity();
      }
    }

    // Load second step: Module configuration
    if (configData.moduleConfiguration) {
      const moduleConfig = configData.moduleConfiguration;

      // Set channels and positions
      if (moduleConfig.channels && moduleConfig.positions) {
        this.secondFormGroup.patchValue({
          channels: moduleConfig.channels,
          positions: moduleConfig.positions,
        });

        // Initialize grid with saved modules
        this.initGridModules();

        if (moduleConfig.modules && Array.isArray(moduleConfig.modules)) {
          this.loadSavedModulesToGrid(moduleConfig.modules);
        }
      }

      // Load special modules configuration separately
      // This ensures special modules are properly loaded into moduleConfigs
      this.loadSpecialModulesConfiguration(configData);
    }

    // Load third step: Group configuration
    if (configData.groupConfiguration && configData.groupConfiguration.groups) {

      // Transform backend snake_case properties to frontend camelCase
      const transformedGroups = configData.groupConfiguration.groups.map(
        (group: any, index: number) => {

          // Transform the group object
          const transformedGroup: any = {
            groupName: group.group_name || group.groupName || group.name,
            groupPosition:
              group.group_position || group.groupPosition || group.position,
            createSubGroups:
              group.createSubGroups !== undefined
                ? group.createSubGroups
                : group.subgroups && group.subgroups.length > 0,
            numberOfSubGroups:
              group.numberOfSubGroups ||
              (group.subgroups ? group.subgroups.length : 0),
            modules: group.modules || [],
          };

          // Transform subgroups if they exist
          if (group.subgroups && Array.isArray(group.subgroups)) {

            transformedGroup.subGroups = group.subgroups.map(
              (subgroup: any, subIndex: number) => {
                const transformedSubgroup: any = {
                  name: subgroup.subgroup_name || subgroup.name,
                  position: subgroup.subgroup_position || subgroup.position,
                  weight: subgroup.subgroup_weight || subgroup.weight,
                  modules: subgroup.modules || [],
                };

                return transformedSubgroup;
              }
            );
          } else if (group.subGroups && Array.isArray(group.subGroups)) {
            transformedGroup.subGroups = group.subGroups.map(
              (subgroup: any, subIndex: number) => {
                return subgroup;
              }
            );
          }


          return transformedGroup;
        }
      );

      // Assign the transformed groups to createdGroups
      this.createdGroups = transformedGroups;

      // Don't automatically mark as completed - let user visit the step first
      if (this.createdGroups.length > 0) {
        this.thirdFormGroup.patchValue({
          thirdCtrl: "hasGroup",
        });
        // Only mark as completed if user has actually visited this step
        // this.isThirdStepCompleted = true;
        // this.isThirdStepError = false;
      }
    }

    // Load fourth step: Site score ranges
    if (configData.siteScoreRanges) {

      // Load moduleRanges (slider values for each module)
      if (
        configData.siteScoreRanges.moduleRanges &&
        Array.isArray(configData.siteScoreRanges.moduleRanges)
      ) {
        // Convert ranges from strings to numbers if needed, and ensure proper format
        const loadedRanges = configData.siteScoreRanges.moduleRanges.map(
          (mod: any) => {
            let range = mod.range || [1, 9];

            // Convert string ranges to number arrays if needed
            if (Array.isArray(range)) {
              range = range.map((val: any) => {
                const numVal = typeof val === "string" ? parseFloat(val) : val;
                return isNaN(numVal) ? 1 : numVal;
              });
            } else {
              range = [1, 9]; // Default if range is not valid
            }

            return {
              code: mod.code || mod.module_code || "",
              name: mod.module_name || mod.name || "",
              range: range as [number, number],
            };
          }
        );

        // Store loaded ranges temporarily before updateDynamicModuleRanges is called
        this.dynamicModuleRanges = loadedRanges;

        // Also load moduleScoreRanges if available for reference
        if (
          configData.siteScoreRanges.moduleScoreRanges &&
          Array.isArray(configData.siteScoreRanges.moduleScoreRanges)
        ) {
          // Use the pre-calculated moduleScoreRanges from API
          this.moduleScoreRanges =
            configData.siteScoreRanges.moduleScoreRanges.map((mod: any) => {
              // Find the corresponding module range for fallback
              const moduleRange = loadedRanges.find(
                (r) =>
                  r.code === (mod.code || mod.module_code) ||
                  r.name === (mod.module_name || mod.name)
              );

              const range = moduleRange?.range || mod.range || [1, 9];
              // Ensure range values are numbers for fallback calculation
              const left =
                typeof range[0] === "string" ? parseFloat(range[0]) : range[0];
              const right =
                typeof range[1] === "string" ? parseFloat(range[1]) : range[1];
              const numLeft = isNaN(left) ? 1 : left;
              const numRight = isNaN(right) ? 9 : right;

              // Convert low/medium/high from strings to numbers if needed
              const convertRange = (r: any): [number, number] => {
                if (!Array.isArray(r) || r.length !== 2) {
                  return [0, 0];
                }
                const min = typeof r[0] === "string" ? parseFloat(r[0]) : r[0];
                const max = typeof r[1] === "string" ? parseFloat(r[1]) : r[1];
                return [isNaN(min) ? 0 : min, isNaN(max) ? 0 : max];
              };

              return {
                code: mod.code || mod.module_code || "",
                module_code: mod.module_code || mod.code || "",
                module_name: mod.module_name || mod.name || "",
                name: mod.name || mod.module_name || "",
                module: mod.module || mod.module_name || mod.name || "",
                // Use API values if available, otherwise calculate from range
                low: mod.low
                  ? convertRange(mod.low)
                  : [this.sliderMin, numLeft],
                medium: mod.medium
                  ? convertRange(mod.medium)
                  : [numLeft, numRight],
                high: mod.high
                  ? convertRange(mod.high)
                  : [numRight, this.sliderMax],
              };
            });
        } else {
          // If moduleScoreRanges not provided, calculate from loaded moduleRanges
          this.moduleScoreRanges = loadedRanges.map((mod) => {
            const left = mod.range[0];
            const right = mod.range[1];
            return {
              code: mod.code,
              module_code: mod.code,
              module_name: mod.name,
              name: mod.name,
              module: mod.name,
              low: [this.sliderMin, left],
              medium: [left, right],
              high: [right, this.sliderMax],
            };
          });
        }
      }

      // Don't automatically mark as completed - let user visit the step first
      // this.isFourthStepCompleted = true;
      // this.isFourthStepError = false;
    }

    // Load fifth step: Opex configuration
    if (
      configData.opexConfiguration &&
      configData.opexConfiguration.parameters
    ) {
      this.loadOpexDataConfiguration(configData);
    }

    // Ensure special modules maintain correct properties
    this.ensureSpecialModuleProperties();

    // Update dynamic module ranges (this will preserve loaded ranges from siteScoreRanges)
    // Only update if we haven't loaded ranges from configData.siteScoreRanges
    // The updateDynamicModuleRanges method will preserve existing ranges for modules that match
    if (
      !configData.siteScoreRanges ||
      !configData.siteScoreRanges.moduleRanges
    ) {
      this.updateDynamicModuleRanges();
    } else {
      // If we loaded ranges, merge with any new modules that might not have been in the saved config
      // but preserve the loaded ranges for existing modules
      this.updateDynamicModuleRangesWithPreservedRanges();
    }


    // Trigger change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  /**
   * Show helpful message about loaded ranges
   */
  private showLoadedRangesMessage(): void {
    const rangesInfo = this.getLoadedRangesInfo();

    if (rangesInfo.loadedFromConfig > 0) {
      this.messageService.add({
        severity: "info",
        summary: "Ranges Loaded",
        detail: rangesInfo.message,
        life: 8000, // Show for 8 seconds
        closable: true,
      });
    }
  }

  loadSavedModulesToGrid(savedModules: any[]) {
    if (!savedModules || !Array.isArray(savedModules)) return;


    savedModules.forEach((module, index) => {
      if (
        module.channel !== undefined &&
        module.channel !== null &&
        module.position
      ) {
        const row = parseInt(module.channel) - 1; // Convert channel to 0-based index
        const col = this.getColumnIndex(module.position); // Convert position to 0-based index

        if (
          row >= 0 &&
          row < this.gridModules.length &&
          col >= 0 &&
          col < this.gridModules[0].length
        ) {
          // Create module object for grid
          const aliasValue =
            module.module_alias || module.alias || module.aliasName || "";
          this.gridModules[row][col] = {
            name: module.module_name || module.name,
            module_code: module.module_code || module.code,
            module_alias: aliasValue,
            aliasName: aliasValue, // Also set aliasName for consistency
          } as any;

          // Load module configuration if parameters exist
          if (module.parameters && Array.isArray(module.parameters)) {
            this.loadModuleConfiguration(module);
          }
        }
      }
    });
  }

  getColumnIndex(position: string): number {
    // Convert position labels (A, B, C, etc.) to 0-based index
    let index = 0;
    for (let i = 0; i < position.length; i++) {
      index = index * 26 + (position.charCodeAt(i) - 65);
    }
    return index;
  }

  loadModuleConfiguration(module: any) {
    if (!module.parameters || !Array.isArray(module.parameters)) return;


    const key =
      (module.module_name || module.name) +
      (module.channel && module.position
        ? `_${module.channel}${module.position}`
        : "");

    // Check for module_id in different possible field names
    let moduleId = module.module_id || module.moduleId || module.id || null;

    // Transform backend parameters to frontend format and mark them as selected
    // ALL parameters from saved configuration should be selected
    const transformedParameters = module.parameters.map((param: any) => ({
      parameter_id: param.parameter_id,
      parameter_name: param.parameter_name,
      code: param.code || param.parameter_code,
      parameter_code: param.parameter_code || param.code,
      parameter_unit: param.parameter_unit,
      parameter_type: param.parameter_type,
      parameter_formula: param.parameter_formula,
      upload_source: param.upload_source,
      parameter_weight: param.parameter_weight, // Preserve the original parameter_weight field
      allranges: param.allranges || [],
      // Map backend fields to frontend table structure
      unit: param.parameter_unit || "",
      type: param.parameter_type || 0,
      uploadType: param.upload_source || "manual",
      weight: param.parameter_weight || 0,
      formula: param.parameter_formula || "",
      calculation: param.calculation || param.parameter_calculation || "",
      parameter_calculation: param.parameter_calculation || param.calculation || "",
      query: param.parameter_query || "",
      criticalParameter:
        param.parameter_critical === 1 || param.parameter_critical === true,
      // FORCE ALL parameters from saved configuration to be selected
      selected: true,
      checked: true,
      isFromSavedConfig: true, // Flag to indicate this came from saved config
      // Add ranges in the format expected by frontend - respect backend selection status
      // IMPORTANT: Deduplicate ranges from backend to prevent duplicates
      ranges: (() => {
        const allRanges = param.allranges || [];

        // Step 1: First deduplicate by range_id (if they exist) - keep first occurrence
        const seenRangeIds = new Set();
        const deduplicatedById = allRanges.filter((range: any) => {
          if (range.range_id != null) {
            if (seenRangeIds.has(range.range_id)) {
              return false; // Skip duplicate
            }
            seenRangeIds.add(range.range_id);
          }
          return true; // Keep this range
        });


        // Step 2: Then deduplicate by signature (min-max-score) - keep first occurrence
        const seenSignatures = new Set();
        const deduplicatedBySignature = deduplicatedById.filter(
          (range: any) => {
            const min = range.min_value || range.min || 0;
            const max = range.max_value || range.max || 0;
            const score = range.score || 0;
            const signature = `${min}-${max}-${score}`;
            if (seenSignatures.has(signature)) {
              return false; // Skip duplicate
            }
            seenSignatures.add(signature);
            return true; // Keep this range
          }
        );


        // Step 3: Map to frontend format
        const mappedRanges = deduplicatedBySignature.map((range: any) => ({
          score: range.score || 0,
          min: range.min_value || range.min || 0,
          max: range.max_value || range.max || 0,
          selected: range.selected !== undefined ? range.selected : true, // Use backend selected status, default to true if not provided
          query: range.query || range.parameter_range_name || "",
          range_id: range.range_id,
          // Add workaround fields
          loadedFromSavedConfig: true, // Flag to indicate this range was loaded from saved config
          originalSelection:
            range.selected !== undefined ? range.selected : true, // Track original selection state
          // Also add the backend field names for compatibility
          min_value: range.min_value || range.min || 0,
          max_value: range.max_value || range.max || 0,
        }));


        return mappedRanges;
      })(),
    }));

    // Debug: Log parameter selection status
    // });

    // Create the module configuration object with module_id
    // Handle displayOnDashboard - check both camelCase and snake_case, prioritize camelCase
    const displayOnDashboardValue =
      module.displayOnDashboard !== undefined
        ? module.displayOnDashboard
        : module.display_on_dashboard !== undefined
        ? !!module.display_on_dashboard
        : false;

    this.moduleConfigs[key] = {
      ...module,
      module_id: moduleId, // Add module_id to preserve database relationship
      aliasName: module.module_alias || module.alias || "",
      moduleType: module.module_type || "",
      displayOnDashboard: displayOnDashboardValue,
      display_on_dashboard: displayOnDashboardValue, // Also preserve snake_case for consistency
      parameters: transformedParameters,
      // Preserve all original module data including parameter_id
      allparameters: transformedParameters, // This is what the dialog component expects
      // FORCE ALL parameters from saved configuration to be selected
      selectedProductCodes: transformedParameters
        .map((p: any) => p.code || p.parameter_code || "")
        .filter(Boolean),
      // Additional flag to indicate this is from saved config
      isFromSavedConfiguration: true,
    };

  }

  /**
   * Find the maximum channel and position values from configured modules
   * Returns { maxChannel: number, maxPosition: number }
   */
  getMaxChannelAndPosition(): { maxChannel: number; maxPosition: number } {
    let maxChannel = 0;
    let maxPosition = 0;

    // Check gridModules for configured modules
    if (this.gridModules && this.gridModules.length > 0) {
      for (let row = 0; row < this.gridModules.length; row++) {
        for (let col = 0; col < (this.gridModules[row]?.length || 0); col++) {
          const module = this.gridModules[row][col];
          if (module && module.name) {
            // Channel is row + 1 (0-based to 1-based)
            const channel = row + 1;
            maxChannel = Math.max(maxChannel, channel);

            // Position is column index converted to letter (A=1, B=2, etc.)
            const position = col + 1; // 0-based to 1-based
            maxPosition = Math.max(maxPosition, position);
          }
        }
      }
    }

    // Also check moduleConfigs keys for channel/position info
    // Keys are in format: "ModuleName_3C" where 3 is channel and C is position
    Object.keys(this.moduleConfigs).forEach((key) => {
      // Skip special modules (Complete System, Lab Module, Corporate Module)
      if (
        key.includes("Complete System") ||
        key.includes("Lab Module") ||
        key.includes("Corporate Module")
      ) {
        return;
      }

      // Extract channel and position from key format: "ModuleName_3C"
      const match = key.match(/_(\d+)([A-Z]+)$/);
      if (match) {
        const channel = parseInt(match[1], 10);
        const positionStr = match[2];
        // Convert position letter to number (A=1, B=2, etc.)
        let position = 0;
        for (let i = 0; i < positionStr.length; i++) {
          position = position * 26 + (positionStr.charCodeAt(i) - 64);
        }

        maxChannel = Math.max(maxChannel, channel);
        maxPosition = Math.max(maxPosition, position);
      }
    });

    return { maxChannel, maxPosition };
  }

  /**
   * Custom validator to ensure channel/position is not smaller than maximum used value
   */
  minChannelPositionValidator(minValue: number) {
    return (control: any) => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      const value = parseInt(control.value, 10);
      if (isNaN(value) || value < minValue) {
        return {
          minChannelPosition: {
            requiredMin: minValue,
            actualValue: value,
          },
        };
      }
      return null;
    };
  }

  /**
   * Get maximum channel and position for use in template
   */
  get maxChannelValue(): number {
    return this.getMaxChannelAndPosition().maxChannel;
  }

  get maxPositionValue(): number {
    return this.getMaxChannelAndPosition().maxPosition;
  }

  /**
   * Update validators for channel and position form controls based on configured modules
   */
  updateChannelPositionValidators() {
    const { maxChannel, maxPosition } = this.getMaxChannelAndPosition();

    const channelsControl = this.secondFormGroup.get("channels");
    const positionsControl = this.secondFormGroup.get("positions");

    if (channelsControl) {
      const validators = [Validators.required, Validators.min(1)];
      if (maxChannel > 0) {
        validators.push(this.minChannelPositionValidator(maxChannel));
      }
      channelsControl.setValidators(validators);
      channelsControl.updateValueAndValidity();
    }

    if (positionsControl) {
      const validators = [Validators.required, Validators.min(1)];
      if (maxPosition > 0) {
        validators.push(this.minChannelPositionValidator(maxPosition));
      }
      positionsControl.setValidators(validators);
      positionsControl.updateValueAndValidity();
    }
  }

  initGridModules() {
    const rows = this.secondFormGroup.get("channels")?.value || 0;
    const cols = this.secondFormGroup.get("positions")?.value || 0;

    // Preserve existing modules before resizing
    const existingModules: Array<{ row: number; col: number; module: any }> =
      [];
    if (this.gridModules && this.gridModules.length > 0) {
      for (let row = 0; row < this.gridModules.length; row++) {
        for (let col = 0; col < (this.gridModules[row]?.length || 0); col++) {
          const module = this.gridModules[row][col];
          if (module && module.name) {
            // Only preserve modules that will fit in the new grid size
            if (row < rows && col < cols) {
              existingModules.push({ row, col, module });
            }
          }
        }
      }
    }

    // Create new grid with new dimensions
    this.gridModules = Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => null)
    );

    // Restore preserved modules to their original positions
    existingModules.forEach(({ row, col, module }) => {
      if (row < rows && col < cols) {
        this.gridModules[row][col] = module;
      }
    });

  }

  // Call this method whenever channels/positions change!

  getColumnLabel(index: number): string {
    // Supports up to 26 columns (A-Z). For more, use AA, AB, etc.
    let label = "";
    index++;
    while (index > 0) {
      let mod = (index - 1) % 26;
      label = String.fromCharCode(65 + mod) + label;
      index = Math.floor((index - mod) / 26);
    }
    return label;
  }

  getRowLabel(index: number): string {
    return (index + 1).toString();
  }

  openModuleConfigDialog(
    module: any,
    event?: MouseEvent,
    hideHeaderFields: boolean = false,
    gridCode?: string
  ) {
    // Prevent multiple dialogs from opening simultaneously
    if (this.dialog.openDialogs.length > 0) {
      return;
    }


    // Debug parameter selection status
    // if (module && module.parameters) {
    //   });
    // }


    // Check if module is null or undefined
    if (!module) {
      return;
    }

    document.body.classList.add("dialog-blur-bg");

    // Get module name (handle both module_name and name properties)
    const moduleName = module.module_name || module.name;

    // Assign fixed code for special modules
    let fixedCode = gridCode;
    if (moduleName === "Complete System") fixedCode = "00";
    if (moduleName === "Corporate Module") fixedCode = "0CP";
    if (moduleName === "Lab Module") fixedCode = "0LAB";

    const key = moduleName + (fixedCode ? "_" + fixedCode : "");

    // Check if we have a configured module with parameters first
    let configuredModule = this.moduleConfigs[key];
    if (
      configuredModule &&
      configuredModule.allparameters &&
      configuredModule.allparameters.length > 0
    ) {
      module = configuredModule; // Use the configured module instead of generic one
    }

    let moduleId = module.module_id;
    let genericModule = null;

    // Always look up the generic module for field population, regardless of whether we have moduleId
    if (this.genericLibraryData?.allmodules) {
      genericModule = this.genericLibraryData.allmodules.find(
        (m: any) => m.module_name === moduleName
      );
      if (genericModule && !moduleId) {
        moduleId = genericModule.module_id;
      }
    }

    // For grid modules, populate missing fields from generic library data
    // This should ONLY run for new modules, not configured modules
    if (genericModule && !configuredModule) {
      // Only populate for new modules that don't have any field values
      const needsFieldPopulation =
        (!module.aliasName && !module.module_alias) ||
        (!module.moduleType && !module.module_type);

      if (needsFieldPopulation) {
        // Only populate if the fields are truly empty
        if (!module.aliasName && !module.module_alias) {
          module.module_alias = genericModule.module_alias || "";
        }
        if (!module.moduleType && !module.module_type) {
          module.module_type = genericModule.module_type || "";
        }

      }
    }

    // Transform parameters - use existing allparameters if available (configured module), otherwise transform from parameters
    let transformedParameters;
    if (
      configuredModule &&
      configuredModule.allparameters &&
      configuredModule.allparameters.length > 0
    ) {
      // Use existing allparameters from configured module, but ensure ranges have proper flags and deduplicate
      transformedParameters = configuredModule.allparameters.map(
        (param: any) => {

          if (param.ranges && Array.isArray(param.ranges)) {
            // Step 1: First deduplicate by range_id (if they exist) - keep first occurrence
            const seenRangeIds = new Set();
            const deduplicatedById = param.ranges.filter((range: any) => {
              if (range.range_id != null) {
                if (seenRangeIds.has(range.range_id)) {
                  return false; // Skip duplicate
                }
                seenRangeIds.add(range.range_id);
              }
              return true; // Keep this range
            });


            // Step 2: Then deduplicate by signature (min-max-score) - keep first occurrence
            const seenSignatures = new Set();
            const deduplicatedBySignature = deduplicatedById.filter(
              (range: any) => {
                const signature = `${range.min || range.min_value || 0}-${
                  range.max || range.max_value || 0
                }-${range.score || 0}`;
                if (seenSignatures.has(signature)) {
                  return false; // Skip duplicate
                }
                seenSignatures.add(signature);
                return true; // Keep this range
              }
            );


            // Step 3: Update ranges with proper flags
            const updatedRanges = deduplicatedBySignature.map((range: any) => ({
              ...range,
              loadedFromSavedConfig: true, // Mark as coming from saved configuration
            }));


            // Also update allranges to match (if it exists)
            const updatedAllRanges =
              param.allranges && Array.isArray(param.allranges)
                ? deduplicatedBySignature.map((range: any) => ({
                    ...range,
                    loadedFromSavedConfig: true,
                  }))
                : updatedRanges;

            return {
              ...param,
              ranges: updatedRanges,
              allranges: updatedAllRanges, // Ensure allranges also has deduplicated ranges
            };
          }
          return param;
        }
      );
    } else {
      // Transform parameters from generic module

      transformedParameters = (module.parameters || []).map(
        (param: any, index: number) => {

          // Check for ranges in different possible locations

          // Use existing ranges if available, otherwise create default ranges
          let rangesToUse;
          if (
            param.allranges &&
            Array.isArray(param.allranges) &&
            param.allranges.length > 0
          ) {

            // Use ranges from backend/saved configuration
            rangesToUse = param.allranges.map(
              (range: any, rangeIdx: number) => {

                return {
                  score: range.score || 0,
                  min: range.min_value || range.min || 0,
                  max: range.max_value || range.max || 0,
                  selected:
                    range.selected !== undefined ? range.selected : true, // Use backend selected status
                  query: range.query || range.parameter_range_name || "",
                  range_id: range.range_id,
                  loadedFromSavedConfig: true,
                };
              }
            );

          } else {
            // Create default ranges if no ranges exist
            rangesToUse = [
              { score: 0, min: 1, max: 20, selected: false, query: "" },
              { score: 0, min: 21, max: 40, selected: false, query: "" },
              { score: 0, min: 41, max: 60, selected: false, query: "" },
            ];
          }

          const transformedParam = {
            parameter_id: param.parameter_id,
            parameter_name: param.parameter_name || param.parameters || "",
            parameter_code: param.parameter_code || param.code,
            parameter_unit: param.parameter_unit,
            parameter_type: param.parameter_type,
            parameter_formula: param.parameter_formula,
            upload_source: param.upload_source,
            parameter_weight: param.parameter_weight, // Preserve the original parameter_weight field
            // Use existing ranges or default ranges
            allranges: rangesToUse,
            // Map backend fields to frontend table structure
            unit: param.parameter_unit || "",
            type: param.parameter_type || 0,
            uploadType: param.upload_source === 0 ? "Manual" : "Online",
            weight: param.parameter_weight || 0,
            formula: param.parameter_formula || "",
            calculation: param.calculation || param.parameter_calculation || "",
            parameter_calculation:
              param.parameter_calculation || param.calculation || "",
            query: param.parameter_query || "",
            criticalParameter:
              param.parameter_critical === 1 ||
              param.parameter_critical === true,
            // Mark as selected since it exists in the backend response
            selected: true,
            checked: true,
            // Use the same ranges array for both allranges and ranges
            // IMPORTANT: This creates a reference to the same array, not a copy
            ranges: rangesToUse,
          };


          return transformedParam;
        }
      );

    }

    // Create selectedProductCodes array with all parameter codes from saved configuration
    const selectedProductCodes = transformedParameters
      .map((param) => param.parameter_code || param.code)
      .filter(Boolean);

    // Always update the module config with current parameters
    this.moduleConfigs[key] = {
      ...module,
      module_id: moduleId, // Preserve module_id for database relationship
      // Preserve saved values if they exist, otherwise use module fields
      aliasName: module.aliasName || module.module_alias || "",
      moduleType: module.moduleType || module.module_type || "",
      displayOnDashboard:
        module.displayOnDashboard !== undefined
          ? module.displayOnDashboard
          : module.display_on_dashboard || false,
      allparameters: transformedParameters, // This is what the dialog expects
      parameters: transformedParameters,
      selectedProductCodes: selectedProductCodes, // This tells the dialog which parameters are selected
      // Preserve all original module data including parameter_id
    };


    // Automatically set module_code and module_alias for special modules
    if (module?.module_name === "Complete System") {
      this.moduleConfigs[key].module_code = "00";
      this.moduleConfigs[key].module_alias = "Complete System";
    } else if (module?.module_name === "Lab Module") {
      this.moduleConfigs[key].module_code = "0LAB";
      this.moduleConfigs[key].module_alias = "Lab Module";
    } else if (module?.module_name === "Corporate Module") {
      this.moduleConfigs[key].module_code = "0CP";
      this.moduleConfigs[key].module_alias = "Corporate Module";
    }

    // Store the fixedCode and moduleName in a variable accessible to the callback
    const storedFixedCode = fixedCode;
    const storedModuleName = moduleName;
    const storedKey = key;

    // Calculate count of configured grid modules with displayOnDashboard enabled
    const configuredGridModulesCount =
      this.getConfiguredGridModulesWithDisplayOnDashboardCount();

    const dialogRef = this.dialog.open(ModuleConfigDialogComponent, {
      data: {
        module: this.moduleConfigs[key],
        allmodules: this.genericLibraryData.allmodules,
        hideHeaderFields,
        gridCode: storedFixedCode,
        configuredGridModulesCount: configuredGridModulesCount,
        isCategoryMode: this.isCategoryMode,
        subCategoryId: this.resolveSubCategoryId(),
        categoryMasterId: this.firstFormGroup?.get("categoryCtrl")?.value ?? null,
        siteId: this.activeSiteId,
        moduleCode: this.resolveModuleCode(this.moduleConfigs[key], storedFixedCode),
        siblingModules: this.buildSiblingModules(key),
        ownerName: this.firstFormGroup?.get("siteNameCtrl")?.value ?? "",
        isDraft: this.isConfigDraft,
      },
      disableClose: false,
      autoFocus: false,
      restoreFocus: false,
      hasBackdrop: true,
      backdropClass: "custom-backdrop",
      width: "94vw",
      maxWidth: "1680px",
      height: "90vh",
      maxHeight: "800px",
      panelClass: "module-config-dialog-panel",
      role: "dialog",
      ariaDescribedBy: "dialog-description",
      closeOnNavigation: true,
      viewContainerRef: undefined,
    });

    dialogRef.afterClosed().subscribe((result) => {
      document.body.classList.remove("dialog-blur-bg");
      if (result) {

        this.moduleConfigs[storedKey] = result;

        // Update gridModules array to reflect the alias name if provided
        // Only update for grid modules (not special modules like Complete System, Lab Module, Corporate Module)
        if (
          storedFixedCode &&
          !["00", "0LAB", "0CP"].includes(storedFixedCode)
        ) {
          // Find the grid position using the stored fixedCode
          for (let row = 0; row < this.gridModules.length; row++) {
            for (let col = 0; col < this.gridModules[row]?.length; col++) {
              const gridCodeForCell =
                this.getRowLabel(row) + this.getColumnLabel(col);
              if (gridCodeForCell === storedFixedCode) {
                const gridModule = this.gridModules[row][col] as any;
                const moduleNameCheck =
                  (gridModule?.name || gridModule?.module_name || "") ===
                  storedModuleName;
                if (gridModule && moduleNameCheck) {
                  // Update the grid module with alias name if provided
                  if (result.aliasName || result.module_alias) {
                    gridModule.module_alias =
                      result.aliasName || result.module_alias;
                    gridModule.aliasName =
                      result.aliasName || result.module_alias;
                  }

                  // Update displayOnDashboard if provided
                  if (result.displayOnDashboard !== undefined) {
                    gridModule.displayOnDashboard = result.displayOnDashboard;
                    gridModule.display_on_dashboard = result.displayOnDashboard;
                  }

                  // Clear validation error for this module if it has parameters
                  if (
                    result.parameters &&
                    Array.isArray(result.parameters) &&
                    result.parameters.length > 0
                  ) {
                    const errorKey = `${row}_${col}`;
                    this.modulesWithValidationErrors.delete(errorKey);
                  }

                  break;
                }
              }
            }
          }
        }

        this.updateDynamicModuleRanges();

        // Immediately save the updated configuration to session storage
        this.saveFormDataToSession();
      }
    });
    if (event) {
      (event.target as HTMLElement).blur();
    }
  }

  // Called when a sidebar label is clicked
  onSidebarParamClick(index: number) {
    if (this.opexParameters[index].checked) {
      this.selectedOpexIndex = index;

      // Always repopulate to ensure all parameters are shown
      const selectedParam = this.opexParameters[index];

      // Always repopulate to show ALL parameters (configured and non-configured)
      this.populateOpexDataForModule(selectedParam);

    }
  }

  // Populate opex data for a specific module
  populateOpexDataForModule(param: any) {

    // If we already have data for this module (from category API), use it instead of generic library
    if (
      this.opexData[param.key] &&
      Array.isArray(this.opexData[param.key]) &&
      this.opexData[param.key].length > 0
    ) {
      return; // Don't overwrite existing data from API
    }

    // Always start with ALL parameters from generic library data
    if (!this.genericLibraryData || !this.genericLibraryData.allopexmodules) {
      return;
    }

    const opexModule = this.genericLibraryData.allopexmodules.find(
      (mod: any) => mod.module_id === param.id
    );

    if (!opexModule || !opexModule.allopexparameters) {
      return;
    }


    // Start with all parameters from generic library (unselected by default)
    this.opexData[param.key] = opexModule.allopexparameters.map(
      (paramData: any) => ({
        parameter_id: paramData.parameter_id,
        parameter_name: paramData.parameter_name,
        details: paramData.details || "",
        parameter_unit: paramData.parameter_unit || "",
        parameter_type:
          this.getParameterTypeString(paramData.parameter_type) || "",
        parameter_code: paramData.parameter_code || "",
        uploadType: paramData.upload_source === 2 ? "Online" : "Manual",
        formula: paramData.parameter_formula || "",
        price_type: this.getPriceTypeString(paramData.price_type) || "",
        price: paramData.price || "",
        selected: false, // Default to unchecked for all parameters
      })
    );

    // Now apply saved configuration if available (this will update selection status and user edits)
    if (this.savedConfiguration && this.savedConfiguration.opexConfiguration) {
      const savedModule =
        this.savedConfiguration.opexConfiguration.parameters.find(
          (sm: any) => sm.module_code === param.key
        );

      if (savedModule && savedModule.parameters) {

        // Apply saved configuration as an overlay on top of all parameters
        savedModule.parameters.forEach((savedParameter: any) => {

          // Try multiple matching strategies
          let existingParamIndex = -1;

          // Strategy 1: Match by parameter_id (most reliable)
          if (savedParameter.parameter_id) {
            existingParamIndex = this.opexData[param.key].findIndex(
              (p: any) => p.parameter_id === savedParameter.parameter_id
            );
          }

          // Strategy 2: Match by parameter_name (case-insensitive)
          if (existingParamIndex === -1 && savedParameter.parameter_name) {
            existingParamIndex = this.opexData[param.key].findIndex(
              (p: any) =>
                p.parameter_name &&
                p.parameter_name.toLowerCase().trim() ===
                  savedParameter.parameter_name.toLowerCase().trim()
            );
          }

          // Strategy 3: Match by parameter_code
          if (existingParamIndex === -1 && savedParameter.parameter_code) {
            existingParamIndex = this.opexData[param.key].findIndex(
              (p: any) => p.parameter_code === savedParameter.parameter_code
            );
          }

          // Strategy 4: Partial name matching (for cases like "Effluent treated flow" vs "Treated Flow")
          if (existingParamIndex === -1 && savedParameter.parameter_name) {
            const savedName = savedParameter.parameter_name
              .toLowerCase()
              .trim();
            existingParamIndex = this.opexData[param.key].findIndex(
              (p: any) => {
                if (!p.parameter_name) return false;
                const paramName = p.parameter_name.toLowerCase().trim();
                // Check if saved name contains param name or vice versa
                return (
                  paramName.includes(savedName) || savedName.includes(paramName)
                );
              }
            );
          }

          if (existingParamIndex !== -1) {
            // Update the existing parameter with saved configuration
            this.opexData[param.key][existingParamIndex] = {
              ...this.opexData[param.key][existingParamIndex], // Keep all generic data
              // Overlay with saved configuration
              details:
                savedParameter.details ||
                this.opexData[param.key][existingParamIndex].details,
              parameter_unit:
                savedParameter.parameter_unit ||
                this.opexData[param.key][existingParamIndex].parameter_unit,
              parameter_type:
                this.getParameterTypeString(savedParameter.parameter_type) ||
                this.opexData[param.key][existingParamIndex].parameter_type,
              parameter_code:
                savedParameter.parameter_code ||
                this.opexData[param.key][existingParamIndex].parameter_code,
              uploadType:
                savedParameter.upload_source === 2 ? "Online" : "Manual",
              formula:
                savedParameter.parameter_formula ||
                this.opexData[param.key][existingParamIndex].formula,
              price_type:
                this.getPriceTypeString(savedParameter.price_type) ||
                this.opexData[param.key][existingParamIndex].price_type,
              price:
                savedParameter.price ||
                this.opexData[param.key][existingParamIndex].price,
              selected: savedParameter.selected || false, // Apply saved selection status
            };

          } else {
            // Parameter not found in generic library - add it to the list

            const newParameter = {
              parameter_id: savedParameter.parameter_id,
              parameter_name: savedParameter.parameter_name,
              details: savedParameter.details || "",
              parameter_unit: savedParameter.parameter_unit || "",
              parameter_type:
                this.getParameterTypeString(savedParameter.parameter_type) ||
                "Raw",
              parameter_code: savedParameter.parameter_code || "",
              uploadType:
                savedParameter.upload_source === 2 ? "Online" : "Manual",
              formula: savedParameter.parameter_formula || "",
              price_type:
                this.getPriceTypeString(savedParameter.price_type) || "Fixed",
              price: savedParameter.price || "",
              selected: savedParameter.selected || false, // Preserve selection status
            };

            // Add the parameter to the list
            this.opexData[param.key].push(newParameter);

          }
        });

      }
    }

  }

  // Returns the data for the selected parameter
  getOpexDataForSelected() {
    if (
      this.selectedOpexIndex !== null &&
      this.opexParameters[this.selectedOpexIndex]?.checked
    ) {
      const selectedParam = this.opexParameters[this.selectedOpexIndex];
      // Return data from opexData which has the correct selection status
      return this.opexData[selectedParam.key] || [];
    }
    return [];
  }

  getSelectedOpexRows() {
    return this.getOpexDataForSelected().filter((row) => row.selected);
  }

  onCreateNewGroup(): void {
    const configuredGridModules = this.getConfiguredGridModules();
    const configuredSpecialModules = this.getConfiguredSpecialModules();
    const allModules = [...configuredGridModules, ...configuredSpecialModules];


    const dialogRef = this.dialog.open(CreateGroupDialogComponent, {
      width: "1000px",
      panelClass: "custom-dialog-radius",
      data: { modules: allModules, existingGroups: this.createdGroups },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && Array.isArray(result)) {

        // Add isExpanded property to each group
        result.forEach((group) => {
          group.isExpanded = false;
        });

        this.createdGroups.push(...result);

        // Mark thirdCtrl as valid when a group is created
        this.thirdFormGroup.get("thirdCtrl")?.setValue("hasGroup");
        this.thirdFormGroup.get("thirdCtrl")?.updateValueAndValidity();
      }
    });
  }

  onEditGroup(group: any): void {
    // Get the modules that were available when the group was created
    // If you want to use the current grid modules, use getConfiguredGridModules()
    const configuredGridModules = this.getConfiguredGridModules();
    const configuredSpecialModules = this.getConfiguredSpecialModules();
    const allModules = [...configuredGridModules, ...configuredSpecialModules];

    const dialogRef = this.dialog.open(CreateGroupDialogComponent, {
      width: "1000px",
      panelClass: "custom-dialog-radius",
      data: { group, modules: allModules, existingGroups: this.createdGroups }, // Pass both group and modules!
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && Array.isArray(result)) {

        // Optionally update the group in createdGroups if edited
        const idx = this.createdGroups.indexOf(group);
        if (idx > -1) {
          this.createdGroups.splice(idx, 1, ...result);
        }

      }
    });
  }

  // Delete a group from the createdGroups array
  deleteGroup(groupIndex: number): void {
    const group = this.createdGroups[groupIndex];
    const groupName = group?.groupName || "Unnamed Group";

    const dialogData: DeleteConfirmationData = {
      title: "Delete Group",
      message:
        "Are you sure you want to delete this group? This will remove the group and all its subgroups.",
      itemName: groupName,
      itemType: "group",
    };

    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: "400px",
      data: dialogData,
      disableClose: false,
      panelClass: "delete-confirmation-dialog-panel",
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {

        // Remove the group from the array
        this.createdGroups.splice(groupIndex, 1);

        // Update form validation
        if (this.createdGroups.length === 0) {
          this.thirdFormGroup.get("thirdCtrl")?.setValue("");
          this.thirdFormGroup.get("thirdCtrl")?.updateValueAndValidity();
          this.isThirdStepCompleted = false;
          this.isThirdStepError = true;
        }


        // Show success message
        this.messageService.add({
          severity: "success",
          summary: "Group Deleted",
          detail: "Group has been deleted successfully.",
        });
      }
    });
  }

  // Delete a subgroup from a specific group
  deleteSubgroup(groupIndex: number, subgroupIndex: number): void {
    const group = this.createdGroups[groupIndex];
    const subgroup = group?.subGroups?.[subgroupIndex];

    if (!group || !subgroup) {
      return;
    }

    const subgroupName = subgroup.name || "Unnamed Subgroup";

    const dialogData: DeleteConfirmationData = {
      title: "Delete Subgroup",
      message:
        "Are you sure you want to delete this subgroup? This action cannot be undone.",
      itemName: subgroupName,
      itemType: "subgroup",
    };

    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: "400px",
      data: dialogData,
      disableClose: false,
      panelClass: "delete-confirmation-dialog-panel",
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {

        // Remove the subgroup from the group
        group.subGroups.splice(subgroupIndex, 1);

        // Update the number of subgroups
        group.numberOfSubGroups = group.subGroups.length;

        // If no subgroups left, update the group structure
        if (group.subGroups.length === 0) {
          group.createSubGroups = false;
          group.subGroups = undefined;
          group.modules = [];
        }


        // Show success message
        this.messageService.add({
          severity: "success",
          summary: "Subgroup Deleted",
          detail: "Subgroup has been deleted successfully.",
        });
      }
    });
  }

  // Add this getter for filtered modules
  get filteredModules() {
    // Filter out specific modules that should not be available for drag and drop
    const excludedModules = [
      "Complete System",
      "Corporate Module",
      "Lab Module",
    ];
    let filtered = this.availableModules.filter(
      (mod) => !excludedModules.includes(mod.name)
    );

    // Apply search filter if search text exists
    if (this.moduleSearchText) {
      filtered = filtered.filter((mod) =>
        mod.name.toLowerCase().includes(this.moduleSearchText.toLowerCase())
      );
    }

    return filtered;
  }

  // Optional: method to clear search
  clearModuleSearch() {
    this.moduleSearchText = "";
    this.showModuleSearch = false;
  }

  getModuleByName(name: string) {
    // Find the full module object from availableModules (which is mapped from allmodules)
    // If availableModules only has name/id, use genericLibraryData.allmodules instead
    if (
      this.genericLibraryData &&
      Array.isArray(this.genericLibraryData.allmodules)
    ) {
      return this.genericLibraryData.allmodules.find(
        (mod: any) => mod.module_name === name
      );
    }
    return null;
  }

  // Method to find existing module configuration from saved data
  findExistingModuleConfiguration(moduleName: string) {

    // Check if we have saved configuration data
    if (!this.hasSavedConfiguration) {
      return null;
    }

    // First, look for the module in the moduleConfigs object
    const moduleKeys = Object.keys(this.moduleConfigs);
    for (const key of moduleKeys) {
      const config = this.moduleConfigs[key];
      if (
        config &&
        (config.module_name === moduleName || config.name === moduleName)
      ) {
        return config;
      }
    }

    // If not found in moduleConfigs, try to find it in the raw saved data
    // This handles cases where the module might not have been processed yet

    if (
      this.rawConfigurationData &&
      this.rawConfigurationData.moduleConfiguration &&
      this.rawConfigurationData.moduleConfiguration.modules
    ) {
      const rawModules = this.rawConfigurationData.moduleConfiguration.modules;

      for (const module of rawModules) {
        if (module.module_name === moduleName || module.name === moduleName) {
          return module;
        }
      }
    }

    return null;
  }

  // Method to handle module configuration button clicks
  handleModuleConfigClick(moduleName: string, event?: MouseEvent) {

    // First, try to find existing configuration
    const existingConfig = this.findExistingModuleConfiguration(moduleName);

    if (existingConfig) {
      // Open dialog with existing configuration
      this.openModuleConfigDialog(existingConfig, event, true);
    } else {
      // Try to load from raw data first
      const rawModule = this.loadModuleFromRawData(moduleName);
      if (rawModule) {
        // For raw modules, we need to open the dialog differently since they don't have a key in moduleConfigs
        this.openModuleConfigDialogDirect(rawModule, event, true);
      } else {
        // Fall back to generic library
        const genericModule = this.getModuleByName(moduleName);
        if (genericModule) {
          this.openModuleConfigDialog(genericModule, event, true);
        } else {
          this.messageService.add({
            severity: "error",
            summary: "Module Not Found",
            detail: `The module "${moduleName}" could not be found. Please refresh the page and try again.`,
          });
        }
      }
    }
  }

  // Method to manually load a module configuration from raw data
  loadModuleFromRawData(moduleName: string) {

    if (
      this.rawConfigurationData &&
      this.rawConfigurationData.moduleConfiguration &&
      this.rawConfigurationData.moduleConfiguration.modules
    ) {
      const rawModules = this.rawConfigurationData.moduleConfiguration.modules;

      const module = rawModules.find(
        (mod: any) => mod.module_name === moduleName || mod.name === moduleName
      );

      if (module) {

        // Process the module to ensure parameters are marked as selected
        this.loadModuleConfiguration(module);

        // Return the processed configuration instead of raw module
        const key =
          (module.module_name || module.name) +
          (module.channel && module.position
            ? `_${module.channel}${module.position}`
            : "");
        const processedConfig = this.moduleConfigs[key];

        if (processedConfig) {
          return processedConfig;
        } else {
          return module;
        }
      }
    }
    return null;
  }

  // Method to open dialog directly with module data (for raw modules)
  openModuleConfigDialogDirect(
    module: any,
    event?: MouseEvent,
    hideHeaderFields: boolean = false,
    gridCode?: string
  ) {

    // Prevent multiple dialogs from opening simultaneously
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    // Check if module is null or undefined
    if (!module) {
      return;
    }

    document.body.classList.add("dialog-blur-bg");

    // Get module name (handle both module_name and name properties)
    const moduleName = module.module_name || module.name;

    // Assign fixed code for special modules
    let fixedCode = gridCode;
    if (moduleName === "Complete System") fixedCode = "00";
    if (moduleName === "Corporate Module") fixedCode = "0CP";
    if (moduleName === "Lab Module") fixedCode = "0LAB";

    // Transform parameters the same way as the regular openModuleConfigDialog method
    // This ensures consistency with how grid modules work
    const transformedParameters = (module.parameters || []).map(
      (param: any) => {
        // Use existing ranges if available, otherwise create default ranges
        let rangesToUse;
        if (
          param.allranges &&
          Array.isArray(param.allranges) &&
          param.allranges.length > 0
        ) {
          // Use ranges from backend/saved configuration
          rangesToUse = param.allranges.map((range: any) => ({
            score: range.score || 0,
            min: range.min_value || range.min || 0,
            max: range.max_value || range.max || 0,
            selected: range.selected !== undefined ? range.selected : true, // Use backend selected status
            query: range.query || range.parameter_range_name || "",
            range_id: range.range_id,
            loadedFromSavedConfig: true,
          }));
        } else {
          // Create default ranges if no ranges exist
          rangesToUse = [
            { score: 0, min: 1, max: 20, selected: false, query: "" },
            { score: 0, min: 21, max: 40, selected: false, query: "" },
            { score: 0, min: 41, max: 60, selected: false, query: "" },
          ];
        }

        return {
          parameter_id: param.parameter_id,
          parameter_name: param.parameter_name || param.parameters || "",
          parameter_code: param.parameter_code || param.code,
          parameter_unit: param.parameter_unit,
          parameter_type: param.parameter_type,
          parameter_formula: param.parameter_formula,
          upload_source: param.upload_source,
          parameter_weight: param.parameter_weight, // Preserve the original parameter_weight field
          // Use existing ranges or default ranges
          allranges: rangesToUse,
          // Map backend fields to frontend table structure
          unit: param.parameter_unit || "",
          type: param.parameter_type || 0,
          uploadType: param.upload_source === 0 ? "Manual" : "Online",
          weight: param.parameter_weight || 0,
          formula: param.parameter_formula || "",
          calculation: param.calculation || param.parameter_calculation || "",
          parameter_calculation:
            param.parameter_calculation || param.calculation || "",
          query: param.parameter_query || "",
          criticalParameter:
            param.parameter_critical === 1 || param.parameter_critical === true,
          // FORCE ALL parameters from saved configuration to be selected
          selected: true,
          checked: true,
          // Use the same ranges array for both allranges and ranges
          ranges: rangesToUse,
        };
      }
    );

    // Create selectedProductCodes array with all parameter codes from saved configuration
    const selectedProductCodes = transformedParameters
      .map((param) => param.parameter_code || param.code)
      .filter(Boolean);

    const moduleWithSelection = {
      ...module,
      allparameters: transformedParameters, // This is what the dialog expects
      parameters: transformedParameters,
      selectedProductCodes: selectedProductCodes, // This tells the dialog which parameters are selected
    };


    // Calculate count of configured grid modules with displayOnDashboard enabled
    const configuredGridModulesCount =
      this.getConfiguredGridModulesWithDisplayOnDashboardCount();

    const dialogRef = this.dialog.open(ModuleConfigDialogComponent, {
      data: {
        module: moduleWithSelection, // Pass the module with selectedProductCodes
        allmodules: this.genericLibraryData?.allmodules || [],
        hideHeaderFields,
        gridCode: fixedCode,
        configuredGridModulesCount: configuredGridModulesCount,
        isCategoryMode: this.isCategoryMode,
        subCategoryId: this.resolveSubCategoryId(),
        categoryMasterId: this.firstFormGroup?.get("categoryCtrl")?.value ?? null,
        siteId: this.activeSiteId,
        moduleCode: this.resolveModuleCode(moduleWithSelection, fixedCode),
        // Same key this dialog's afterClosed writes back to, so the module being
        // configured never appears in its own dictionary.
        siblingModules: this.buildSiblingModules(`${moduleName}_${fixedCode}`),
        ownerName: this.firstFormGroup?.get("siteNameCtrl")?.value ?? "",
        isDraft: this.isConfigDraft,
      },
      disableClose: false,
      autoFocus: false,
      restoreFocus: false,
      hasBackdrop: true,
      backdropClass: "custom-backdrop",
      width: "94vw",
      maxWidth: "1680px",
      height: "90vh",
      maxHeight: "800px",
      panelClass: "module-config-dialog-panel",
      role: "dialog",
      ariaDescribedBy: "dialog-description",
      closeOnNavigation: true,
      viewContainerRef: undefined,
    });

    // Handle dialog close and save configuration
    dialogRef.afterClosed().subscribe((result) => {
      document.body.classList.remove("dialog-blur-bg");
      if (result) {
        // Create the key for special modules
        const key = `${moduleName}_${fixedCode}`;

        // Save the configuration to moduleConfigs
        this.moduleConfigs[key] = result;
        this.updateDynamicModuleRanges();

        // Immediately save the updated configuration to session storage
        this.saveFormDataToSession();
      }
    });

  }

  getModuleNameByCode(moduleCode: string): string {

    // First, check generic library modules
    if (
      this.genericLibraryData &&
      Array.isArray(this.genericLibraryData.allmodules)
    ) {
      // );

      const genericModule = this.genericLibraryData.allmodules.find(
        (mod: any) => mod.module_code === moduleCode
      );


      if (genericModule) {
        return genericModule.module_name;
      }
    }

    // If not found in generic library, check configured grid modules
    const configuredGridModules = this.getConfiguredGridModules();

    const gridModule = configuredGridModules.find(
      (mod: any) => mod.module_code === moduleCode || mod.code === moduleCode
    );

    if (gridModule) {
      const moduleName = gridModule.module_name || gridModule.name;
      return moduleName;
    }

    // If not found in grid modules, check special modules
    const configuredSpecialModules = this.getConfiguredSpecialModules();

    const specialModule = configuredSpecialModules.find(
      (mod: any) => mod.module_code === moduleCode || mod.code === moduleCode
    );

    if (specialModule) {
      const moduleName = specialModule.module_name || specialModule.name;
      return moduleName;
    }

    // If not found anywhere, fall back to the code
    return moduleCode;
  }

  startEditFormula(row: any) {
    this.editingFormulaRow = row;
  }

  saveFormulaEdit() {
    this.editingFormulaRow = null;
    // Optionally, trigger save to backend here
  }

  cancelFormulaEdit() {
    this.editingFormulaRow = null;
  }

  openFormulaEditor(row: any) {
    // Raw/Delta formulas are tied to the parameter code and are not manually
    // editable, so the editor must not open for them.
    if (this.isOpexRawOrDelta(row.parameter_type)) {
      return;
    }
    this.formulaEditorRow = row;
    this.formulaEditorText = row.formula || "";
    this.formulaEditorVisible = true;
  }

  saveFormulaEditor() {
    if (this.formulaEditorRow) {
      // For Raw/Delta types the formula is tied to the parameter code
      // (mirrors the module parameter editor in Step 2). Free-typed text is
      // only honoured for Score/Conditional types.
      if (this.isOpexRawOrDelta(this.formulaEditorRow.parameter_type)) {
        this.formulaEditorRow.formula = this.formulaEditorRow.parameter_code || "";
      } else {
        this.formulaEditorRow.formula = this.formulaEditorText;
      }
    }
    this.formulaEditorVisible = false;
    this.formulaEditorRow = null;
    this.formulaEditorText = "";
  }

  /**
   * Returns true for Opex parameter types whose formula must follow the
   * parameter code rather than a free-typed expression (Raw / Delta).
   */
  isOpexRawOrDelta(type: any): boolean {
    const normalized = (type ?? "").toString().trim().toLowerCase();
    return normalized === "raw" || normalized === "delta";
  }

  /**
   * Keep the formula in sync with the parameter code when the Opex row type
   * changes to Raw/Delta (mirrors Step 2 behaviour).
   */
  onOpexTypeChange(row: any) {
    if (row && this.isOpexRawOrDelta(row.parameter_type)) {
      row.formula = row.parameter_code || "";
    }
  }

  // Convert numeric price type to meaningful string
  getPriceTypeString(priceType: any): string {
    if (priceType === null || priceType === undefined) return "";

    const numericPriceType = Number(priceType);
    let result = "";

    switch (numericPriceType) {
      case 0:
        result = "Fixed";
        break;
      case 1:
        result = "Dynamic";
        break;
      default:
        // If it's already a string, use it directly (e.g., "Fixed" or "Dynamic")
        // Otherwise convert to string
        result = typeof priceType === 'string' ? priceType : priceType.toString();
    }

    return result;
  }

  // Prevent non-numeric input in price field
  onPriceKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const input = event.target as HTMLInputElement;
    const currentValue = input.value || '';
    
    // Allow: backspace, delete, tab, escape, enter
    if (
      charCode === 8 ||  // backspace
      charCode === 9 ||  // tab
      charCode === 27 || // escape
      charCode === 13 || // enter
      charCode === 46 || // delete
      (charCode >= 35 && charCode <= 40)    // home, end, left, right arrow keys
    ) {
      return true;
    }
    
    // Allow decimal point only if not already present
    if (charCode === 46 || charCode === 110) { // decimal point (both keypad and regular)
      if (currentValue.indexOf('.') !== -1) {
        event.preventDefault();
        return false;
      }
      return true;
    }
    
    // Allow numbers (0-9) and keypad numbers
    if ((charCode >= 48 && charCode <= 57) || (charCode >= 96 && charCode <= 105)) {
      return true;
    }
    
    // Block all other characters (alphabetic, special characters, etc.)
    event.preventDefault();
    return false;
  }

  // Convert numeric parameter type to meaningful string
  getParameterTypeString(parameterType: any): string {
    if (parameterType === null || parameterType === undefined) return "";

    const numericParameterType = Number(parameterType);
    let result = "";

    switch (numericParameterType) {
      case 0:
        result = "Raw";
        break;
      case 1:
        result = "Score";
        break;
      case 2:
        result = "Delta";
        break;
      case 3:
        result = "Conditional";
        break;
      default:
        result = parameterType.toString();
    }

    return result;
  }

  startEditOpexRow(row: any) {
    if (row.selected) {
      // Store a deep copy of the original row data before editing
      this.originalOpexRowData = JSON.parse(JSON.stringify(row));
      this.editingOpexRow = row;
      // Ensure Raw/Delta formulas reflect the current parameter code
      this.onOpexTypeChange(row);
    }
  }

  saveOpexRowEdit() {
    // Validate that parameter_name is not empty
    if (!this.editingOpexRow) {
      return;
    }

    const parameterName = this.editingOpexRow.parameter_name;
    if (!parameterName || (typeof parameterName === 'string' && parameterName.trim() === '')) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Parameter name is required. Please enter a value in the Parameters column before saving.',
        life: 5000
      });
      return; // Don't save if parameter name is empty
    }

    // Clear the original data since changes are being saved
    this.originalOpexRowData = null;
    this.editingOpexRow = null;
  }

  cancelOpexRowEdit() {
    if (this.editingOpexRow && this.originalOpexRowData) {
      // Restore all original values from the stored copy
      Object.keys(this.originalOpexRowData).forEach(key => {
        this.editingOpexRow[key] = this.originalOpexRowData[key];
      });
    }
    // Clear the stored original data and exit edit mode
    this.originalOpexRowData = null;
    this.editingOpexRow = null;
  }

  openYoutubeHelp(videoId: string) {
    this.messageService.add({
      severity: 'info',
      summary: 'Coming Soon',
      detail: 'No link found - coming soon',
      life: 3000
    });
  }

  closeYoutubeHelp() {
    this.youtubeHelpVisible = false;
    this.youtubeEmbedUrl = "";
  }

  // Description modal methods
  openDescriptionModal() {
    this.descriptionModalText = this.firstFormGroup.get('siteDescriptionCtrl')?.value || '';
    this.isDescriptionModalOpen = true;
  }

  closeDescriptionModal() {
    this.isDescriptionModalOpen = false;
    this.descriptionModalText = '';
  }

  saveDescriptionModal() {
    this.firstFormGroup.get('siteDescriptionCtrl')?.setValue(this.descriptionModalText);
    this.closeDescriptionModal();
  }

  // Helper method to get correct step indices based on visibility
  getStepIndices() {
    return {
      basicDetails: 0,
      moduleConfig: 1,
      groupModules: this.showGroupModulesStep ? 2 : -1, // -1 means step doesn't exist
      siteScore: this.showGroupModulesStep ? 3 : 2,
      openOpex: this.showGroupModulesStep ? 4 : 3,
    };
  }

  onFirstStepNext(stepper: MatStepper) {
    this.logFirstStep();
    if (this.firstFormGroup.valid) {
      // Always go to Module Configuration step
      stepper.selectedIndex = this.getStepIndices().moduleConfig;
    } else {
      // Mark all fields as touched to show validation errors with red borders
      this.firstFormGroup.markAllAsTouched();
      
      // Set error state for step indicator
      this.isFirstStepError = true;
    }
  }

  /**
   * Handles keypress event for contact number field
   * Only allows numbers and + sign at the start
   */
  onContactNumberKeyPress(event: KeyboardEvent): boolean {
    const input = event.target as HTMLInputElement;
    const char = event.key;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;

    // Allow control keys (backspace, delete, arrow keys, etc.)
    if (event.ctrlKey || event.metaKey || char === 'Backspace' || char === 'Delete' || 
        char === 'ArrowLeft' || char === 'ArrowRight' || char === 'Tab') {
      return true;
    }

    // Allow + only at the start (position 0)
    if (char === '+') {
      if (selectionStart === 0 && !currentValue.includes('+')) {
        return true;
      }
      event.preventDefault();
      return false;
    }

    // Allow only digits
    if (/^[0-9]$/.test(char)) {
      return true;
    }

    // Block all other characters
    event.preventDefault();
    return false;
  }

  /**
   * Handles paste event for contact number field
   * Filters out invalid characters from pasted content
   */
  onContactNumberPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    // Clean pasted text - remove all non-numeric characters except + at the start
    let cleanedText = pastedText.replace(/[^0-9+]/g, '');
    
    // If + is in the middle, remove it (only allow at start)
    const plusIndex = cleanedText.indexOf('+');
    if (plusIndex > 0) {
      cleanedText = cleanedText.replace(/\+/g, '');
    }
    
    // If current value already has + at start and we're pasting at position 0, remove + from paste
    if (currentValue.startsWith('+') && selectionStart === 0 && cleanedText.startsWith('+')) {
      cleanedText = cleanedText.substring(1);
    }
    
    // If pasting not at start, remove any + from pasted text
    if (selectionStart > 0) {
      cleanedText = cleanedText.replace(/\+/g, '');
    }

    // Build new value
    const beforeSelection = currentValue.substring(0, selectionStart);
    const afterSelection = currentValue.substring(selectionEnd);
    let newValue = beforeSelection + cleanedText + afterSelection;

    // Ensure only one + at the start
    if (newValue.includes('+') && !newValue.startsWith('+')) {
      newValue = newValue.replace(/\+/g, '');
    }

    // Limit to 16 characters (15 digits + optional +)
    newValue = newValue.substring(0, 16);

    // Update the form control
    this.firstFormGroup.get('contactNumberCtrl')?.setValue(newValue);
    this.firstFormGroup.get('contactNumberCtrl')?.markAsTouched();
  }

  /**
   * Validates that all modules in the grid have at least one parameter configured
   * @returns true if all modules have parameters, false otherwise
   */
  validateModulesHaveParameters(): boolean {
    this.modulesWithValidationErrors.clear();
    let hasErrors = false;

    if (!this.gridModules || this.gridModules.length === 0) {
      return true; // No modules to validate
    }

    // Iterate through all grid cells
    for (let row = 0; row < this.gridModules.length; row++) {
      if (!this.gridModules[row]) continue;

      for (let col = 0; col < this.gridModules[row].length; col++) {
        const gridModule = this.gridModules[row][col];

        // Skip empty cells
        if (!gridModule) continue;

        // Get module name
        const moduleName = gridModule.name;
        if (!moduleName) continue;

        // Generate the key used in moduleConfigs
        const channel = this.getRowLabel(row);
        const position = this.getColumnLabel(col);
        const configKey = `${moduleName}_${channel}${position}`;

        // Check if module has configuration with parameters
        const moduleConfig = this.moduleConfigs[configKey];

        // Check if module has parameters
        const hasParameters =
          moduleConfig &&
          moduleConfig.parameters &&
          Array.isArray(moduleConfig.parameters) &&
          moduleConfig.parameters.length > 0;

        if (!hasParameters) {
          // Mark this module as having validation error
          const errorKey = `${row}_${col}`;
          this.modulesWithValidationErrors.add(errorKey);
          hasErrors = true;
        }
      }
    }

    return !hasErrors;
  }

  /**
   * Checks if a module at specific grid position has validation error
   * @param row Row index
   * @param col Column index
   * @returns true if module has validation error
   */
  hasModuleValidationError(row: number, col: number): boolean {
    const errorKey = `${row}_${col}`;
    return this.modulesWithValidationErrors.has(errorKey);
  }

  onSecondStepNext(stepper: MatStepper, event?: Event) {
    // Prevent default behavior and stop propagation to prevent matStepperNext from advancing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.logSecondStep();

    // Validate that all modules have parameters
    if (!this.validateModulesHaveParameters()) {
      // Show error message
      this.messageService.add({
        severity: "error",
        summary: "Validation Error",
        detail:
          "All modules must have at least one parameter configured. Please configure parameters for all modules or remove modules without parameters.",
        life: 5000,
      });
      // Prevent navigation - don't advance stepper
      return;
    }

    // Only advance if validation passes and form is valid
    if (this.secondFormGroup.valid) {
      const indices = this.getStepIndices();
      // If Group Modules step is not shown, skip to Site Score step
      if (!this.showGroupModulesStep) {
        stepper.selectedIndex = indices.siteScore;
      } else {
        stepper.selectedIndex = indices.groupModules;
      }
    }
  }
  onThirdStepNext(stepper: MatStepper) {
    
    if (this.createdGroups && this.createdGroups.length > 0) {
      this.isThirdStepCompleted = true;
      this.isThirdStepError = false;
      
      // IMPORTANT: Set the form control value to make the form valid
      // This is required because the stepper checks form validity before allowing navigation
      this.thirdFormGroup.get("thirdCtrl")?.setValue("hasGroup");
      this.thirdFormGroup.get("thirdCtrl")?.updateValueAndValidity();
      
      const targetIndex = this.getStepIndices().siteScore;
      
      // Go to Site Score step
      stepper.selectedIndex = targetIndex;
    } else {
      this.isThirdStepCompleted = false;
      this.isThirdStepError = true;
      // Clear the form control to show validation error
      this.thirdFormGroup.get("thirdCtrl")?.setValue("");
      this.thirdFormGroup.get("thirdCtrl")?.updateValueAndValidity();
    }
  }
  onFourthStepNext(stepper: MatStepper) {
    this.logFourthStep();
    if (this.fourthFormGroup.valid) {
      // Go to Open Opex step
      stepper.selectedIndex = this.getStepIndices().openOpex;
    }
  }

  onModuleConfigStepBack(stepper: MatStepper) {
    // Go back to Basic Details step
    stepper.selectedIndex = this.getStepIndices().basicDetails;
  }

  onGroupModulesStepBack(stepper: MatStepper) {
    const targetIndex = this.getStepIndices().moduleConfig;
    // Go back to Module Configuration step
    stepper.selectedIndex = targetIndex;
  }

  onSiteScoreStepBack(stepper: MatStepper) {
    const indices = this.getStepIndices();
    // If Group Modules step is not shown, go back to Module Configuration step
    if (!this.showGroupModulesStep) {
      stepper.selectedIndex = indices.moduleConfig;
    } else {
      stepper.selectedIndex = indices.groupModules;
    }
  }

  onOpenOpexStepBack(stepper: MatStepper) {
    // Go back to Site Score step
    stepper.selectedIndex = this.getStepIndices().siteScore;
  }

  getConfiguredModulesForScoring() {
    // Get modules from grid
    const gridModules: any[] = [];
    for (let row = 0; row < this.gridModules.length; row++) {
      for (let col = 0; col < this.gridModules[row].length; col++) {
        const mod = this.gridModules[row][col];
        if (mod) {
          gridModules.push({
            ...mod,
            code: this.getRowLabel(row) + this.getColumnLabel(col),
          });
        }
      }
    }
    // Get special modules (with fixed codes)
    const specialModules = [
      { name: "Complete System", code: "00" },
      { name: "Corporate Module", code: "0CP" },
      { name: "Lab Module", code: "0LAB" },
    ]
      .map(({ name, code }) => {
        const config = this.moduleConfigs[name + "_" + code];
        if (config) {
          return { ...config, name, code };
        }
        return null;
      })
      .filter(Boolean);

    // Merge and deduplicate by code
    const all = [...gridModules, ...specialModules];
    const unique = all.filter(
      (mod, idx, arr) => arr.findIndex((m) => m.code === mod.code) === idx
    );
    return unique;
  }

  getConfiguredGridModules(): any[] {
    // Flatten gridModules and filter out empty cells
    const modules: any[] = [];
    for (let row = 0; row < this.gridModules.length; row++) {
      for (let col = 0; col < this.gridModules[row].length; col++) {
        const mod = this.gridModules[row][col];
        if (mod) {
          modules.push({
            ...mod,
            row,
            col,
            position: this.getColumnLabel(col),
            channel: this.getRowLabel(row),
            code: this.getRowLabel(row) + this.getColumnLabel(col), // Grid position code (e.g., "1A", "2B")
            // Preserve module_code for backend compatibility
            module_code:
              mod.module_code ||
              this.getRowLabel(row) + this.getColumnLabel(col),
          });
        }
      }
    }
    return modules;
  }

  /**
   * Get count of all grid modules (configured and unconfigured)
   * This is used to populate the position dropdown in the module config dialog
   * The dropdown will show position numbers from 1 to this count
   * Includes both configured modules and newly added modules that haven't been configured yet
   */
  getConfiguredGridModulesWithDisplayOnDashboardCount(): number {
    const allGridModules = this.getConfiguredGridModules();

    // Count ALL grid modules (both configured and unconfigured)
    // This ensures new modules are also included in the position dropdown
    let count = 0;
    const consideredModules: any[] = [];

    for (const mod of allGridModules) {
      const moduleName = mod.name || mod.module_name;
      const gridCode = mod.module_code || mod.code;
      const key = `${moduleName}_${gridCode}`;
      const config = this.moduleConfigs[key];

      count++;
      const isConfigured =
        config && config.allparameters && config.allparameters.length > 0;

      consideredModules.push({
        name: moduleName,
        code: gridCode,
        key: key,
        position: count,
        isConfigured: isConfigured,
        parametersCount: isConfigured ? config.allparameters.length : 0,
      });
    }

    return count;
  }

  getConfiguredSpecialModules(): any[] {
    const specialModules = [
      { name: "Complete System", code: "00" },
      { name: "Corporate Module", code: "0CP" },
      { name: "Lab Module", code: "0LAB" },
    ];
    return specialModules
      .map(({ name, code }) => {
        // Find config by key
        const key = name + "_" + code;
        const config = this.moduleConfigs[key];
        if (config) {
          return {
            name,
            code,
            module_code: code, // Add module_code for backend compatibility
            position: "", // Not from grid
            channel: "", // Not from grid
            ...config,
          };
        }
        return null;
      })
      .filter(Boolean);
  }

  updateDynamicModuleRanges() {
    const modules = this.getConfiguredModulesForScoring();
    // Keep previous ranges if possible, otherwise default to [1, 9]
    this.dynamicModuleRanges = modules.map((mod) => {
      const prev = this.dynamicModuleRanges.find((r) => r.code === mod.code);
      return {
        code: mod.code,
        name: mod.name,
        range: prev ? prev.range : [1, 9],
      };
    });

    // Ensure special modules maintain correct properties after module updates
    this.ensureSpecialModuleProperties();
  }

  /**
   * Update dynamic module ranges while preserving loaded ranges from saved configuration
   * This ensures that when modules are loaded from API, their ranges are preserved
   * and any new modules get default ranges
   */
  updateDynamicModuleRangesWithPreservedRanges() {
    const modules = this.getConfiguredModulesForScoring();
    const preservedRanges = [...this.dynamicModuleRanges]; // Keep loaded ranges

    // Update ranges for all configured modules, preserving loaded ranges
    this.dynamicModuleRanges = modules.map((mod) => {
      // First check preserved ranges (loaded from config)
      const preserved = preservedRanges.find((r) => r.code === mod.code);
      if (preserved) {
        return preserved; // Use loaded range
      }

      // If not in preserved, check existing dynamicModuleRanges
      const prev = this.dynamicModuleRanges.find((r) => r.code === mod.code);

      return {
        code: mod.code,
        name: mod.name,
        range: prev ? prev.range : [1, 9],
      };
    });

    // Ensure special modules maintain correct properties after module updates
    this.ensureSpecialModuleProperties();
  }

  syncVerticalScroll(event: Event, targetElement: HTMLElement): void {
    const sourceElement = event.target as HTMLElement;
    if (sourceElement && targetElement && sourceElement !== targetElement) {
      // Prevent recursive scroll events
      if (!sourceElement.dataset["syncing"]) {
        sourceElement.dataset["syncing"] = "true";
        targetElement.scrollTop = sourceElement.scrollTop;
        setTimeout(() => {
          delete sourceElement.dataset["syncing"];
        }, 10);
      }
    }
  }

  onGridScroll(event: Event): void {
    const gridElement = event.target as HTMLElement;
    const rowLabels = document.querySelector(
      ".grid-row-labels .grid-scroll-sync"
    ) as HTMLElement;

    if (gridElement && rowLabels) {
      // Sync the vertical scroll position from grid to channel labels
      rowLabels.scrollTop = gridElement.scrollTop;
    }
  }

  saveFormDataToSession() {
    const formData = {
      firstFormGroup: this.firstFormGroup.value,
      secondFormGroup: this.secondFormGroup.value,
      thirdFormGroup: this.thirdFormGroup.value,
      fourthFormGroup: this.fourthFormGroup.value,
      moduleConfigs: this.moduleConfigs,
      dynamicModuleRanges: this.dynamicModuleRanges,
      moduleRanges: this.moduleRanges,
      moduleScoreRanges: this.moduleScoreRanges,
      opexParameters: this.opexParameters,
      opexData: this.opexData,
      createdGroups: this.createdGroups,
      isFirstStepCompleted: this.isFirstStepCompleted,
      isSecondStepCompleted: this.isSecondStepCompleted,
      isThirdStepCompleted: this.isThirdStepCompleted,
      isFourthStepCompleted: this.isFourthStepCompleted,
      isOpexStepCompleted: this.isOpexStepCompleted,
      isAiTopicStepCompleted: this.isAiTopicStepCompleted,
      isFormModified: this.isFormModified,
      siteNameFromSession: this.siteNameFromSession,
      showGroupModulesStep: this.showGroupModulesStep,
      isLoadingFormData: this.isLoadingFormData,
      isFirstStepError: this.isFirstStepError,
      isSecondStepError: this.isSecondStepError,
      isThirdStepError: this.isThirdStepError,
      isFourthStepError: this.isFourthStepError,
      isOpexStepError: this.isOpexStepError,
      isAiTopicStepError: this.isAiTopicStepError,
      selectedOpexIndex: this.selectedOpexIndex,
      genericLibraryData: this.genericLibraryData,
      showModuleSearch: this.showModuleSearch,
      moduleSearchText: this.moduleSearchText,
      editingFormulaRow: this.editingFormulaRow,
      formulaEditorVisible: this.formulaEditorVisible,
      formulaEditorText: this.formulaEditorText,
      formulaEditorRow: this.formulaEditorRow,
      editingOpexRow: this.editingOpexRow,
      youtubeHelpVisible: this.youtubeHelpVisible,
      youtubeEmbedUrl: this.youtubeEmbedUrl,
      youtubeStepVideoIds: this.youtubeStepVideoIds,
      fabPosition: this.fabPosition,
      isDraggingFab: this.isDraggingFab,
      dragOffset: this.dragOffset,
      dragMoved: this.dragMoved,
      dragThreshold: this.dragThreshold,
      dragStartX: this.dragStartX,
      dragStartY: this.dragStartY,
      popoverPosition: this.popoverPosition,
      sliderMin: this.sliderMin,
      sliderMax: this.sliderMax,
      sliderOptions: this.sliderOptions,
      gridModules: this.gridModules,
      lastSavedTimestamp: this.lastSavedTimestamp,
    };

    // Update timestamp
    this.lastSavedTimestamp = new Date();
    formData.lastSavedTimestamp = this.lastSavedTimestamp;


    sessionStorage.setItem("lastFormData", JSON.stringify(formData));
  }

  restoreFormDataFromSession() {
    const savedFormData = sessionStorage.getItem("lastFormData");

    // Check if we already have fresh data from backend
    if (this.createdGroups && this.createdGroups.length > 0) {
      return;
    }

    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);

        this.firstFormGroup.patchValue(parsedData.firstFormGroup);
        this.secondFormGroup.patchValue(parsedData.secondFormGroup);
        this.thirdFormGroup.patchValue(parsedData.thirdFormGroup);
        this.fourthFormGroup.patchValue(parsedData.fourthFormGroup);
        this.moduleConfigs = parsedData.moduleConfigs;
        // Ensure special modules maintain correct module_code and module_alias
        this.ensureSpecialModuleProperties();
        this.dynamicModuleRanges = parsedData.dynamicModuleRanges;
        this.moduleRanges = parsedData.moduleRanges;
        this.moduleScoreRanges = parsedData.moduleScoreRanges;
        this.opexParameters = parsedData.opexParameters;
        this.opexData = parsedData.opexData;
        this.createdGroups = parsedData.createdGroups;
        this.isFirstStepCompleted = parsedData.isFirstStepCompleted;
        this.isSecondStepCompleted = parsedData.isSecondStepCompleted;
        this.isThirdStepCompleted = parsedData.isThirdStepCompleted;
        this.isFourthStepCompleted = parsedData.isFourthStepCompleted;
        this.isOpexStepCompleted = parsedData.isOpexStepCompleted;
        this.isAiTopicStepCompleted = parsedData.isAiTopicStepCompleted;
        this.isFormModified = parsedData.isFormModified;
        this.siteNameFromSession = parsedData.siteNameFromSession;
        this.showGroupModulesStep = parsedData.showGroupModulesStep;
        this.isLoadingFormData = parsedData.isLoadingFormData;
        this.isFirstStepError = parsedData.isFirstStepError;
        this.isSecondStepError = parsedData.isSecondStepError;
        this.isThirdStepError = parsedData.isThirdStepError;
        this.isFourthStepError = parsedData.isFourthStepError;
        this.isOpexStepError = parsedData.isOpexStepError;
        this.isAiTopicStepError = parsedData.isAiTopicStepError;
        this.selectedOpexIndex = parsedData.selectedOpexIndex;
        this.genericLibraryData = parsedData.genericLibraryData;
        this.showModuleSearch = parsedData.showModuleSearch;
        this.moduleSearchText = parsedData.moduleSearchText;
        this.editingFormulaRow = parsedData.editingFormulaRow;
        this.formulaEditorVisible = parsedData.formulaEditorVisible;
        this.formulaEditorText = parsedData.formulaEditorText;
        this.formulaEditorRow = parsedData.formulaEditorRow;
        this.editingOpexRow = parsedData.editingOpexRow;
        this.youtubeHelpVisible = parsedData.youtubeHelpVisible;
        this.youtubeEmbedUrl = parsedData.youtubeEmbedUrl;
        this.youtubeStepVideoIds = parsedData.youtubeStepVideoIds;
        this.fabPosition = parsedData.fabPosition;
        this.isDraggingFab = parsedData.isDraggingFab;
        this.dragOffset = parsedData.dragOffset;
        this.dragMoved = parsedData.dragMoved;
        this.dragThreshold = parsedData.dragThreshold;
        this.dragStartX = parsedData.dragStartX;
        this.dragStartY = parsedData.dragStartY;
        this.popoverPosition = parsedData.popoverPosition;
        this.sliderMin = parsedData.sliderMin;
        this.sliderMax = parsedData.sliderMax;
        this.sliderOptions = parsedData.sliderOptions;
        this.gridModules = parsedData.gridModules;
        this.lastSavedTimestamp = parsedData.lastSavedTimestamp;


        this.cdr.detectChanges();
      } catch (error) {
        // Clear corrupted data
        sessionStorage.removeItem("lastFormData");
      }
    }
  }

  clearFormDataFromSession() {
    sessionStorage.removeItem("lastFormData");

    // Reset configuration flags
    this.hasSavedConfiguration = false;
    this.isViewMode = false;

  }

  /**
   * Ensures that special modules always have correct module_code and module_alias
   * This method is called after restoring or importing module configurations
   */
  private ensureSpecialModuleProperties() {
    for (const key of Object.keys(this.moduleConfigs)) {
      const mod = this.moduleConfigs[key];
      if (mod?.module_name === "Complete System") {
        mod.module_code = "00";
        mod.module_alias = "Complete System";
      } else if (mod?.module_name === "Lab Module") {
        mod.module_code = "0LAB";
        mod.module_alias = "Lab Module";
      } else if (mod?.module_name === "Corporate Module") {
        mod.module_code = "0CP";
        mod.module_alias = "Corporate Module";
      }
    }
  }

  ngOnDestroy(): void {
    this.saveFormDataToSession();
  }

  canDeactivate(): boolean {
    if (this.isFormModified) {
      const confirmLeave = confirm(
        "You have unsaved changes. Are you sure you want to leave? Your changes will be saved automatically."
      );
      if (confirmLeave) {
        this.saveFormDataToSession();
        return true;
      }
      return false;
    }
    return true;
  }

  clearFormData() {
    const confirmClear = confirm(
      "Are you sure you want to clear all form data? This action cannot be undone."
    );
    if (confirmClear) {
      // Reset all form groups
      this.firstFormGroup.reset();
      this.secondFormGroup.reset();
      this.thirdFormGroup.reset();
      this.fourthFormGroup.reset();

      // Reset component state
      this.isFormModified = false;
      this.isFirstStepCompleted = false;
      this.isSecondStepCompleted = false;
      this.isThirdStepCompleted = false;
      this.isFourthStepCompleted = false;
      this.isOpexStepCompleted = false;
      this.isFirstStepError = false;
      this.isSecondStepError = false;
      this.isThirdStepError = false;
      this.isFourthStepError = false;
      this.isOpexStepError = false;

      // Reset configuration flags
      this.hasSavedConfiguration = false;
      this.isViewMode = false;

      // Clear session storage
      this.clearFormDataFromSession();

      // Show success message
      this.messageService.add({
        severity: "success",
        summary: "Cleared",
        detail: "All form data has been cleared successfully.",
      });

      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  exportFormData() {
    try {
      // Prepare export data
      const exportData = {
        timestamp: new Date().toISOString(),
        siteInfo: {
          siteName: this.siteNameFromSession,
          formData: this.firstFormGroup.value,
          moduleConfigs: this.moduleConfigs,
          dynamicModuleRanges: this.dynamicModuleRanges,
          createdGroups: this.createdGroups,
          opexData: this.opexData,
          opexParameters: this.opexParameters,
        },
      };

      // Create and download file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `site-setting-data-${
        this.siteNameFromSession || "unknown"
      }-${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      window.URL.revokeObjectURL(url);

      // Show success message
      this.messageService.add({
        severity: "success",
        summary: "Exported",
        detail: "Form data exported successfully!",
      });
    } catch (error) {
      this.messageService.add({
        severity: "error",
        summary: "Export Failed",
        detail: "Failed to export form data. Please try again.",
      });
    }
  }

  importFormData() {
    // Trigger file input click
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const importedData = JSON.parse(e.target.result);

          // Validate imported data structure
          if (importedData.siteInfo && importedData.siteInfo.formData) {
            // Confirm import
            const confirmImport = confirm(
              "This will replace all current form data. Are you sure you want to continue?"
            );
            if (confirmImport) {
              // Import form data
              this.firstFormGroup.patchValue(importedData.siteInfo.formData);

              // Import other data if available
              if (importedData.siteInfo.moduleConfigs) {
                this.moduleConfigs = importedData.siteInfo.moduleConfigs;
                // Ensure special modules maintain correct module_code and module_alias
                this.ensureSpecialModuleProperties();
              }
              if (importedData.siteInfo.dynamicModuleRanges) {
                this.dynamicModuleRanges =
                  importedData.siteInfo.dynamicModuleRanges;
              }
              if (importedData.siteInfo.createdGroups) {
                this.createdGroups = importedData.siteInfo.createdGroups;
              }
              if (importedData.siteInfo.opexData) {
                this.opexData = importedData.siteInfo.opexData;
              }
              if (importedData.siteInfo.opexParameters) {
                this.opexParameters = importedData.siteInfo.opexParameters;
              }

              // Reset form modification flag
              this.isFormModified = false;

              // Show success message
              this.messageService.add({
                severity: "success",
                summary: "Imported",
                detail: "Form data imported successfully!",
              });

              // Trigger change detection
              this.cdr.detectChanges();
            }
          } else {
            throw new Error("Invalid data structure");
          }
        } catch (error) {
          this.messageService.add({
            severity: "error",
            summary: "Import Failed",
            detail:
              "Invalid file format. Please select a valid JSON file exported from this application.",
          });
        }
      };
      reader.readAsText(file);
    }

    // Reset file input
    event.target.value = "";
  }

  manualSave() {

    this.saveFormDataToSession();

    // Show success message
    this.messageService.add({
      severity: "success",
      summary: "Saved",
      detail: "Form data saved successfully!",
    });

    // Reset form modification flag
    this.isFormModified = false;
  }

  private startAutoSave() {
    setInterval(() => {
      // Only auto-save if we don't have fresh backend data
      if (!this.hasSavedConfiguration) {
        this.saveFormDataToSession();
      }
    }, 120000); // 2 minutes in milliseconds
  }

  loadSpecialModulesConfiguration(configData: any) {
    // Load special modules if they exist in the configuration
    const specialModules = [
      "Complete System",
      "Lab Module",
      "Corporate Module",
    ];

    specialModules.forEach((moduleName) => {
      const specialModule = configData.moduleConfiguration?.modules?.find(
        (m: any) => m.module_name === moduleName || m.name === moduleName
      );

      if (specialModule) {

        let moduleCode = "";
        if (moduleName === "Complete System") moduleCode = "00";
        else if (moduleName === "Lab Module") moduleCode = "0LAB";
        else if (moduleName === "Corporate Module") moduleCode = "0CP";

        const key = `${moduleName}_${moduleCode}`;

        // Transform backend parameters to frontend format and mark them as selected
        const transformedSpecialParameters = (
          specialModule.parameters || []
        ).map((param: any) => ({
          parameter_id: param.parameter_id,
          parameter_name: param.parameter_name,
          code: param.code || param.parameter_code,
          parameter_code: param.parameter_code || param.code,
          parameter_unit: param.parameter_unit,
          parameter_type: param.parameter_type,
          parameter_formula: param.parameter_formula,
          upload_source: param.upload_source,
          allranges: param.allranges || [],
          // Map backend fields to frontend table structure
          unit: param.parameter_unit || "",
          type: param.parameter_type || 0,
          uploadType: param.upload_source || "manual",
          weight: param.parameter_weight || 0,
          formula: param.parameter_formula || "",
          calculation: param.calculation || param.parameter_calculation || "",
          parameter_calculation:
            param.parameter_calculation || param.calculation || "",
          query: param.parameter_query || "",
          criticalParameter:
            param.parameter_critical === 1 || param.parameter_critical === true,
          // Mark as selected since it exists in the backend response
          selected: true,
          checked: true,
          // Add ranges in the format expected by frontend - only mark as selected if they exist in backend
          // IMPORTANT: Deduplicate ranges from backend to prevent duplicates
          ranges: (() => {
            const allRanges = param.allranges || [];

            // Step 1: First deduplicate by range_id (if they exist) - keep first occurrence
            const seenRangeIds = new Set();
            const deduplicatedById = allRanges.filter((range: any) => {
              if (range.range_id != null) {
                if (seenRangeIds.has(range.range_id)) {
                  return false; // Skip duplicate
                }
                seenRangeIds.add(range.range_id);
              }
              return true; // Keep this range
            });


            // Step 2: Then deduplicate by signature (min-max-score) - keep first occurrence
            const seenSignatures = new Set();
            const deduplicatedBySignature = deduplicatedById.filter(
              (range: any) => {
                const min = range.min_value || range.min || 0;
                const max = range.max_value || range.max || 0;
                const score = range.score || 0;
                const signature = `${min}-${max}-${score}`;
                if (seenSignatures.has(signature)) {
                  return false; // Skip duplicate
                }
                seenSignatures.add(signature);
                return true; // Keep this range
              }
            );


            // Step 3: Map to frontend format
            const mappedRanges = deduplicatedBySignature.map((range: any) => ({
              score: range.score || 0,
              min: range.min_value || range.min || 0,
              max: range.max_value || range.max || 0,
              selected: true, // Selected because it exists in backend response
              query: range.query || range.parameter_range_name || "",
              range_id: range.range_id,
              // Add workaround fields
              loadedFromSavedConfig: true, // Flag to indicate this range was loaded from saved config
              originalSelection: true, // Track original selection state
              // Also add the backend field names for compatibility
              min_value: range.min_value || range.min || 0,
              max_value: range.max_value || range.max || 0,
            }));


            return mappedRanges;
          })(),
        }));

        // Handle displayOnDashboard - check both camelCase and snake_case, prioritize camelCase
        const displayOnDashboardValue =
          specialModule.displayOnDashboard !== undefined
            ? specialModule.displayOnDashboard
            : specialModule.display_on_dashboard !== undefined
            ? !!specialModule.display_on_dashboard
            : false;

        this.moduleConfigs[key] = {
          ...specialModule,
          module_id: specialModule.module_id, // Add module_id to preserve database relationship
          module_name: moduleName,
          module_code: moduleCode,
          module_alias: moduleName,
          aliasName: moduleName,
          moduleType: specialModule.module_type || "",
          displayOnDashboard: displayOnDashboardValue,
          display_on_dashboard: displayOnDashboardValue, // Also preserve snake_case for consistency
          parameters: transformedSpecialParameters,
          allparameters: transformedSpecialParameters,
          selectedProductCodes: transformedSpecialParameters
            .filter((p: any) => p.selected || p.checked) // Only include selected parameters
            .map((p: any) => p.code || p.parameter_code || "")
            .filter(Boolean),
        };

      }
    });
  }

  private waitForGenericLibraryAllopexModules(
    maxAttempts: number = 20,
    delayMs: number = 200
  ): Promise<void> {
    if (Array.isArray(this.genericLibraryData?.allopexmodules)) {
      return Promise.resolve();
    }

    if (!this.genericLibraryOpexWaitPromise) {
      this.genericLibraryOpexWaitPromise = new Promise((resolve) => {
        const poll = (attempt = 0) => {
          if (Array.isArray(this.genericLibraryData?.allopexmodules)) {
            this.genericLibraryOpexWaitPromise = null;
            resolve();
            return;
          }

          if (attempt >= maxAttempts) {
            this.genericLibraryOpexWaitPromise = null;
            resolve();
            return;
          }

          setTimeout(() => poll(attempt + 1), delayMs);
        };

        poll();
      });
    }

    return this.genericLibraryOpexWaitPromise;
  }

  loadOpexDataConfiguration(configData: any, skipWaitForGeneric = false) {
    if (
      configData.opexConfiguration &&
      configData.opexConfiguration.parameters
    ) {
      if (
        !skipWaitForGeneric &&
        !Array.isArray(this.genericLibraryData?.allopexmodules)
      ) {
        this.waitForGenericLibraryAllopexModules().then(() => {
          this.loadOpexDataConfiguration(configData, true);
        });
        return;
      }

      const allOpexModules = Array.isArray(
        this.genericLibraryData?.allopexmodules
      )
        ? this.genericLibraryData.allopexmodules
        : null;

      // Store the configuration for later use
      this.savedConfiguration = configData;


      // Update opex parameters with saved selections based on module_code
      this.opexParameters = this.opexParameters.map((param) => {
        const savedParam = configData.opexConfiguration.parameters.find(
          (p: any) => p.module_code === param.key
        );
        if (savedParam) {
          return {
            ...param,
            checked: savedParam.selected || false,
          };
        }
        return param;
      });

      // Update the opexData with the parameters from the API response
      configData.opexConfiguration.parameters.forEach((savedParam: any) => {
        if (
          savedParam.selected &&
          savedParam.parameters &&
          Array.isArray(savedParam.parameters)
        ) {
          // Find the corresponding opex data array using module_code
          const opexDataKey = savedParam.module_code;

          // Find the matching module in allopexmodules to get the full parameter data
          const opexModule = allOpexModules?.find(
            (mod: any) => mod.module_code === savedParam.module_code
          );

          if (opexModule && opexModule.allopexparameters) {

            // Create a map of configured parameters by parameter_code for quick lookup
            const configuredParamsMap = new Map();
            savedParam.parameters.forEach((param: any) => {
              const paramCode = param.parameter_code || "";
              configuredParamsMap.set(paramCode, param);
            });

            // Start with ALL parameters from generic library
            const allParameters = opexModule.allopexparameters.map(
              (paramData: any) => {
                const paramCode = paramData.parameter_code || "";
                const configuredParam = configuredParamsMap.get(paramCode);

                if (configuredParam) {
                  // This parameter is configured - use saved values and mark as selected
                  return {
                    parameter_id: configuredParam.parameter_id,
                    parameter_name: configuredParam.parameter_name,
                    details: configuredParam.details || paramData.details || "",
                    parameter_unit:
                      configuredParam.parameter_unit ||
                      paramData.parameter_unit ||
                      "",
                    parameter_type:
                      this.getParameterTypeString(
                        configuredParam.parameter_type
                      ) ||
                      this.getParameterTypeString(paramData.parameter_type) ||
                      "Raw",
                    parameter_code: paramCode,
                    uploadType:
                      configuredParam.upload_source === 2 ? "Online" : "Manual",
                    formula:
                      configuredParam.parameter_formula ||
                      paramData.parameter_formula ||
                      "",
                    price_type:
                      this.getPriceTypeString(configuredParam.price_type) ||
                      this.getPriceTypeString(paramData.price_type) ||
                      "Fixed",
                    price:
                      configuredParam.price !== undefined
                        ? configuredParam.price
                        : paramData.price || "",
                    parameter_banchmark:
                      configuredParam.parameter_banchmark !== undefined
                        ? configuredParam.parameter_banchmark
                        : paramData.parameter_banchmark,
                    selected: configuredParam.selected || true, // Mark as selected since it's configured
                  };
                } else {
                  // This parameter is NOT configured - include it but leave it unchecked
                  return {
                    parameter_id: paramData.parameter_id,
                    parameter_name: paramData.parameter_name,
                    details: paramData.details || "",
                    parameter_unit: paramData.parameter_unit || "",
                    parameter_type:
                      this.getParameterTypeString(paramData.parameter_type) ||
                      "Raw",
                    parameter_code: paramCode,
                    uploadType:
                      paramData.upload_source === 2 ? "Online" : "Manual",
                    formula: paramData.parameter_formula || "",
                    price_type:
                      this.getPriceTypeString(paramData.price_type) || "Fixed",
                    price: paramData.price || "",
                    parameter_banchmark: paramData.parameter_banchmark || null,
                    selected: false, // Not configured, so unchecked
                  };
                }
              }
            );

            // Add any configured parameters that don't exist in generic library
            savedParam.parameters.forEach((configuredParam: any) => {
              const paramCode = configuredParam.parameter_code || "";
              const existsInAll = allParameters.some(
                (p) => (p.parameter_code || "") === paramCode
              );

              if (!existsInAll) {
                allParameters.push({
                  parameter_id: configuredParam.parameter_id,
                  parameter_name: configuredParam.parameter_name,
                  details: configuredParam.details || "",
                  parameter_unit: configuredParam.parameter_unit || "",
                  parameter_type:
                    this.getParameterTypeString(
                      configuredParam.parameter_type
                    ) || "Raw",
                  parameter_code: paramCode,
                  uploadType:
                    configuredParam.upload_source === 2 ? "Online" : "Manual",
                  formula: configuredParam.parameter_formula || "",
                  price_type:
                    this.getPriceTypeString(configuredParam.price_type) ||
                    "Fixed",
                  price: configuredParam.price || "",
                  parameter_banchmark:
                    configuredParam.parameter_banchmark || null,
                  selected: configuredParam.selected || true,
                });
              }
            });

            // Set the final opexData with ALL parameters
            this.opexData[opexDataKey] = allParameters;
          } else {
            // Fallback: if generic library not available, use only configured parameters
            this.opexData[opexDataKey] = savedParam.parameters.map(
              (savedParameter: any) => {
                return {
                  parameter_id: savedParameter.parameter_id,
                  parameter_name: savedParameter.parameter_name,
                  details: savedParameter.details || "",
                  parameter_unit: savedParameter.parameter_unit || "",
                  parameter_type:
                    this.getParameterTypeString(
                      savedParameter.parameter_type
                    ) || "",
                  parameter_code: savedParameter.parameter_code || "",
                  uploadType:
                    savedParameter.upload_source === 2 ? "Online" : "Manual",
                  formula: savedParameter.parameter_formula || "",
                  price_type:
                    this.getPriceTypeString(savedParameter.price_type) || "",
                  price: savedParameter.price || "",
                  selected: savedParameter.selected || false,
                };
              }
            );
          }
        }
      });

      // Don't automatically mark as completed - let user visit the step first
      if (this.opexParameters.some((p) => p.checked)) {
        // this.isOpexStepCompleted = true;
        // this.isOpexStepError = false;

        // Auto-select the first checked parameter to show its data
        const firstCheckedIndex = this.opexParameters.findIndex(
          (p) => p.checked
        );
        if (firstCheckedIndex !== -1) {
          this.selectedOpexIndex = firstCheckedIndex;
        }
      }

    }
  }

  /**
   * Workaround method to help users understand loaded ranges
   * Shows information about ranges loaded from saved configuration
   */
  getLoadedRangesInfo(): {
    totalRanges: number;
    loadedFromConfig: number;
    message: string;
  } {
    let totalRanges = 0;
    let loadedFromConfig = 0;

    // Count ranges across all modules
    Object.values(this.moduleConfigs).forEach((module: any) => {
      if (module.ranges && Array.isArray(module.ranges)) {
        totalRanges += module.ranges.length;
        loadedFromConfig += module.ranges.filter(
          (range: any) => range.loadedFromSavedConfig
        ).length;
      }
    });

    let message = "";
    if (loadedFromConfig > 0) {
      message = `⚠️ ${loadedFromConfig} ranges loaded from saved configuration. These ranges are selected because they exist in your saved configuration. You can deselect ranges you don't want.`;
    } else {
      message = "No ranges loaded from saved configuration.";
    }

    return { totalRanges, loadedFromConfig, message };
  }

  /**
   * Reset ranges to their original selection state
   * Useful when user wants to revert changes
   */
  resetRangesToOriginalSelection(): void {
    Object.values(this.moduleConfigs).forEach((module: any) => {
      if (module.ranges && Array.isArray(module.ranges)) {
        module.ranges.forEach((range: any) => {
          if (range.loadedFromSavedConfig) {
            range.selected = range.originalSelection;
          }
        });
      }
    });

    this.messageService.add({
      severity: "info",
      summary: "Ranges Reset",
      detail:
        "Ranges have been reset to their original selection state from saved configuration.",
    });
  }

  /**
   * Clear all range selections
   * Useful when user wants to start fresh
   */
  clearAllRangeSelections(): void {
    Object.values(this.moduleConfigs).forEach((module: any) => {
      if (module.ranges && Array.isArray(module.ranges)) {
        module.ranges.forEach((range: any) => {
          range.selected = false;
        });
      }
    });

    this.messageService.add({
      severity: "info",
      summary: "Ranges Cleared",
      detail:
        "All range selections have been cleared. You can now select only the ranges you want.",
    });
  }

  /**
   * Get ranges that were loaded from saved configuration vs. newly added
   */
  getRangesBySource(): { loadedFromConfig: any[]; newlyAdded: any[] } {
    const loadedFromConfig: any[] = [];
    const newlyAdded: any[] = [];

    Object.values(this.moduleConfigs).forEach((module: any) => {
      if (module.ranges && Array.isArray(module.ranges)) {
        module.ranges.forEach((range: any) => {
          if (range.loadedFromSavedConfig) {
            loadedFromConfig.push({ ...range, moduleName: module.module_name });
          } else {
            newlyAdded.push({ ...range, moduleName: module.module_name });
          }
        });
      }
    });

    return { loadedFromConfig, newlyAdded };
  }

  /**
   * Debug method to log current range states
   */
  logCurrentRangeStates(): void {

  }

  /**
   * Get summary of range selections for current configuration
   */
  getRangeSelectionSummary(): string {
    const rangesInfo = this.getLoadedRangesInfo();
    const { loadedFromConfig, newlyAdded } = this.getRangesBySource();

    let summary = `Total Ranges: ${rangesInfo.totalRanges}\n`;
    summary += `Loaded from Config: ${loadedFromConfig.length}\n`;
    summary += `Newly Added: ${newlyAdded.length}\n`;

    if (loadedFromConfig.length > 0) {
      summary += `\n⚠️ Note: ${loadedFromConfig.length} ranges were loaded from saved configuration and are selected because they exist in your saved configuration.`;
      summary += `\nYou can deselect ranges you don't want to keep.`;
    }

    return summary;
  }

  getUserRole(): void {
    try {
      const userInfo = sessionStorage.getItem("user-info");
      if (userInfo) {
        const userData = JSON.parse(userInfo);
        this.userRole = userData.role || "";
      }
    } catch (error) {
      this.userRole = "";
    }
  }

  hasRole1(): boolean {
    return this.userRole === "1";
  }

  hasRole6(): boolean {
    return this.userRole === "6";
  }

  updateFormValidators(): void {
    // In category mode, only Category Name and Category Description are required
    if (this.isCategoryMode) {
      // Ensure siteNameCtrl (Category Name) and siteDescriptionCtrl (Category Description) are required
      this.firstFormGroup
        .get("siteNameCtrl")
        ?.setValidators([Validators.required]);
      this.firstFormGroup
        .get("siteDescriptionCtrl")
        ?.setValidators([Validators.required]);

      // Clear validators for all other fields
      this.firstFormGroup.get("siteAddressCtrl")?.clearValidators();
      this.firstFormGroup.get("contactNumberCtrl")?.clearValidators();
      this.firstFormGroup.get("plantViewCtrl")?.clearValidators();
      this.firstFormGroup.get("globalDataCleansingCtrl")?.clearValidators();
      this.firstFormGroup.get("currencyCtrl")?.clearValidators();
      this.firstFormGroup.get("assistantIdCtrl")?.clearValidators();
      this.firstFormGroup.get("apiKeyCtrl")?.clearValidators();
      this.firstFormGroup.get("alertTypeCtrl")?.clearValidators();
      this.firstFormGroup.get("latitudeCtrl")?.clearValidators();
      this.firstFormGroup.get("longitudeCtrl")?.clearValidators();
      this.firstFormGroup.get("categoryCtrl")?.clearValidators();

      // Update validity for all controls
      this.firstFormGroup.get("siteNameCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("siteDescriptionCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("siteAddressCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("contactNumberCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("plantViewCtrl")?.updateValueAndValidity();
      this.firstFormGroup
        .get("globalDataCleansingCtrl")
        ?.updateValueAndValidity();
      this.firstFormGroup.get("currencyCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("assistantIdCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("apiKeyCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("alertTypeCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("latitudeCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("longitudeCtrl")?.updateValueAndValidity();
      this.firstFormGroup.get("categoryCtrl")?.updateValueAndValidity();
      return;
    }

    // Normal site mode - apply role-based validators
    if (this.hasRole1()) {
      // User has role 1, make these fields required
      this.firstFormGroup
        .get("assistantIdCtrl")
        ?.setValidators([Validators.required]);
      this.firstFormGroup
        .get("apiKeyCtrl")
        ?.setValidators([Validators.required]);
      this.firstFormGroup
        .get("alertTypeCtrl")
        ?.setValidators([Validators.required]);
    } else {
      // User doesn't have role 1, make these fields optional
      this.firstFormGroup.get("assistantIdCtrl")?.clearValidators();
      this.firstFormGroup.get("apiKeyCtrl")?.clearValidators();
      this.firstFormGroup.get("alertTypeCtrl")?.clearValidators();
    }

    if (this.hasRole6()) {
      // User has role 6, make these fields required
      this.firstFormGroup
        .get("latitudeCtrl")
        ?.setValidators([Validators.required]);
      this.firstFormGroup
        .get("longitudeCtrl")
        ?.setValidators([Validators.required]);
      this.firstFormGroup
        .get("categoryCtrl")
        ?.setValidators([Validators.required]);
    } else {
      // User doesn't have role 6, make these fields optional
      this.firstFormGroup.get("latitudeCtrl")?.clearValidators();
      this.firstFormGroup.get("longitudeCtrl")?.clearValidators();
      this.firstFormGroup.get("categoryCtrl")?.clearValidators();
    }

    // Update validity for all affected controls
    this.firstFormGroup.get("assistantIdCtrl")?.updateValueAndValidity();
    this.firstFormGroup.get("apiKeyCtrl")?.updateValueAndValidity();
    this.firstFormGroup.get("alertTypeCtrl")?.updateValueAndValidity();
    this.firstFormGroup.get("latitudeCtrl")?.updateValueAndValidity();
    this.firstFormGroup.get("longitudeCtrl")?.updateValueAndValidity();
    this.firstFormGroup.get("categoryCtrl")?.updateValueAndValidity();
  }

  /**
   * Toggle group expansion state
   * @param groupIndex - Index of the group to toggle
   */
  toggleGroupExpansion(groupIndex: number): void {
    if (this.createdGroups && this.createdGroups[groupIndex]) {
      this.createdGroups[groupIndex].isExpanded =
        !this.createdGroups[groupIndex].isExpanded;
    }
  }

  /**
   * Get total modules count for a group
   * @param group - The group object
   * @returns Total number of modules in the group
   */
  getTotalModulesCount(group: any): number {
    let count = 0;
    if (group.subGroups) {
      group.subGroups.forEach((sub: any) => {
        if (sub.modules) {
          count += sub.modules.length;
        }
      });
    } else if (group.modules) {
      count = group.modules.length;
    }
    return count;
  }

  /**
   * Get all modules from a group (flattened)
   * @param group - The group object
   * @returns Array of all modules in the group
   */
  getAllModules(group: any): any[] {
    const modules: any[] = [];
    if (group.subGroups) {
      group.subGroups.forEach((sub: any) => {
        if (sub.modules) {
          modules.push(...sub.modules);
        }
      });
    } else if (group.modules) {
      modules.push(...group.modules);
    }
    return modules;
  }

  // AI Topic Settings step methods
  completeAiTopicStep(): void {
    this.isAiTopicStepCompleted = true;
    this.isAiTopicStepError = false;

    // Save the step completion status
    this.saveFormDataToSession();

    // Show success message
  }

  isAiTopicStepValid(): boolean {
    // For now, always return true since AI Topic component handles its own validation
    // In the future, you might want to check if at least one topic is configured
    return true;
  }
}
